import React from 'react';

/**
 * Illustrated scene art for invitations.
 *
 * Each scene is a self-contained SVG component drawn to sit BEHIND the card's
 * text as a backdrop — detail lives around the edges, corners, top and bottom,
 * while the central vertical band stays calm so overlaid copy stays legible.
 *
 * Every component takes the same props so they're interchangeable:
 *   color   primary line/art colour
 *   color2  secondary colour
 *   accent  accent colour
 *   scale   card width in px (card aspect is 3/4)
 *
 * Scenes are registered in SCENES and rendered through <Scene name="..." />,
 * which is what CardFace calls. A design opts in via design.scene.
 *
 * NOTE ON <defs> IDS: a gallery renders many cards at once. Each scene is bound
 * to a single design system (see invite_designs.py), so every instance of a
 * given scene resolves to identical gradients — duplicate ids therefore resolve
 * to visually identical defs and are harmless. Reusing one scene across systems
 * with different palettes would require per-instance id scoping.
 */

// Translucent variant of a hex colour. Shared by every scene below.
export function hexA(hex, a = 1) {
  const h = String(hex || '#888').replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(f, 16);
  if (Number.isNaN(n)) return `rgba(136,136,136,${a})`;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

// ─── WatercolorEucalyptusWreath — eucalyptus-wreath
function WatercolorEucalyptusWreath({ color, color2, accent, scale = 300 }) {
  // ---- palette (spec) : props drive front greens / mid body / warm olive ----
  const INK = color || '#3E4E3A';
  const SAGE = color2 || '#9CAF88';
  const OLV = accent || '#8B8663';
  const PALE = '#E7EDE2', LGT = '#C2CFB6', MIDG = '#7C9070', DEEP = '#5A6E52',
        GLA = '#94A7A0', STM = '#6E6552', FLW = '#FFFDF8', BUF = '#E3D9BE', HAIR = '#A9BA9D';

  // ---- oval ring geometry (optically centred 20u above middle) ----
  const CX = 150, CY = 195, RX = 95, RY = 130;
  const K = scale < 200 ? 1.3 : 1; // hairlines hold up in the gallery thumbnail
  const f = (v) => Math.round(v * 100) / 100;
  const rnd = (a, b) => { const x = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453; return x - Math.floor(x); };
  const PT = (dg, r) => { const a = dg * Math.PI / 180; return [CX + (RX + r) * Math.sin(a), CY - (RY + r) * Math.cos(a)]; };

  // ---- organic silhouettes: ellipse with anchors pushed off-radius ----
  const blobP = (rx, ry, k) => {
    const j = (i) => 1 + (rnd(k, i) - 0.5) * 0.18, c = 0.552;
    const a = rx * j(1), b = ry * j(2), d = rx * j(3), e = ry * j(4);
    return [[0, -b], [a * c * 1.12, -b], [a, -e * c], [a, 0], [a, e * c * 1.14], [d * c, e], [0, e],
            [-d * c * 1.06, e], [-d, b * c], [-d, 0], [-d, -b * c * 1.1], [-a * c, -b], [0, -b]];
  };
  const lanceP = (L, w, k) => {
    const b = 1 + (rnd(k, 5) - 0.5) * 0.3, g = 1 + (rnd(k, 6) - 0.5) * 0.25;
    return [[0, -L / 2], [w * b, -L * 0.16], [w * 0.92, L * 0.24], [0, L / 2],
            [-w * 0.86 * g, L * 0.2], [-w * b * 0.94, -L * 0.2], [0, -L / 2]];
  };
  const XF = (pts, cx, cy, rot, s, ox, oy) => {
    const a = rot * Math.PI / 180, co = Math.cos(a), si = Math.sin(a);
    const sc = s == null ? 1 : s, px = ox || 0, py = oy || 0;
    return pts.map((p) => { const x = px + p[0] * sc, y = py + p[1] * sc; return [cx + x * co - y * si, cy + x * si + y * co]; });
  };
  const CUB = (p) => {
    let d = 'M' + f(p[0][0]) + ' ' + f(p[0][1]);
    for (let i = 1; i < p.length; i += 3)
      d += 'C' + f(p[i][0]) + ' ' + f(p[i][1]) + ' ' + f(p[i + 1][0]) + ' ' + f(p[i + 1][1]) + ' ' + f(p[i + 2][0]) + ' ' + f(p[i + 2][1]);
    return d + 'Z';
  };
  const SH = (pts, cx, cy, rot, s, ox, oy) => CUB(XF(pts, cx, cy, rot, s, ox, oy));
  const flor = (cx, cy, r, k) => { // five soft lobes, never a circle
    let d = '';
    for (let i = 0; i < 5; i++) {
      const a0 = i / 5 * 6.2832 + k * 0.3, a1 = (i + 1) / 5 * 6.2832 + k * 0.3;
      const rr = r * (0.86 + rnd(k, i) * 0.3) * 1.5;
      const x0 = cx + Math.cos(a0) * r * 0.4, y0 = cy + Math.sin(a0) * r * 0.4;
      const x1 = cx + Math.cos(a1) * r * 0.4, y1 = cy + Math.sin(a1) * r * 0.4;
      d += (i === 0 ? 'M' + f(x0) + ' ' + f(y0) : '') + 'Q' + f(cx + Math.cos((a0 + a1) / 2) * rr) + ' ' +
           f(cy + Math.sin((a0 + a1) / 2) * rr) + ' ' + f(x1) + ' ' + f(y1);
    }
    return d + 'Z';
  };
  // like-for-like shapes coalesce into one multi-subpath node; alternating parity
  // keeps neighbouring leaves in different nodes so overlaps still multiply
  const sink = () => {
    const arr = [], m = {};
    return { a: arr, add: (key, at, d) => { let s = m[key]; if (!s) { s = Object.assign({ d: '' }, at); m[key] = s; arr.push(s); } s.d += d; } };
  };
  const back = sink(), mid = sink(), front = sink(), gypB = sink(), gypF = sink(), trim = sink();
  const B = [back, mid, front];

  // sp: silver-dollar / baby-blue / seeded / olive. t=clock angle, r=band offset, L=depth layer
  const SPRIGS = [
    { sp: 'sd', t0: 201, t1: 167, r0: 5, r1: -8, L: 2, sz: 1.08 }, { sp: 'sd', t0: 177, t1: 213, r0: -1, r1: -9, L: 2, sz: 1.04 },
    { sp: 'sd', t0: 197, t1: 173, r0: 16, r1: 9, L: 1, sz: 0.98 }, { sp: 'ol', t0: 206, t1: 168, r0: -13, r1: -3, L: 2 },
    { sp: 'ol', t0: 166, t1: 206, r0: 11, r1: 19, L: 1 }, { sp: 'se', t0: 179, t1: 201, r0: 7, r1: 27, L: 1 },
    { sp: 'bb', t0: 210, t1: 180, r0: 15, r1: 6, L: 0 }, { sp: 'sd', t0: 213, t1: 189, r0: -13, r1: -5, L: 1, sz: 0.95 },
    { sp: 'bb', t0: 172, t1: 198, r0: -14, r1: -6, L: 0 },
    { sp: 'sd', t0: 163, t1: 139, r0: 7, r1: 1, L: 1, sz: 1 }, { sp: 'ol', t0: 161, t1: 129, r0: -9, r1: -1, L: 1 },
    { sp: 'bb', t0: 153, t1: 127, r0: 14, r1: 8, L: 0 },
    { sp: 'sd', t0: 129, t1: 105, r0: 2, r1: -3, L: 1, sz: 0.98 }, { sp: 'se', t0: 104, t1: 76, r0: 6, r1: 26, L: 1 },
    { sp: 'se', t0: 133, t1: 111, r0: 2, r1: 17, L: 1 }, { sp: 'bb', t0: 132, t1: 102, r0: -10, r1: -4, L: 0 },
    { sp: 'ol', t0: 121, t1: 87, r0: 11, r1: 4, L: 1 }, { sp: 'sd', t0: 99, t1: 79, r0: 11, r1: 5, L: 0, sz: 0.92 },
    { sp: 'ol', t0: 96, t1: 70, r0: -11, r1: -5, L: 2 }, { sp: 'sd', t0: 101, t1: 77, r0: 7, r1: 2, L: 1, sz: 1 },
    { sp: 'bb', t0: 93, t1: 69, r0: -10, r1: -5, L: 0 },
    { sp: 'sd', t0: 73, t1: 47, r0: -2, r1: -7, L: 1, sz: 0.98 }, { sp: 'sd', t0: 51, t1: 27, r0: 7, r1: 2, L: 2, sz: 0.94 },
    { sp: 'ol', t0: 82, t1: 52, r0: -10, r1: -3, L: 1 }, { sp: 'bb', t0: 63, t1: 33, r0: 13, r1: 7, L: 0 },
    { sp: 'se', t0: 41, t1: 19, r0: 3, r1: 15, L: 1, sz: 0.9 },
    { sp: 'sd', t0: 217, t1: 249, r0: 0, r1: -7, L: 2, sz: 1.03 }, { sp: 'sd', t0: 233, t1: 257, r0: 11, r1: 5, L: 1, sz: 0.96 },
    { sp: 'ol', t0: 213, t1: 249, r0: 13, r1: 4, L: 1 }, { sp: 'se', t0: 227, t1: 253, r0: 5, r1: 25, L: 1 },
    { sp: 'bb', t0: 239, t1: 271, r0: -9, r1: -3, L: 0 }, { sp: 'ol', t0: 251, t1: 221, r0: -11, r1: -4, L: 0 },
    { sp: 'sd', t0: 271, t1: 291, r0: 2, r1: -3, L: 1, sz: 0.88 },
    { sp: 'se', t0: 304, t1: 337, r0: 0, r1: 11, L: 1 }, { sp: 'bb', t0: 313, t1: 339, r0: -9, r1: -4, L: 0 },
    { sp: 'sd', t0: 323, t1: 343, r0: 5, r1: 0, L: 1, sz: 0.88 },
    { sp: 'se', t0: 348, t1: 14, r0: 4, r1: 0, L: 0, sz: 0.78 }, { sp: 'sd', t0: 113, t1: 139, r0: -6, r1: -11, L: 1, sz: 0.84 },
    { sp: 'ol', t0: 300, t1: 276, r0: -6, r1: -10, L: 0 }, { sp: 'ol', t0: 36, t1: 64, r0: 9, r1: 15, L: 0 },
    { sp: 'sd', t0: 191, t1: 213, r0: 9, r1: 2, L: 2, sz: 1.05 }
  ];
  const GYP = [[172, 12, 6], [196, -12, 6], [208, 15, 6], [186, 4, 5], [48, 14, 5], [112, -12, 5],
               [240, 15, 6], [262, -12, 5], [286, 10, 4], [326, -11, 5], [136, 16, 4]];
  const GOLD = [[196, -15, 1], [48, 7, 0], [242, -13, 1], [128, 13, 0], [308, 4, 1]];

  // ---- palest back washes (paper bloom behind the band) ----
  for (let i = 0; i < 13; i++) {
    const th = (i * 29 + 14) % 360;
    if (th > 344 || th < 16) continue; // 12 o'clock gap stays open
    const q = PT(th, (rnd(i, 1) - 0.5) * 9);
    back.add('w' + (i % 3), { fill: i % 3 ? PALE : LGT, opacity: 0.3 },
      SH(blobP(9 + rnd(i, 2) * 5.5, 6.5 + rnd(i, 3) * 4.5, i + 50), q[0], q[1], th + 60));
  }

  SPRIGS.forEach((s, si) => {
    const d0 = Math.abs(s.t1 - s.t0) * 1.975; // ring units spanned
    const N = s.sp === 'sd' ? Math.max(3, Math.round(d0 / 9.8)) : s.sp === 'bb' ? Math.max(3, Math.round(d0 / 6.7))
      : s.sp === 'ol' ? Math.max(6, Math.round(d0 / 5.4)) : Math.max(4, Math.round(d0 / 7.2));
    const bk = B[s.L], SEG = 24, pts = [];
    for (let i = 0; i <= SEG; i++) {
      const u = i / SEG, th = s.t0 + (s.t1 - s.t0) * u;
      const ro = s.r0 + (s.r1 - s.r0) * u + Math.sin(u * 3.6 + si * 1.7) * 1.7;
      const q = PT(th, ro); pts.push({ x: q[0], y: q[1], ang: 0 });
    }
    for (let i = 0; i <= SEG; i++) {
      const a = pts[Math.max(0, i - 1)], b = pts[Math.min(SEG, i + 1)];
      pts[i].ang = Math.atan2(b.y - a.y, b.x - a.x);
    }
    const at = (u) => pts[Math.min(SEG, Math.max(0, Math.round(u * SEG)))];

    // tapered filled stem (4.5u base -> 1u tip), never a constant stroke
    const w0 = s.sp === 'sd' ? 1.05 : 0.8, LL = [], RR = [];
    for (let i = 0; i <= SEG; i++) {
      const u = i / SEG, w = (w0 + (0.24 - w0) * u) / 2, p = pts[i];
      const nx = -Math.sin(p.ang) * w, ny = Math.cos(p.ang) * w;
      LL.push(f(p.x + nx) + ' ' + f(p.y + ny)); RR.push(f(p.x - nx) + ' ' + f(p.y - ny));
    }
    bk.add('stem', { fill: hexA(STM, s.L === 0 ? 0.26 : s.L === 1 ? 0.46 : 0.62) },
      'M' + LL.join('L') + 'L' + RR.reverse().join('L') + 'Z');

    const pal = s.L === 0 ? { b: si % 2 ? PALE : LGT, o: 0.85, s: LGT, so: 0.55, bl: GLA }
      : s.L === 1 ? { b: si % 5 === 0 ? GLA : SAGE, o: 0.62, s: DEEP, so: 0.3, bl: GLA }
      : { b: si % 2 ? MIDG : SAGE, o: 0.74, s: DEEP, so: 0.42, bl: OLV };

    if (s.sp === 'sd' || s.sp === 'bb') { // sessile opposite pairs, stem runs through
      const big = s.sp === 'sd';
      for (let i = 0; i < N; i++) {
        const u = N === 1 ? 0.5 : 0.07 + 0.9 * (i / (N - 1)), p = at(u);
        const tp = u < 0.6 ? 1 : 1 - (u - 0.6) * 1.05;            // pairs shrink toward the tip
        const sz = (s.sz || 1) * tp * (0.84 + rnd(si, i) * 0.32);
        for (let d = -1; d <= 1; d += 2) {
          const fore = 0.72 + rnd(si, i * 6 + d * 2) * 0.3;       // foreshortening
          const rx = (big ? 5.85 : 3.7) * sz * fore * (0.94 + rnd(si, i * 4 + d) * 0.16);
          const ry = (big ? 5.5 : 3.5) * sz * (0.9 + rnd(si, i * 5 + d) * 0.22);
          const phi = p.ang + d * 1.5708 * (0.8 + rnd(si, i + d * 3) * 0.34);
          const off = ry * (0.62 + rnd(si, i * 9 + d) * 0.34);
          const cx = p.x + Math.cos(phi) * off, cy = p.y + Math.sin(phi) * off;
          const rot = phi * 57.2958 - 90 + (rnd(si, i * 3 + d) - 0.5) * 22;
          const P0 = blobP(rx, ry, si * 10 + i * 2 + d), g = i % 2;
          const dark = s.L === 2 && rnd(si, i * 7 + d) > 0.56;
          bk.add(si + 'b' + g + (dark ? 'd' : ''), { fill: dark ? DEEP : pal.b, opacity: pal.o + (dark ? 0.12 : 0) },
            SH(P0, cx, cy, rot));
          if (s.L > 0 || rnd(si, i + d) > 0.5) // shadow wash, pulled toward the stem
            bk.add(si + 's' + g + (dark ? 'd' : ''), { fill: dark ? INK : pal.s, opacity: pal.so },
              SH(P0, cx, cy, rot, 0.68, 0.25, -0.85));
          if (rnd(si, i * 11 + d) > 0.62) // glaucous bloom spot, different hue
            bk.add(si + 'm' + g, { fill: pal.bl, opacity: 0.28 },
              SH(blobP(rx * 0.44, ry * 0.38, si + i + d), cx, cy, rot, 1, -rx * 0.2, ry * 0.22));
          if (s.L === 2 && rnd(si, i * 13 + d) > 0.45) { // pigment-pooling rim, one side only
            const e = XF([[-rx * 0.92, -ry * 0.06], [-rx * 0.72, ry * 0.88], [rx * 0.18, ry * 0.95]], cx, cy, rot);
            bk.add(si + 'r', { fill: 'none', stroke: INK, strokeWidth: 0.34 * K, strokeLinecap: 'round', opacity: 0.4 },
              'M' + f(e[0][0]) + ' ' + f(e[0][1]) + 'Q' + f(e[1][0]) + ' ' + f(e[1][1]) + ' ' + f(e[2][0]) + ' ' + f(e[2][1]));
          }
        }
      }
    }

    if (s.sp === 'ol') { // olive: narrow lanceolate, alternate, pale underside on flipped leaves
      for (let i = 0; i < N; i++) {
        const u = 0.08 + 0.9 * (i / (N - 1)), p = at(u), d = i % 2 ? 1 : -1;
        const L = (12.4 - 3.6 * u) * (s.sz || 1) * (0.9 + rnd(si, i) * 0.2), W = 1.62 * (0.88 + rnd(si, i + 2) * 0.26);
        const phi = p.ang + d * (0.61 + rnd(si, i + 7) * 0.5);
        const cx = p.x + Math.cos(phi) * L * 0.44, cy = p.y + Math.sin(phi) * L * 0.44;
        const rot = phi * 57.2958 - 90 + (rnd(si, i * 3) - 0.5) * 14;
        const P0 = lanceP(L, W, si * 9 + i), g = i % 2, flip = rnd(si, i * 17) > 0.64;
        bk.add(si + 'o' + g + (flip ? 'f' : ''), { fill: flip ? LGT : (s.L === 0 ? PALE : OLV), opacity: flip ? 0.6 : pal.o * 0.98 },
          SH(P0, cx, cy, rot));
        if (!flip && s.L > 0) bk.add(si + 'x' + g, { fill: s.L === 2 ? DEEP : OLV, opacity: 0.34 }, SH(P0, cx, cy, rot, 0.62, 0.3, -0.5));
        if (flip) bk.add(si + 'xf' + g, { fill: DEEP, opacity: 0.18 }, SH(P0, cx, cy, rot, 0.72, -0.35, 0.3));
      }
      if (s.L > 0) { // two small olives with a pale highlight
        const p = at(0.34);
        for (let o = 0; o < 2; o++) {
          const ox = p.x + (rnd(si, o + 30) - 0.5) * 5, oy = p.y + (rnd(si, o + 40) - 0.5) * 5;
          bk.add(si + 'fr' + o, { fill: STM, opacity: 0.62 }, SH(blobP(1.45, 1.75, si + o), ox, oy, 0));
          bk.add(si + 'fh', { fill: LGT, opacity: 0.5 }, SH(blobP(0.5, 0.6, si + o), ox - 0.45, oy - 0.5, 0));
        }
      }
    }

    if (s.sp === 'se') { // seeded euc: drooping lanceolate leaves + seed-pod umbels
      for (let i = 0; i < N; i++) {
        const u = 0.08 + 0.9 * (i / (N - 1)), p = at(u), d = i % 2 ? 1 : -1;
        const L = (15.5 - 5 * u) * (s.sz || 1) * (0.88 + rnd(si, i) * 0.24), W = 2.15 * (0.9 + rnd(si, i + 3) * 0.22);
        let phi = p.ang + d * (0.8 + rnd(si, i + 5) * 0.45);
        phi = Math.atan2(Math.sin(phi) * 0.76 + 0.26, Math.cos(phi) * 0.76); // droop
        const cx = p.x + Math.cos(phi) * L * 0.45, cy = p.y + Math.sin(phi) * L * 0.45;
        const rot = phi * 57.2958 - 90 + (rnd(si, i * 3) - 0.5) * 12;
        const P0 = lanceP(L, W, si * 7 + i + 2), g = i % 2;
        bk.add(si + 'b' + g, { fill: s.L === 0 ? LGT : (si % 3 === 0 ? GLA : SAGE), opacity: s.L === 0 ? 0.8 : 0.6 }, SH(P0, cx, cy, rot));
        bk.add(si + 's' + g, { fill: s.L === 0 ? PALE : DEEP, opacity: s.L === 0 ? 0.5 : 0.3 }, SH(P0, cx, cy, rot, 0.6, 0.35, -1.1));
        if (rnd(si, i * 19) > 0.6) bk.add(si + 'm' + g, { fill: OLV, opacity: 0.24 },
          SH(blobP(W * 0.5, L * 0.22, si + i), cx, cy, rot, 1, 0, L * 0.1));
      }
      [0.5, 0.86].forEach((cu, ci) => {
        const p = at(cu), np = 6 + ci;
        for (let j = 0; j < np; j++) {
          const a = (j / np) * 6.2832 + rnd(si, ci * 5 + j), rr = 1.6 + rnd(si, j + ci) * 2.4;
          const px = p.x + Math.cos(a) * rr, py = p.y + Math.sin(a) * rr + 1.2;
          bk.add(si + 'p' + (j % 2), { fill: j % 3 ? OLV : STM, opacity: 0.72 },
            SH(blobP(0.85 + rnd(si, j) * 0.3, 1.05 + rnd(si, j + 4) * 0.35, si + j), px, py, a * 57.3));
          if (j % 2 === 0) bk.add(si + 'pc', { fill: STM, opacity: 0.8 }, SH(blobP(0.36, 0.36, j), px + 0.3, py - 0.75, 0)); // operculum
        }
      });
    }

    if (s.L === 2 && si % 2 === 0) { // unopened buds on thread stems at the tips
      const p = at(0.99); let td = '';
      for (let j = 0; j < 3; j++) {
        const a = p.ang + (j - 1) * 0.5, ex = p.x + Math.cos(a) * 3.4, ey = p.y + Math.sin(a) * 3.4;
        td += 'M' + f(p.x) + ' ' + f(p.y) + 'L' + f(ex) + ' ' + f(ey);
        bk.add(si + 'bud', { fill: MIDG, opacity: 0.75 }, SH(blobP(0.75 + rnd(si, j) * 0.35, 0.8 + rnd(si, j) * 0.3, si + j), ex, ey, 0));
      }
      bk.add(si + 'th', { fill: 'none', stroke: hexA(STM, 0.55), strokeWidth: 0.25 * K }, td);
    }
  });

  // ---- gypsophila: florets fan from a node, 40% sunk behind the foliage ----
  GYP.forEach((g, gi) => {
    const q = PT(g[0], g[1]), n = g[2]; let sd = '';
    for (let i = 0; i < n; i++) {
      const a = -1.5708 + (i - (n - 1) / 2) * 0.34 + (rnd(gi, i) - 0.5) * 0.5, len = 2.4 + rnd(gi, i + 9) * 4.6;
      const fx = q[0] + Math.cos(a) * len, fy = q[1] + Math.sin(a) * len;
      sd += 'M' + f(q[0]) + ' ' + f(q[1]) + 'Q' + f((q[0] + fx) / 2 + (rnd(gi, i + 3) - 0.5) * 2) + ' ' +
            f((q[1] + fy) / 2) + ' ' + f(fx) + ' ' + f(fy);
      const r = 1.15 + rnd(gi, i + 5) * 0.6, fr = rnd(gi, i + 13) > 0.4, t = fr ? gypF : gypB;
      t.add('fl' + (fr ? 1 : 0), { fill: FLW, opacity: fr ? 0.97 : 0.45 }, flor(fx, fy, r, gi + i));
      t.add('fc' + (fr ? 1 : 0), { fill: BUF, opacity: fr ? 0.9 : 0.4 }, SH(blobP(r * 0.3, r * 0.3, gi + i), fx, fy, 0));
      if (fr) t.add('fs', { fill: 'none', stroke: LGT, strokeWidth: 0.34 * K, opacity: 0.72, strokeLinecap: 'round' },
        'M' + f(fx - r * 0.8) + ' ' + f(fy - r * 0.35) + 'A' + f(r) + ' ' + f(r) + ' 0 0 0 ' + f(fx + r * 0.25) + ' ' + f(fy + r * 0.8));
    }
    gypB.add('gs', { fill: 'none', stroke: hexA(HAIR, 0.7), strokeWidth: 0.26 * K, strokeLinecap: 'round' }, sd);
  });
  [[357, 9], [7, -7], [352, -6]].forEach((g, i) => { // loose florets floating in the top gap
    const q = PT(g[0], g[1]);
    gypF.add('fl1', { fill: FLW, opacity: 0.97 }, flor(q[0], q[1], 1.05, i + 40));
    gypF.add('fc1', { fill: BUF, opacity: 0.9 }, SH(blobP(0.32, 0.32, i), q[0], q[1], 0));
  });

  // ---- restrained gold: five small leaves, 3-stop gradient ----
  GOLD.forEach((g, i) => {
    const q = PT(g[0], g[1]);
    trim.add('gold', { fill: 'url(#ewGold)', opacity: 0.62 },
      SH(g[2] ? lanceP(7, 1.5, i + 70) : lanceP(8.5, 1.2, i + 80), q[0], q[1], (g[0] + 140) % 360));
  });
  // ---- stray paint speckles outside the ring ----
  for (let i = 0; i < 14; i++) {
    const a = (i * 47) % 360;
    const rr = ((i % 3 === 0 ? 26 : 20) + rnd(i, 2) * 16) * (rnd(i, 5) > 0.5 ? 1 : -1) * 0.9 + (i % 4 === 0 ? 8 : 0);
    const q = PT(a, rr);
    front.add('dust' + (i % 2), { fill: i % 3 ? DEEP : OLV, opacity: 0.16 + (i % 3) * 0.07 },
      SH(blobP(0.4 + rnd(i, 9) * 0.9, 0.45 + rnd(i, 10) * 0.8, i), q[0] + (rnd(i, 7) - 0.5) * 10, q[1] + (rnd(i, 8) - 0.5) * 10, 0));
  }

  const P = (arr, pre) => arr.map((e, i) => <path key={pre + i} {...e} />);

  return (
    <svg viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice" aria-hidden
      className="absolute inset-0 w-full h-full pointer-events-none" style={{ isolation: 'isolate' }}>
      <defs>
        <linearGradient id="ewGold" x1="0" y1="0" x2="1" y2="0.5">
          <stop offset="0" stopColor="#8E6F3E" /><stop offset="0.5" stopColor="#E6C67C" /><stop offset="1" stopColor="#A8853F" />
        </linearGradient>
        <filter id="ewWc" x="-12%" y="-10%" width="124%" height="120%">
          <feTurbulence type="fractalNoise" baseFrequency="0.11" numOctaves="4" seed="4" result="n" />
          <feDisplacementMap in="SourceGraphic" in2="n" scale="1.1" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id="ewSoft" x="-15%" y="-15%" width="130%" height="130%"><feGaussianBlur stdDeviation="0.5" /></filter>
        <filter id="ewGrain" x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" seed="11" />
          <feColorMatrix type="saturate" values="0" />
          <feComponentTransfer>
            <feFuncR type="linear" slope="0.35" intercept="0.6" />
            <feFuncG type="linear" slope="0.35" intercept="0.6" />
            <feFuncB type="linear" slope="0.35" intercept="0.6" />
            <feFuncA type="discrete" tableValues="1" />
          </feComponentTransfer>
        </filter>
      </defs>
      <rect width="300" height="400" fill="#FAF7F0" />
      <g filter="url(#ewWc)" style={{ mixBlendMode: 'multiply' }}>
        <g filter="url(#ewSoft)" opacity="0.62">{P(back.a, 'k')}</g>
        <g opacity="0.92">{P(gypB.a, 'q')}</g>
        <g>{P(mid.a, 'm')}</g>
        <g>{P(front.a, 'f')}</g>
      </g>
      <g>{P(gypF.a, 'g')}</g>
      <g style={{ mixBlendMode: 'multiply' }} opacity="0.9">{P(trim.a, 't')}</g>
      <rect width="300" height="400" filter="url(#ewGrain)" opacity="0.075" style={{ mixBlendMode: 'multiply' }} />
    </svg>
  );
}

// ─── MonogramCrest — monogram-crest
function MonogramCrest({ color, color2, accent, scale = 300 }) {
  const D2R = Math.PI / 180;
  const CX = 150, CY = 78;
  const R_MAIN = 39, R_HAIR = 41.8, R_STEM = 46.5;
  const K = scale < 200 ? 1.45 : 1;
  const f = (n) => Math.round(n * 100) / 100;
  const sx = (a, r) => CX + r * Math.sin(a * D2R);
  const sy = (a, r) => CY - r * Math.cos(a * D2R);
  const arc = (r, a0, a1) =>
    'M ' + f(sx(a0, r)) + ' ' + f(sy(a0, r)) + ' A ' + r + ' ' + r + ' 0 1 1 ' + f(sx(a1, r)) + ' ' + f(sy(a1, r));

  const almond = (x0, y0, ax, ay, len) => {
    const x1 = x0 + ax * len, y1 = y0 + ay * len;
    const mx = x0 + ax * len * 0.5, my = y0 + ay * len * 0.5;
    const w = len / 3.15;
    const qx = -ay * w, qy = ax * w;
    return 'M ' + f(x0) + ' ' + f(y0) +
      ' Q ' + f(mx + qx) + ' ' + f(my + qy) + ' ' + f(x1) + ' ' + f(y1) +
      ' Q ' + f(mx - qx) + ' ' + f(my - qy) + ' ' + f(x0) + ' ' + f(y0) + ' Z';
  };

  // ---- laurel leaves: 10 per sprig, alternating outer / inner, hand-drawn variance
  const leaves = [];
  [1, -1].forEach((dir, s) => {
    for (let i = 0; i < 10; i++) {
      const t = i / 9;
      const a = dir * (150 - 100 * t) * D2R;
      const inward = i % 2 === 1;
      const lv = 1 + (((i * 37 + s * 53) % 15) - 7) / 100;
      let len = (8.2 - 4.9 * t) * lv;
      if (inward) len *= 0.78;
      const tilt = (inward ? -1 : 1) * (30 + (((i * 23 + s * 31) % 7) - 3)) * D2R;
      const dxT = -dir * Math.cos(a), dyT = -dir * Math.sin(a);
      const nx = Math.sin(a), ny = -Math.cos(a);
      const ax = dxT * Math.cos(tilt) + nx * Math.sin(tilt);
      const ay = dyT * Math.cos(tilt) + ny * Math.sin(tilt);
      const r0 = R_STEM - 0.25;
      leaves.push({
        k: 'lf' + s + '-' + i,
        d: almond(CX + r0 * nx, CY + r0 * ny, ax, ay, len),
        o: 0.94 - 0.05 * ((i * 17 + s * 5) % 4) - (inward ? 0.09 : 0)
      });
    }
  });

  // ---- tapered stems (filled paths, not strokes)
  const stems = [1, -1].map((dir, s) => {
    const N = 24, outer = [], inner = [];
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      const a = dir * (153 - 106 * t) * D2R;
      const hw = 0.34 - 0.21 * t;
      const nx = Math.sin(a), ny = -Math.cos(a);
      const bx = CX + R_STEM * nx, by = CY + R_STEM * ny;
      outer.push(f(bx + nx * hw) + ' ' + f(by + ny * hw));
      inner.push(f(bx - nx * hw) + ' ' + f(by - ny * hw));
    }
    inner.reverse();
    return { k: 'st' + s, d: 'M ' + outer.join(' L ') + ' L ' + inner.join(' L ') + ' Z' };
  });

  // ---- berries, 3 per sprig, clustered at the base
  const berries = [];
  [1, -1].forEach((dir, s) => {
    [[147, 43.4, 1.05], [142, 42.5, 0.86], [144.2, 45.2, 0.7]].forEach((b, i) => {
      const a = dir * b[0] * D2R;
      berries.push({
        k: 'br' + s + '-' + i,
        cx: f(CX + b[1] * Math.sin(a)),
        cy: f(CY - b[1] * Math.cos(a)),
        r: b[2]
      });
    });
  });

  // ---- Didone-ish letterforms, built from overlapping filled strokes (counters stay open)
  const A_PARTS = [
    'M-1 -22.4L0.2 -22.4L-7.4 0L-10.2 0Z',
    'M-0.3 -22.4L1.2 -22.4L10.4 0L5.3 0Z',
    'M-7.2 -6.7L7.4 -6.7L7.4 -5.3L-7.2 -5.3Z',
    'M-11 -1.35L-6.2 -1.35L-6.2 0L-11 0Z',
    'M4.3 -1.35L11.2 -1.35L11.2 0L4.3 0Z',
    'M-3 -22.5L1.5 -22.5L1.5 -21.3L-2.1 -21.3Z'
  ];
  const J_PARTS = [
    'M-7.2 -22.4L5.9 -22.4L5.9 -20.9L-7.2 -20.9Z',
    'M5.3 -21.4L5.3 -7.4C5.3 -1.5 1.7 0.9 -2.3 0.9C-5.9 0.9 -8 -1.1 -8.6 -4.6L-5.7 -5.5C-5.3 -2.8 -4.3 -1.6 -2.5 -1.6C-0.5 -1.6 1.5 -3.1 1.5 -7.4L1.5 -21.4Z'
  ];
  const A_T = 'translate(132.8 90) scale(1.08)';
  const J_T = 'translate(166.8 90) scale(1.08)';

  const BSY = 364;
  const botLeaves = [
    almond(150, BSY - 1.2, 0, -1, 9.6),
    almond(150, BSY + 1.4, -0.695, -0.719, 7.4),
    almond(150, BSY + 1.4, 0.695, -0.719, 7.4)
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <filter id="mcGrain" x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" seed="7" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <filter id="mcSoft" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="0.4" />
        </filter>
        <radialGradient id="mcVig" cx="50%" cy="44%" r="72%">
          <stop offset="55%" stopColor={hexA(color2, 0)} />
          <stop offset="100%" stopColor={hexA(color2, 0.11)} />
        </radialGradient>
      </defs>

      {/* letterpress deboss — sits beneath the ink */}
      <g filter="url(#mcSoft)" opacity="0.55">
        <path d={arc(R_MAIN, 209, 151)} fill="none" stroke={hexA('#FFFFFF', 0.5)} strokeWidth="0.85" transform="translate(0 0.55)" />
        <line x1="150" y1="63.5" x2="150" y2="93.5" stroke={hexA('#FFFFFF', 0.5)} strokeWidth="0.45" />
        <g transform={A_T + ' translate(0 0.5)'}>
          {A_PARTS.map((d, i) => <path key={'ad' + i} d={d} fill={hexA('#FFFFFF', 0.42)} />)}
        </g>
        <g transform={J_T + ' translate(0 0.5)'}>
          {J_PARTS.map((d, i) => <path key={'jd' + i} d={d} fill={hexA('#FFFFFF', 0.42)} />)}
        </g>
      </g>

      <g shapeRendering="geometricPrecision">
        {/* laurel — stems, leaves, berries */}
        {stems.map((s) => <path key={s.k} d={s.d} fill={hexA(color2, 0.9)} />)}
        {leaves.map((l) => <path key={l.k} d={l.d} fill={hexA(color2, l.o)} />)}
        {berries.map((b) => <circle key={b.k} cx={b.cx} cy={b.cy} r={b.r} fill={hexA(accent, 0.92)} />)}

        {/* double-rule ring, interrupted once at the bottom */}
        <path d={arc(R_HAIR, 212, 148)} fill="none" stroke={hexA(color, 0.5)} strokeWidth={0.32 * K} />
        <path d={arc(R_MAIN, 209, 151)} fill="none" stroke={hexA(color, 0.92)} strokeWidth="0.85" />

        {/* the vertical rule — butt caps, overshooting cap line and baseline */}
        <line x1="150" y1="63" x2="150" y2="93" stroke={color} strokeWidth={0.45 * K} strokeLinecap="butt" />

        {/* initials */}
        <g transform={A_T}>
          {A_PARTS.map((d, i) => <path key={'a' + i} d={d} fill={hexA(color, 0.93)} />)}
        </g>
        <g transform={J_T}>
          {J_PARTS.map((d, i) => <path key={'j' + i} d={d} fill={hexA(color, 0.93)} />)}
        </g>

        {/* date, set inside the ring's gap */}
        <text
          x="150.45"
          y="116.5"
          textAnchor="middle"
          fontFamily="Didot, 'Bodoni MT', 'Times New Roman', Georgia, serif"
          fontSize="5.4"
          letterSpacing="0.9"
          fill={hexA(color2, 0.85)}
        >
          MMXXVI
        </text>

        {/* rule divider: two hairline segments flanking a lozenge */}
        <line x1="130.4" y1="136" x2="146.4" y2="136" stroke={hexA(color, 0.5)} strokeWidth={0.32 * K} />
        <line x1="153.6" y1="136" x2="169.6" y2="136" stroke={hexA(color, 0.5)} strokeWidth={0.32 * K} />
        <path d="M150 134.1L151.9 136L150 137.9L148.1 136Z" fill={hexA(accent, 0.85)} />

        {/* bottom sprig */}
        <g opacity="0.62">
          <line x1="150" y1={BSY + 3.6} x2="150" y2={BSY - 1} stroke={hexA(color2, 0.9)} strokeWidth="0.3" />
          {botLeaves.map((d, i) => (
            <path key={'bl' + i} d={d} fill={hexA(i === 0 ? color2 : color2, i === 0 ? 0.95 : 0.8)} />
          ))}
          <circle cx="150" cy={BSY + 4.6} r="0.75" fill={hexA(accent, 0.9)} />
        </g>
      </g>

      {/* paper */}
      <rect x="0" y="0" width="300" height="400" fill="url(#mcVig)" />
      <rect x="0" y="0" width="300" height="400" filter="url(#mcGrain)" opacity="0.045" style={{ mixBlendMode: 'multiply' }} />
    </svg>
  );
}

// ─── GildedCartoucheFrame — gold-filigree
function GildedCartoucheFrame({ color, color2, accent, scale = 300 }) {
  // one tonal family only: body gold, shadow gold, champagne highlight
  const GOLD = color || '#BF953F', DEEP = color2 || '#8A6A25', LITE = accent || '#E8CE7B';
  const uid = 'gf' + [GOLD, DEEP, LITE].join('').replace(/[^a-zA-Z0-9]/g, '');
  const K = scale < 190 ? 1.25 : 1;
  const detail = scale >= 190;
  const F = (n) => Math.round(n * 100) / 100;
  const TIP = 0.2;

  // ---- curve maths: sampled beziers + true logarithmic spirals -------------
  const bez = (p0, p1, p2, p3, n) => {
    const o = [];
    for (let i = 0; i <= n; i++) {
      const t = i / n, u = 1 - t, a = u * u * u, b = 3 * u * u * t, c = 3 * u * t * t, d = t * t * t;
      o.push([a * p0[0] + b * p1[0] + c * p2[0] + d * p3[0], a * p0[1] + b * p1[1] + c * p2[1] + d * p3[1]]);
    }
    return o;
  };
  const spiral = (cx, cy, r0, a0, turns, dir) => {
    const tot = turns * 2 * Math.PI, dec = Math.pow(TIP, 1 / tot), o = [];
    for (let i = 0; i <= 30; i++) {
      const th = (tot * i) / 30, r = r0 * Math.pow(dec, th), a = (a0 * Math.PI) / 180 + dir * th;
      o.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
    }
    return o;
  };
  const sTan = (a0, turns, dir) => {
    const k = -Math.log(TIP) / (turns * 2 * Math.PI), a = (a0 * Math.PI) / 180;
    const x = -k * Math.cos(a) - dir * Math.sin(a), y = -k * Math.sin(a) + dir * Math.cos(a);
    const m = Math.hypot(x, y) || 1;
    return [x / m, y / m];
  };
  // every ornament is a closed filled ribbon whose two edges converge to a point
  const ribbon = (pts, wfn) => {
    const n = pts.length, A = [], B = [];
    for (let i = 0; i < n; i++) {
      const a = pts[Math.max(0, i - 1)], b = pts[Math.min(n - 1, i + 1)];
      let dx = b[0] - a[0], dy = b[1] - a[1];
      const m = Math.hypot(dx, dy) || 1; dx /= m; dy /= m;
      const t = i / (n - 1), w = wfn(t) / 2;
      A.push([pts[i][0] - dy * w, pts[i][1] + dx * w]);
      B.push([pts[i][0] + dy * w, pts[i][1] - dx * w]);
    }
    const f = (q) => q.map((z) => F(z[0]) + ',' + F(z[1])).join('L');
    return 'M' + f(A) + 'L' + f(B.reverse()) + 'Z';
  };
  const tw = (w0, w1, p) => (t) => w1 + (w0 - w1) * Math.pow(1 - t, p === undefined ? 1.5 : p);
  const lw = (wm, k) => (t) => wm * Math.sin(Math.PI * Math.pow(t, k === undefined ? 0.78 : k));
  const scrollPts = (root, c1, cx, cy, r0, a0, turns, dir, stem) => {
    const S = [cx + r0 * Math.cos((a0 * Math.PI) / 180), cy + r0 * Math.sin((a0 * Math.PI) / 180)];
    const t2 = sTan(a0, turns, dir);
    return bez(root, c1, [S[0] - stem * t2[0], S[1] - stem * t2[1]], S, 12)
      .concat(spiral(cx, cy, r0, a0, turns, dir).slice(1));
  };

  // ---- emitters ------------------------------------------------------------
  let n = 0;
  const L = [];
  let T = L;
  const add = (d) => T.push(<path key={'p' + n++} d={d} />);
  const cut = (d) => T.push(<path key={'c' + n++} d={d} fill="#000" />);
  const dot = (x, y, r) => T.push(<circle key={'o' + n++} cx={F(x)} cy={F(y)} r={F(r)} />);
  const ln = (d, w) => T.push(<path key={'l' + n++} d={d} fill="none" strokeWidth={F(w * K)} />);
  const sub = (tr, fn) => {
    const prev = T, arr = [];
    T = arr; fn(); T = prev;
    T.push(<g key={'g' + n++} transform={tr}>{arr}</g>);
  };
  const scr = (root, c1, cx, cy, r0, a0, turns, dir, w0, w1, stem, eye) => {
    const p = scrollPts(root, c1, cx, cy, r0, a0, turns, dir, stem);
    add(ribbon(p, tw(w0 * K, w1 * K)));
    if (eye) { const q = p[p.length - 1]; dot(q[0], q[1], eye * K); }
  };
  const leaf = (pts, wm, vein) => {
    add(ribbon(pts, lw(wm * K)));
    if (vein && detail) cut(ribbon(pts.slice(2, pts.length - 5), tw(0.42, 0.12, 1.2)));
  };
  const loz = (x, y, w, h) =>
    add('M' + F(x) + ',' + F(y - h) + 'L' + F(x + w) + ',' + F(y) + 'L' + F(x) + ',' + F(y + h) + 'L' + F(x - w) + ',' + F(y) + 'Z');

  // ---- primary rail: whole left side, corner sweeps + waist pinch ----------
  ln('M118,30L48,30C35.3,30 25,40.3 25,53L25,168C25,182 29.5,186 29.5,200C29.5,214 25,218 25,232L25,347C25,359.7 35.3,370 48,370L118,370', 1.5);
  add(ribbon(bez([110, 30], [118, 30], [124, 30.1], [130, 30.4], 10), tw(1.5 * K, 0, 1.4)));
  add(ribbon(bez([110, 370], [119, 370], [126, 369.9], [133, 369.6], 10), tw(1.5 * K, 0, 1.4)));

  // ---- corner cluster: drawn once, mirrored (never rotated) ----------------
  const corner = (rich) => {
    scr([25.9, 74], [34, 76], 47, 55.5, 9.2, 45, 1.45, -1, 1.5, 0.24, 5.5, 0.5);
    scr([63.5, 30.7], [66, 36], 57.5, 41.5, 5.6, 20, 1.3, 1, 1.05, 0.2, 4.2, 0.38);
    scr([64.6, 31.4], [68, 33.6], 72.5, 37.2, 4.4, 135, 1.2, -1, 0.72, 0.16, 3.2, 0.3);
    leaf(bez([26.8, 70], [32.5, 66.4], [39, 66.6], [44.6, 70.6], 16), 3.5, true);
    leaf(bez([51.4, 37.4], [50, 41], [48.4, 43], [45.6, 46.6], 14), 2.5, true);
    scr([33.4, 73.4], [36, 77], 31.4, 80.4, 3.1, 30, 1.15, 1, 0.55, 0.14, 2.6, 0.24);
    add(ribbon(bez([50.5, 36.6], [42, 36.6], [33, 41], [33, 51.6], 16)
      .concat(bez([33, 51.6], [33, 58], [33, 62.4], [33.6, 67], 8).slice(1)), lw(1 * K, 1)));
    dot(35.5, 38.3, 0.9 * K);
    dot(44, 33.6, 0.56 * K);
    dot(29, 50, 0.56 * K);
    scr([51.5, 63.6], [56, 67], 62.5, 68.5, 3.1, 160, 1.2, -1, 0.55, 0.14, 2.6, 0.24);
    if (rich) {
      add(ribbon(bez([44.8, 70.8], [47.4, 73.2], [48.8, 75.2], [49.8, 78.2], 10), tw(0.36 * K, 0, 1.2)));
      add(ribbon(bez([34, 76.4], [31.4, 78.4], [29.8, 79.8], [28, 80.4], 10), tw(0.34 * K, 0, 1.2)));
      add(ribbon(bez([59.2, 45.4], [61.8, 50], [62.4, 53.6], [61.4, 57.2], 10), tw(0.36 * K, 0, 1.2)));
    }
    dot(27, 72.4, 0.92 * K);
    dot(63.9, 31.6, 0.74 * K);
    dot(37.6, 45.6, 0.7 * K);
    dot(46.8, 44.6, 0.54 * K);
  };
  corner(true);
  sub('translate(0,400) scale(1,-1)', () => corner(false));

  // ---- waist ear straddling the pinch --------------------------------------
  const ear = () => {
    scr([25.6, 181.4], [29.4, 186.4], 36.6, 189.6, 5.4, 135, 1.35, -1, 1.1, 0.2, 3.8, 0.38);
    add(ribbon(bez([39.6, 193.4], [42.8, 195], [44.6, 195.4], [47.6, 194.8], 10), tw(0.44 * K, 0, 1.2)));
    dot(26.9, 183, 0.78 * K);
    dot(31.4, 194.6, 0.5 * K);
  };
  ear();
  sub('translate(0,400) scale(1,-1)', ear);
  add(ribbon(bez([32.4, 200], [28.6, 200], [25.4, 200], [21.4, 200], 12), lw(3 * K, 0.95)));
  dot(20.4, 200, 0.6 * K);

  // ---- sprigs punctuating the long clean rail runs -------------------------
  const sprigPair = () => {
    const sprig = () => {
      scr([92, 30.5], [91.4, 33.2], 87.4, 35.2, 3, 20, 1.15, 1, 0.6, 0.14, 2.4, 0.26);
      add(ribbon(bez([92, 30.6], [93.6, 33.2], [93.8, 35], [93.4, 37.4], 10), tw(0.36 * K, 0, 1.2)));
    };
    sprig();
    sub('translate(184,0) scale(-1,1)', sprig);
    dot(92, 37, 0.8 * K);
  };
  sprigPair();
  sub('translate(92,400) scale(0.92,-0.92) translate(-92,0)', sprigPair);

  // ---- broken interior keyline + lozenge accents ---------------------------
  const kw = 0.34;
  ln('M60,48L114,48', kw); ln('M139,48L147.2,48', kw);
  ln('M60,352L117,352', kw); ln('M141,352L147.2,352', kw);
  ln('M43,66L43,186', kw); ln('M43,214L43,334', kw);
  loz(43, 200, 1.1 * K, 2.6 * K);
  loz(150, 48, 1.1 * K, 2.6 * K);
  loz(150, 352, 1 * K, 2.3 * K);

  // ---- crown crest, re-used at 85% and flipped for the pendant -------------
  const crown = () => {
    scr([149.6, 44], [141, 46], 130, 35, 8, 135, 1.4, 1, 1.35, 0.22, 5.4, 0.46);
    scr([149.4, 41.4], [146.6, 35], 135, 21.6, 4.6, 135, 1.3, 1, 0.95, 0.18, 4.6, 0.36);
    leaf(bez([147.6, 45.6], [143.4, 46.4], [139.4, 45.6], [134.6, 43], 16), 2.5, true);
    add(ribbon(bez([136.6, 40.4], [130.4, 43.6], [125.4, 44.8], [119.6, 44.2], 14), tw(0.55 * K, 0, 1.3)));
    scr([147.6, 43.8], [145.6, 40.6], 143.2, 38.4, 2.6, 20, 1.15, 1, 0.6, 0.14, 2.2, 0.26);
    add(ribbon(bez([150, 44], [149.4, 35], [149.4, 26], [150, 19.4], 16), tw(2.6 * K, 0, 2)));
    loz(150, 44.2, 3.6 * K, 2.8 * K);
    dot(150, 18.6, 1.3 * K);
    dot(146.6, 45.4, 0.76 * K);
    dot(130.4, 35.8, 0.38 * K);
  };
  crown();
  sub('translate(150,370) scale(0.85,-0.85) translate(-150,-30)', crown);
  scr([123.6, 42.8], [120.6, 42], 117.8, 38.6, 3.2, 60, 1.2, 1, 0.55, 0.14, 2.8, 0.24);

  const mk = 'url(#' + uid + 'm)';
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id={uid + 'f'} gradientUnits="userSpaceOnUse" x1="14" y1="12" x2="286" y2="388">
          <stop offset="0" stopColor={DEEP} />
          <stop offset="0.17" stopColor={GOLD} />
          <stop offset="0.34" stopColor={GOLD} stopOpacity="0.42" />
          <stop offset="0.51" stopColor={DEEP} />
          <stop offset="0.68" stopColor={GOLD} stopOpacity="0.46" />
          <stop offset="0.84" stopColor={DEEP} />
          <stop offset="1" stopColor={GOLD} />
        </linearGradient>
        <radialGradient id={uid + 'v'} cx="50%" cy="46%" r="74%">
          <stop offset="0.5" stopColor={LITE} stopOpacity="0" />
          <stop offset="1" stopColor={LITE} stopOpacity="0.15" />
        </radialGradient>
        <filter id={uid + 'g'} x="0" y="0" width="100%" height="100%" colorInterpolationFilters="sRGB">
          <feTurbulence type="fractalNoise" baseFrequency="1.3" numOctaves="3" seed="9" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.54 0 0 0 0 0.45 0 0 0 0 0.28 0 0 0 0.6 0" />
        </filter>
        <mask id={uid + 'm'} maskUnits="userSpaceOnUse" x="0" y="0" width="300" height="400">
          <g fill="#fff" stroke="#fff" strokeWidth={0} strokeLinecap="butt" strokeLinejoin="round">
            {L}
            <g transform="translate(300,0) scale(-1,1)">{L}</g>
          </g>
        </mask>
      </defs>
      <rect width="300" height="400" fill={'url(#' + uid + 'v)'} />
      <rect x="-3" y="-3" width="306" height="406" transform="translate(-0.4,-0.45)" fill={hexA(GOLD, 0.5)} mask={mk} />
      <rect x="-3" y="-3" width="306" height="406" transform="translate(0.45,0.55)" fill={hexA(DEEP, 0.2)} mask={mk} />
      <rect x="-3" y="-3" width="306" height="406" fill={'url(#' + uid + 'f)'} mask={mk} />
      <rect width="300" height="400" fill={LITE} filter={'url(#' + uid + 'g)'} opacity={0.055} style={{ mixBlendMode: 'multiply' }} />
    </svg>
  );
}

// ─── StringLightsChalkboard — string-lights
function StringLightsChalkboard({ color, color2, accent, scale = 300 }) {
  const STRANDS = [
    { x0: -28, y0: 10, cx: 118, cy: 64, x1: 328, y1: 30, n: 15, r: 2.0, o: 1, cord: 'a', w: 0.9 },
    { x0: -22, y0: 34, cx: 196, cy: 86, x1: 326, y1: 12, n: 14, r: 1.9, o: 0.95, cord: 'b', w: 0.85 },
    { x0: -26, y0: 56, cx: 92, cy: 112, x1: 324, y1: 68, n: 13, r: 2.1, o: 1, cord: 'c', w: 0.95 },
    { x0: -18, y0: 72, cx: 214, cy: 124, x1: 322, y1: 46, n: 12, r: 1.75, o: 0.9, cord: 'a', w: 0.8 },
    { x0: -26, y0: 2, cx: 176, cy: 56, x1: 330, y1: 40, n: 13, r: 1.6, o: 0.82, cord: 'b', w: 0.75 },
    { x0: 48, y0: -14, cx: 190, cy: 46, x1: 326, y1: 6, n: 8, r: 1.7, o: 0.88, cord: 'c', w: 0.8 },
    { x0: -20, y0: 22, cx: 150, cy: 98, x1: 320, y1: 86, n: 11, r: 1.05, o: 0.5, cord: 'd', w: 0.6 },
    { x0: -24, y0: 392, cx: 140, cy: 414, x1: 326, y1: 382, n: 9, r: 1.5, o: 0.58, cord: 'd', w: 0.7 },
  ];

  const path = (s) => `M ${s.x0} ${s.y0} Q ${s.cx} ${s.cy} ${s.x1} ${s.y1}`;

  const bulbs = [];
  STRANDS.forEach((s, si) => {
    for (let i = 0; i < s.n; i += 1) {
      const t = i / (s.n - 1);
      const u = 1 - t;
      const tx = u * u * s.x0 + 2 * u * t * s.cx + t * t * s.x1;
      const ty = u * u * s.y0 + 2 * u * t * s.cy + t * t * s.y1;
      const a = ((i * 37 + si * 53) % 19) / 19;
      const b = ((i * 61 + si * 29) % 23) / 23;
      const r = s.r * (0.84 + a * 0.34);
      bulbs.push({
        k: `s${si}b${i}`,
        tx,
        ty,
        x: tx + (b - 0.5) * 1.7,
        y: ty + r * 1.15 + (a - 0.5) * 0.6,
        r,
        o: Math.min(1, s.o * (0.76 + b * 0.3)),
        gold: (i + si * 2) % 5 === 0,
        spark: r > 1.95 && (i * 7 + si) % 9 === 0,
      });
    }
  });

  const smudges = [
    { cx: 62, cy: 74, rx: 66, ry: 40, rot: -14, o: 0.55 },
    { cx: 238, cy: 148, rx: 78, ry: 46, rot: 12, o: 0.4 },
    { cx: 150, cy: 268, rx: 96, ry: 54, rot: -6, o: 0.3 },
    { cx: 44, cy: 322, rx: 58, ry: 70, rot: 20, o: 0.34 },
    { cx: 268, cy: 342, rx: 52, ry: 44, rot: -22, o: 0.3 },
  ];

  const dust = [
    { x: 24, y: 128, r: 0.7 }, { x: 276, y: 104, r: 0.6 }, { x: 168, y: 132, r: 0.55 },
    { x: 96, y: 122, r: 0.65 }, { x: 292, y: 152, r: 0.5 }, { x: 12, y: 96, r: 0.6 },
    { x: 286, y: 372, r: 0.55 }, { x: 14, y: 366, r: 0.5 },
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <defs>
        <radialGradient id="sl-halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.62" />
          <stop offset="25%" stopColor={color} stopOpacity="0.46" />
          <stop offset="33%" stopColor={color} stopOpacity="0.24" />
          <stop offset="43%" stopColor={color} stopOpacity="0.14" />
          <stop offset="58%" stopColor={color} stopOpacity="0.085" />
          <stop offset="75%" stopColor={color} stopOpacity="0.04" />
          <stop offset="92%" stopColor={color} stopOpacity="0.014" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sl-gold" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={accent} stopOpacity="0.34" />
          <stop offset="26%" stopColor={accent} stopOpacity="0.2" />
          <stop offset="46%" stopColor={accent} stopOpacity="0.085" />
          <stop offset="70%" stopColor={accent} stopOpacity="0.03" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sl-pool" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={accent} stopOpacity="0.085" />
          <stop offset="45%" stopColor={accent} stopOpacity="0.04" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sl-smudge" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color2} stopOpacity="0.075" />
          <stop offset="55%" stopColor={color2} stopOpacity="0.03" />
          <stop offset="100%" stopColor={color2} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sl-lift" cx="50%" cy="32%" r="62%">
          <stop offset="0%" stopColor={color} stopOpacity="0.05" />
          <stop offset="60%" stopColor={color} stopOpacity="0.018" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sl-vig" cx="50%" cy="40%" r="72%">
          <stop offset="0%" stopColor="#000000" stopOpacity="0" />
          <stop offset="52%" stopColor="#000000" stopOpacity="0.08" />
          <stop offset="78%" stopColor="#000000" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.4" />
        </radialGradient>
        <linearGradient id="sl-floor" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#000000" stopOpacity="0" />
          <stop offset="62%" stopColor="#000000" stopOpacity="0.04" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.22" />
        </linearGradient>
        <linearGradient id="sl-cord-a" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={color} stopOpacity="0.12" />
          <stop offset="14%" stopColor={color} stopOpacity="0.34" />
          <stop offset="31%" stopColor={color} stopOpacity="0.17" />
          <stop offset="48%" stopColor={color} stopOpacity="0.44" />
          <stop offset="66%" stopColor={color} stopOpacity="0.23" />
          <stop offset="84%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0.14" />
        </linearGradient>
        <linearGradient id="sl-cord-b" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="20%" stopColor={color} stopOpacity="0.15" />
          <stop offset="38%" stopColor={color} stopOpacity="0.38" />
          <stop offset="57%" stopColor={color} stopOpacity="0.2" />
          <stop offset="76%" stopColor={color} stopOpacity="0.42" />
          <stop offset="100%" stopColor={color} stopOpacity="0.18" />
        </linearGradient>
        <linearGradient id="sl-cord-c" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="26%" stopColor={color} stopOpacity="0.42" />
          <stop offset="44%" stopColor={color} stopOpacity="0.19" />
          <stop offset="62%" stopColor={color} stopOpacity="0.36" />
          <stop offset="82%" stopColor={color} stopOpacity="0.16" />
          <stop offset="100%" stopColor={color} stopOpacity="0.3" />
        </linearGradient>
        <linearGradient id="sl-cord-d" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={color} stopOpacity="0.08" />
          <stop offset="30%" stopColor={color} stopOpacity="0.2" />
          <stop offset="55%" stopColor={color} stopOpacity="0.1" />
          <stop offset="78%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0.09" />
        </linearGradient>
        <filter id="sl-grain" x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.92" numOctaves="3" seed="7" stitchTiles="stitch" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.58  0 0 0 0 0.55  0 0 0 0 0.54  0 0 0 0.4 0"
          />
        </filter>
      </defs>

      {/* chalkboard body: mottling, vignette, grain */}
      <rect x="0" y="0" width="300" height="400" fill="url(#sl-lift)" />
      {smudges.map((s, i) => (
        <ellipse
          key={`sm${i}`}
          cx={s.cx}
          cy={s.cy}
          rx={s.rx}
          ry={s.ry}
          fill="url(#sl-smudge)"
          opacity={s.o}
          transform={`rotate(${s.rot} ${s.cx} ${s.cy})`}
        />
      ))}
      <ellipse cx="150" cy="46" rx="190" ry="120" fill="url(#sl-pool)" />
      <rect x="0" y="0" width="300" height="400" fill="url(#sl-vig)" />
      <rect x="0" y="0" width="300" height="400" fill="url(#sl-floor)" />
      <rect x="0" y="0" width="300" height="400" filter="url(#sl-grain)" opacity="0.13" />

      {/* cords */}
      {STRANDS.map((s, i) => (
        <path
          key={`c${i}`}
          d={path(s)}
          fill="none"
          stroke={`url(#sl-cord-${s.cord})`}
          strokeWidth={s.w}
          strokeLinecap="round"
        />
      ))}

      {/* sockets: each bulb hangs from the cord on a short gold stem */}
      {bulbs.map((b) => (
        <line
          key={`st${b.k}`}
          x1={b.tx}
          y1={b.ty - 0.35}
          x2={b.x}
          y2={b.y - b.r * 0.5}
          stroke={hexA(accent, 0.42 * b.o)}
          strokeWidth={Math.max(0.5, b.r * 0.34)}
          strokeLinecap="round"
        />
      ))}

      {/* halos: gold outer, warm inner, multi-stop falloff */}
      {bulbs.map((b) => (
        <circle
          key={`g${b.k}`}
          cx={b.x}
          cy={b.y}
          r={b.r * 5.6}
          fill="url(#sl-gold)"
          opacity={b.o * 0.9}
        />
      ))}
      {bulbs.map((b) => (
        <circle key={`h${b.k}`} cx={b.x} cy={b.y} r={b.r * 4} fill="url(#sl-halo)" opacity={b.o} />
      ))}

      {/* cores */}
      {bulbs.map((b) => (
        <circle
          key={`b${b.k}`}
          cx={b.x}
          cy={b.y}
          r={b.r}
          fill={b.gold ? hexA(accent, 0.9 * b.o) : hexA(color, 0.94 * b.o)}
        />
      ))}
      {bulbs.map((b) => (
        <circle
          key={`w${b.k}`}
          cx={b.x - b.r * 0.16}
          cy={b.y - b.r * 0.2}
          r={b.r * 0.55}
          fill={hexA('#FFFFFF', 0.75 * b.o)}
        />
      ))}

      {/* a few glints */}
      {bulbs
        .filter((b) => b.spark)
        .map((b) => (
          <g key={`sp${b.k}`} opacity={0.4 * b.o}>
            <line
              x1={b.x - b.r * 3.4}
              y1={b.y}
              x2={b.x + b.r * 3.4}
              y2={b.y}
              stroke={hexA('#FFFFFF', 0.5)}
              strokeWidth="0.5"
              strokeLinecap="round"
            />
            <line
              x1={b.x}
              y1={b.y - b.r * 2.5}
              x2={b.x}
              y2={b.y + b.r * 2.5}
              stroke={hexA('#FFFFFF', 0.42)}
              strokeWidth="0.5"
              strokeLinecap="round"
            />
          </g>
        ))}

      {/* stray chalk dust / distant sparks */}
      {dust.map((d, i) => (
        <g key={`d${i}`}>
          <circle cx={d.x} cy={d.y} r={d.r * 4.5} fill="url(#sl-halo)" opacity="0.4" />
          <circle cx={d.x} cy={d.y} r={d.r} fill={hexA(color, 0.5)} />
        </g>
      ))}
    </svg>
  );
}

// ─── CountryClubParTee — country-club
function CountryClubParTee({ color, color2, accent, scale = 300 }) {
  const forest = color;
  const green = color2;
  const red = accent;
  const cream = '#F6F1E3';
  const ochre = '#E3B84A';
  const sand = '#F3E7D2';
  const sage = hexA(color2, 0.45);
  const dimpleC = hexA(color2, 0.28);
  const shade = hexA(color, 0.1);

  const PITCH = 16.5, HW = 8.25, HH = 15, DW = 7.3, DH = 13.3;
  const dia = (cx, cy) =>
    `M${cx} ${cy - DH}L${cx + DW} ${cy}L${cx} ${cy + DH}L${cx - DW} ${cy}Z`;
  const band = (y0, phase) => {
    let a = '', b = '', h = '';
    for (let i = -2; i < 20; i++) {
      const cx = phase + i * PITCH + HW;
      const ox = phase + i * PITCH;
      if (i % 2 === 0) {
        a += dia(cx, y0 + HH);
        b += dia(ox, y0) + dia(ox, y0 + 2 * HH);
      } else {
        b += dia(cx, y0 + HH);
        a += dia(ox, y0) + dia(ox, y0 + 2 * HH);
      }
    }
    for (let k = -2; k < 22; k++) {
      const x = phase + k * PITCH;
      h += `M${x - HW} ${y0 + 45}L${x + 3 * HW} ${y0 - 15}M${x + HW} ${y0 + 45}L${x - 3 * HW} ${y0 - 15}`;
    }
    return { a, b, h };
  };
  const TB = band(0, 0);
  const BB = band(370, HW);

  const FR_A = 'M143 33L22 33L13 42L13 358L22 367L143 367M157 33L278 33L287 42L287 358L278 367L157 367';
  const FR_B = 'M143 36.9L23.6 36.9L16.9 43.6L16.9 356.4L23.6 363.1L143 363.1M157 36.9L276.4 36.9L283.1 43.6L283.1 356.4L276.4 363.1L157 363.1';
  const MOUND_A = 'M-10 372L-10 310C45 292,105 284,155 288C215 293,270 307,310 311L310 372Z';
  const MOUND_B = 'M-10 372L-10 336C40 321,95 313,150 316C215 320,275 331,310 335L310 372Z';
  const GLASS = 'M191 282L194.2 322Q194.5 326 198 326L230 326Q233.5 326 233.8 322L237 282';
  const TEE = 'M-2.3 -5.6L2.3 -5.6L1.45 -3.6L0.62 -3.1L0.5 3.6L0 5.6L-0.5 3.6L-0.62 -3.1L-1.45 -3.6Z';

  const blade = (x, y, hh, d) =>
    `M${x - 0.55} ${y}C${x + d * 0.7} ${y - hh * 0.45},${x + d * 1.7} ${y - hh * 0.75},${x + d * 3.1} ${y - hh}C${x + d * 1.1} ${y - hh * 0.7},${x + d * 0.45} ${y - hh * 0.4},${x + 0.55} ${y}Z`;
  const tufts = [[104, 304, 7.5], [187, 325, 6.4], [243, 327, 6.8]];
  const dimples = [[-10, 2], [-6, 6], [-11, 7], [-3, 1], [-7, -2], [-2, 8], [-8, 11], [-13, -1], [2, 5], [1, 11], [6, 8], [5, 0], [9, 4], [10, -2], [4, -5], [0, -8], [8, -7], [-3, -10]];
  const loose = [[45, 330, 5], [271, 349, 4.5], [152, 353, 5.3]];
  const lp = [[-1.7, 0.4], [0.7, 1.5], [-0.5, -1.7], [1.9, -0.5], [0.3, 2.6]];
  const sc = [[33, 64, 0], [264, 80, 1], [45, 112, 2], [255, 136, 0], [29, 170, 1], [268, 196, 2], [41, 226, 0], [259, 244, 1], [36, 290, 2]];
  const seg = [144, 108, 72, 36]
    .map((a) => `M234 272L${(234 + 8 * Math.cos((a * Math.PI) / 180)).toFixed(2)} ${(272 - 8 * Math.sin((a * Math.PI) / 180)).toFixed(2)}`)
    .join('');

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <clipPath id="ccBandT"><rect x="-30" y="0" width="360" height="30" /></clipPath>
        <clipPath id="ccBandB"><rect x="-30" y="370" width="360" height="30" /></clipPath>
        <clipPath id="ccMound"><path d={MOUND_A} /></clipPath>
        <clipPath id="ccGlass"><path d={GLASS + 'Z'} /></clipPath>
        <filter id="ccGrain" x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="3" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
      </defs>

      {/* ---- knitwear argyle bands ---- */}
      <g clipPath="url(#ccBandT)">
        <rect x="-30" y="0" width="360" height="30" fill={sage} />
        <path d={TB.a} fill={forest} />
        <path d={TB.b} fill={green} />
        <path d={TB.h} stroke={cream} strokeWidth="0.45" opacity="0.6" fill="none" />
      </g>
      <g clipPath="url(#ccBandB)">
        <rect x="-30" y="370" width="360" height="30" fill={sage} />
        <path d={BB.a} fill={forest} />
        <path d={BB.b} fill={green} />
        <path d={BB.h} stroke={cream} strokeWidth="0.45" opacity="0.6" fill="none" />
      </g>

      {/* ---- chamfered double-rule frame ---- */}
      <g stroke={forest} fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d={FR_A} strokeWidth="0.8" />
        <path d={FR_B} strokeWidth="0.3" opacity="0.8" />
      </g>
      <path d="M150 28.6L153.7 34.9L150 41.2L146.3 34.9Z" fill={forest} />

      {/* ---- whisper-weight motif scatter, gutters only ---- */}
      <g opacity="0.07" fill={forest}>
        {sc.map((s, i) =>
          s[2] === 0 ? (
            <circle key={`s${i}`} cx={s[0]} cy={s[1]} r="3.2" />
          ) : s[2] === 1 ? (
            <path key={`s${i}`} d={`M${s[0]} ${s[1] - 4.6}L${s[0] + 2.7} ${s[1]}L${s[0]} ${s[1] + 4.6}L${s[0] - 2.7} ${s[1]}Z`} />
          ) : (
            <g key={`s${i}`} transform={`translate(${s[0]} ${s[1]}) rotate(${i % 2 ? 18 : -14})`}>
              <path d={TEE} />
            </g>
          )
        )}
      </g>

      {/* ---- two-tone greens, fill only ---- */}
      <path d={MOUND_A} fill={sage} />
      <path d="M-10 310C45 292,105 284,155 288C215 293,270 307,310 311L310 318.5C270 314.5,215 300.5,155 295.5C105 291.5,45 299.5,-10 317.5Z" fill={shade} />
      <path d="M236 303C258 305,282 308,306 310" stroke={cream} strokeWidth="0.9" opacity="0.5" fill="none" strokeLinecap="round" />
      <path d={MOUND_B} fill={green} />
      <path d="M-10 336C40 321,95 313,150 316C215 320,275 331,310 335L310 342C275 338,215 327,150 323C95 320,40 328,-10 343Z" fill={shade} />

      {/* frame repeats in cream where it crosses the greens */}
      <g clipPath="url(#ccMound)" stroke={cream} fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.92">
        <path d={FR_A} strokeWidth="0.8" />
        <path d={FR_B} strokeWidth="0.3" />
      </g>

      {/* ---- ground furniture ---- */}
      <path d="M224 336C230 330,244 328,252 331C260 334,264 339,257 342C250 345,238 343,232 343C225 343,219 341,224 336Z" fill={sand} />
      <g fill={cream} stroke={forest} strokeWidth="0.9">
        {loose.map((b, i) => <circle key={`b${i}`} cx={b[0]} cy={b[1]} r={b[2]} />)}
      </g>
      <g fill={dimpleC}>
        {loose.map((b, i) =>
          lp.map((p, j) => (
            <circle key={`b${i}d${j}`} cx={b[0] + (p[0] * b[2]) / 5} cy={b[1] + (p[1] * b[2]) / 5} r={(0.72 * b[2]) / 5} />
          ))
        )}
      </g>
      <g transform="translate(58 340) rotate(-18)"><path d={TEE} fill={forest} /></g>
      <g transform="translate(263 316) rotate(22)"><path d={TEE} fill={ochre} /></g>
      <g fill={forest} opacity="0.9">
        {tufts.map((t, i) =>
          [[-1, t[2] * 0.82], [0.28, t[2]], [1, t[2] * 0.88]].map((d, j) => (
            <path key={`t${i}-${j}`} d={blade(t[0] + d[0] * 1.3, t[1], d[1], d[0] === 0.28 ? 0.5 : d[0])} />
          ))
        )}
      </g>

      {/* ---- the pin ---- */}
      <ellipse cx="96" cy="306" rx="5.5" ry="2" fill={forest} />
      <rect x="94.9" y="250" width="2.2" height="56" fill={cream} stroke={forest} strokeWidth="0.55" />
      <g fill={forest}>
        <rect x="94.9" y="261" width="2.2" height="4.6" />
        <rect x="94.9" y="273" width="2.2" height="4.6" />
        <rect x="94.9" y="285" width="2.2" height="4.6" />
      </g>
      <path d="M96.4 250L138 253L130.5 260L138 267L96.4 270Z" fill={red} />
      <path d="M99.7 253.4C101 257,101 263,99.7 266.7C98.9 263,98.9 257,99.7 253.4Z" fill={cream} opacity="0.9" />

      {/* ---- ball in the lowball glass ---- */}
      <ellipse cx="214" cy="327.5" rx="25" ry="3" fill={hexA(color, 0.16)} />
      <path d="M221.2 266.8L229.4 287.4" stroke={forest} strokeWidth="1.25" strokeLinecap="round" />
      <circle cx="220.5" cy="265.4" r="2.7" fill={red} />
      <g clipPath="url(#ccGlass)">
        <rect x="188" y="278" width="52" height="50" fill="#F2F8EF" opacity="0.55" />
        <rect x="188" y="301" width="52" height="27" fill={ochre} />
        <ellipse cx="214" cy="301" rx="20.6" ry="4.2" fill={ochre} />
        <g stroke={forest} strokeWidth="1.25" fill="none">
          <g transform="translate(201.5 314.5) rotate(12)"><rect x="-4" y="-4" width="8" height="8" rx="2" /></g>
          <g transform="translate(226.5 317) rotate(-20)"><rect x="-4" y="-4" width="8" height="8" rx="2" /></g>
        </g>
        <rect x="197.4" y="278" width="3.8" height="50" fill={cream} opacity="0.55" />
      </g>
      <ellipse cx="214" cy="282" rx="23" ry="5" fill="none" stroke={forest} strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="214" cy="293" r="16" fill={cream} stroke={forest} strokeWidth="1.5" />
      <g fill={dimpleC}>
        {dimples.map((d, i) => (
          <circle key={`d${i}`} cx={214 + d[0] * 0.86} cy={293 + d[1] * 0.86} r="1.3" />
        ))}
      </g>
      <path d="M191 282A23 5 0 0 0 237 282" fill="none" stroke={forest} strokeWidth="1.75" strokeLinecap="round" />
      <path d={GLASS} fill="none" stroke={forest} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <g transform="rotate(-12 234 272)">
        <path d="M226 272A8 8 0 0 1 242 272Z" fill={ochre} />
        <path d={seg} stroke={cream} strokeWidth="0.8" fill="none" strokeLinecap="round" />
        <path d="M226 272A8 8 0 0 1 242 272" fill="none" stroke={forest} strokeWidth="1.25" strokeLinecap="round" />
      </g>

      <path d="M150 358.8L153.7 365.1L150 371.4L146.3 365.1Z" fill={cream} opacity="0.92" />
      <rect x="0" y="0" width="300" height="400" filter="url(#ccGrain)" opacity="0.05" style={{ mixBlendMode: 'multiply' }} />
    </svg>
  );
}

// ─── NauticalBowOnWater — nautical-boat
function NauticalBowOnWater({ color, color2, accent, scale = 300 }) {
  const CX = 149;
  const APEX = 228;
  const DEEP = '#0044A9';
  const LITE = '#0082CF';
  const FOAM = '#CDE5FF';
  const CREAM = '#EFE5D3';
  const CREAM_IN = '#E3D4C0';
  const RAILC = '#E8E2D2';
  const MAH = '#6F4720';
  const MAH_LT = '#8B5A2E';
  const POSTHI = '#B77546';
  const DECK = '#D8A788';
  const DECK_D = '#BE8360';
  const DECK_L = '#E0A786';
  const DECK_S = '#CC916E';
  const GLARE = '#BFD2D5';

  const f1 = (n) => Number(n.toFixed(1));
  const curve = (p) => {
    let d = `M ${f1(p[0][0])} ${f1(p[0][1])}`;
    for (let i = 0; i < p.length - 1; i++) {
      const p0 = p[i - 1] || p[i];
      const p1 = p[i];
      const p2 = p[i + 1];
      const p3 = p[i + 2] || p2;
      d += ` C ${f1(p1[0] + (p2[0] - p0[0]) / 6)} ${f1(p1[1] + (p2[1] - p0[1]) / 6)},` +
        ` ${f1(p2[0] - (p3[0] - p1[0]) / 6)} ${f1(p2[1] - (p3[1] - p1[1]) / 6)},` +
        ` ${f1(p2[0])} ${f1(p2[1])}`;
    }
    return d;
  };

  // hull edge: ogive (half-width ~ 124*(dy/172)^0.72), hand-nudged off the perfect curve
  const EDGE = [[149, 228], [137.4, 234], [126.5, 242], [117, 252], [105, 266], [93.6, 282],
    [81.4, 300], [69.4, 320], [56.4, 342], [43.9, 366], [31.3, 388], [25, 400], [18.8, 412]];
  const mir = (p) => [298 - p[0], p[1]];
  const inset = (pts, s) => pts.map((p, i) => {
    if (i === 0) return [CX, APEX + s * 1.25];
    const a = pts[i - 1];
    const b = pts[Math.min(pts.length - 1, i + 1)];
    let tx = b[0] - a[0];
    let ty = b[1] - a[1];
    const L = Math.hypot(tx, ty) || 1;
    tx /= L;
    ty /= L;
    return [p[0] + s * ty, p[1] - s * tx];
  });
  const hull = (s) => {
    const l = inset(EDGE, s);
    const r = l.map(mir).slice().reverse();
    return `${curve(l)} ${curve(r).replace('M', 'L')} Z`;
  };

  // painted water: broad swathes, ragged inner chunks, dry-brush bristle clusters
  const SW = [
    { a: [322, -46], b: [-20, 246], w: 100, c: DEEP, o: 0.34, wide: true, cl: 4 },
    { a: [338, 24], b: [86, 272], w: 70, c: LITE, o: 0.3, wide: true, cl: 3 },
    { a: [334, 146], b: [38, 440], w: 108, c: DEEP, o: 0.3, wide: true, cl: 4 },
    { a: [300, -74], b: [168, 58], w: 54, c: LITE, o: 0.26, wide: false, cl: 3 },
    { a: [128, -58], b: [-46, 120], w: 60, c: DEEP, o: 0.24, wide: false, cl: 3 },
    { a: [132, 292], b: [10, 414], w: 50, c: LITE, o: 0.22, wide: false, cl: 2 },
    { a: [318, 330], b: [236, 412], w: 44, c: DEEP, o: 0.26, wide: false, cl: 1 }
  ];
  const wideWash = [];
  const softWash = [];
  const chunks = [];
  const bristles = [];
  SW.forEach((s, i) => {
    const dx = s.b[0] - s.a[0];
    const dy = s.b[1] - s.a[1];
    const L = Math.hypot(dx, dy);
    const ux = dx / L;
    const uy = dy / L;
    const nx = -uy;
    const ny = ux;
    const w1 = ((i * 31) % 9) - 4;
    const w2 = ((i * 47) % 11) - 5;
    (s.wide ? wideWash : softWash).push(
      <path key={`w${i}`} fill="none" strokeLinecap="round" strokeWidth={s.w}
        stroke={hexA(s.c, s.o)}
        d={curve([s.a,
          [s.a[0] + ux * L * 0.33 + nx * w1, s.a[1] + uy * L * 0.33 + ny * w1],
          [s.a[0] + ux * L * 0.68 + nx * w2, s.a[1] + uy * L * 0.68 + ny * w2], s.b])} />
    );
    for (let k = 0; k < 3; k++) {
      const q = 0.16 + k * 0.26;
      const q2 = q + 0.3 + ((k * 13) % 7) / 34;
      const lat = s.w * 0.5 * (k % 2 ? 1 : -1) * (0.55 + ((k * 7) % 5) / 12);
      chunks.push(
        <path key={`c${i}-${k}`} fill="none" strokeLinecap="round"
          strokeWidth={f1(s.w * 0.34)} stroke={hexA(s.c, s.o * 0.7)}
          d={curve([[s.a[0] + ux * L * q + nx * lat, s.a[1] + uy * L * q + ny * lat],
            [s.a[0] + ux * L * q2 + nx * lat * 0.72, s.a[1] + uy * L * q2 + ny * lat * 0.72]])} />
      );
    }
    for (let c = 0; c < s.cl; c++) {
      const h = 0.12 + ((c * 41 + i * 23) % 70) / 100;
      const base = (((c + i) % 2) ? 1 : -1) * s.w * (0.3 + ((c * 17) % 5) / 16);
      const light = (c + i) % 3 === 0;
      const bx = s.a[0] + ux * L * h + nx * base;
      const by = s.a[1] + uy * L * h + ny * base;
      const calm = by > 84 && by < 236 ? 0.5 : 1;
      for (let j = 0; j < 3; j++) {
        const sp = (j - 1) * (1.9 + ((j * 13 + c) % 4) * 0.6);
        const len = 16 + ((j * 29 + c * 13 + i * 7) % 30);
        const st = ((j * 11 + c * 7) % 9) - 4;
        const x0 = bx + nx * sp + ux * st;
        const y0 = by + ny * sp + uy * st;
        bristles.push(
          <line key={`b${i}-${c}-${j}`} x1={f1(x0)} y1={f1(y0)}
            x2={f1(x0 + ux * len)} y2={f1(y0 + uy * len)} strokeLinecap="round"
            strokeWidth={f1(0.5 + ((j * 7 + c) % 5) / 5)}
            stroke={hexA(light ? LITE : DEEP, (light ? 0.26 : 0.22) * calm)} />
        );
      }
    }
  });

  // deck planking (coarser at thumbnail size, where hairlines go sub-pixel)
  const pitch = scale < 200 ? 8.2 : 6.15;
  const planks = [];
  for (let i = 0; i * pitch < 246; i++) {
    const xb = 27 + i * pitch + ((i * 13) % 5) * 0.55;
    planks.push(
      <line key={`p${i}`} x1={f1(xb + (CX - xb) * 0.12)} y1="242" x2={f1(xb)} y2="402"
        strokeWidth={f1(0.75 + ((i * 17) % 3) * 0.11)}
        stroke={hexA(DECK_D, 0.4 + ((i * 11) % 4) * 0.07)} />
    );
  }

  // bow pulpit + lifeline, left side (mirrored for the right)
  const UP = [[138.6, 238.4], [133.5, 227.1], [133, 218.4], [131, 210.4], [127.5, 205.2],
    [123, 203.9], [120, 206.9], [118, 211.8], [112, 217.5], [101, 224.5], [83.5, 238.5],
    [63, 259.6], [46.5, 280.7], [25.5, 315.8], [14.5, 336.9], [5.5, 364.9], [0.5, 384], [-7, 401]];
  const LO = [[125, 231.5], [114.5, 238.5], [95, 252.6], [80, 266.7], [67, 280.7], [56.5, 294.7],
    [42.5, 315.8], [35, 329.9], [23.5, 358], [17.5, 379.1], [15.5, 393.1], [15, 405.4]];
  const rigging = [];
  [false, true].forEach((m, mi) => {
    [[UP, 0.96, 2], [UP.slice(0, 11), 0.9, 2.7], [UP.slice(13), 0.78, 1.45],
      [LO, 0.93, 1.9], [LO.slice(5), 0.8, 1.35]].forEach((g, gi) => rigging.push(
      <path key={`r${mi}-${gi}`} fill="none" strokeLinecap="round" strokeWidth={g[2]}
        stroke={hexA(RAILC, g[1])} d={curve(m ? g[0].map(mir) : g[0])} />
    ));
  });
  const XB = [[52.7, 272.8, 58.5, 292.1], [74.3, 272.8, 39.7, 292.1],
    [5.9, 363.7, 18.3, 376.4], [21.9, 363.7, 3.05, 376.4]];
  [false, true].forEach((m, mi) => {
    XB.forEach((b, i) => {
      const p1 = m ? mir([b[0], b[1]]) : [b[0], b[1]];
      const p2 = m ? mir([b[2], b[3]]) : [b[2], b[3]];
      rigging.push(
        <line key={`x${mi}-${i}`} x1={f1(p1[0])} y1={p1[1]} x2={f1(p2[0])} y2={p2[1]}
          strokeLinecap="round" strokeWidth={i < 2 ? 1.5 : 1.3} stroke={hexA(RAILC, 0.85)} />
      );
    });
  });

  const POST = 'M 149 190.2 C 145.9 190.4, 145.1 192.6, 145.2 195.4 C 145.5 203, 145 211, 144.3 217.5' +
    ' C 143.8 222.5, 141.9 225.6, 139.3 229.4 L 146.3 231.3 C 147.2 227.4, 148.2 225, 149 224.3' +
    ' C 149.8 225, 150.8 227.4, 151.7 231.3 L 158.7 229.4 C 156.1 225.6, 154.2 222.5, 153.7 217.5' +
    ' C 153 211, 152.5 203, 152.8 195.4 C 152.9 192.6, 152.1 190.4, 149 190.2 Z';

  return (
    <svg viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice" aria-hidden
      className="absolute inset-0 w-full h-full pointer-events-none">
      <defs>
        <filter id="nbw-a" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="7.5" />
        </filter>
        <filter id="nbw-b" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
        <filter id="nbw-c" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.6" />
        </filter>
        <filter id="nbw-d" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="0.8" />
        </filter>
        <radialGradient id="nbw-halo" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor={hexA(DEEP, 0.26)} />
          <stop offset="0.6" stopColor={hexA(DEEP, 0.11)} />
          <stop offset="1" stopColor={hexA(DEEP, 0)} />
        </radialGradient>
        <linearGradient id="nbw-mah" x1="0" y1="0" x2="1" y2="0.35">
          <stop offset="0" stopColor={MAH} />
          <stop offset="0.5" stopColor={MAH_LT} />
          <stop offset="1" stopColor={MAH} />
        </linearGradient>
        <linearGradient id="nbw-grain" x1="20" y1="0" x2="280" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={hexA(DECK_S, 0.3)} />
          <stop offset="0.18" stopColor={hexA(DECK_S, 0.26)} />
          <stop offset="0.32" stopColor={hexA(DECK_L, 0.34)} />
          <stop offset="0.46" stopColor={hexA(DECK_L, 0.06)} />
          <stop offset="0.62" stopColor={hexA(DECK_S, 0.24)} />
          <stop offset="0.82" stopColor={hexA(DECK_L, 0.3)} />
          <stop offset="1" stopColor={hexA(DECK_S, 0.22)} />
        </linearGradient>
        <clipPath id="nbw-deck"><path d={hull(16)} /></clipPath>
      </defs>

      {/* water: flat field, swathes, bristle drag, then a soft deep halo behind the type */}
      <rect x="0" y="0" width="300" height="400" fill={color} />
      <g filter="url(#nbw-a)">{wideWash}</g>
      <g filter="url(#nbw-b)">{softWash}</g>
      <g filter="url(#nbw-c)">{chunks}</g>
      <ellipse cx="268" cy="46" rx="88" ry="70" fill={hexA(color2, 0.1)} filter="url(#nbw-b)" />
      <g filter="url(#nbw-d)">{bristles}</g>
      <g filter="url(#nbw-d)">
        <line x1="146" y1="42" x2="106" y2="94" strokeWidth="1.4" strokeLinecap="round" stroke={hexA(FOAM, 0.15)} />
        <line x1="104" y1="20" x2="68" y2="60" strokeWidth="1.1" strokeLinecap="round" stroke={hexA(FOAM, 0.13)} />
        <line x1="78" y1="130" x2="54" y2="164" strokeWidth="0.8" strokeLinecap="round" stroke={hexA(accent, 0.08)} />
      </g>
      <ellipse cx="150" cy="150" rx="170" ry="145" fill="url(#nbw-halo)" />

      {/* stem post, under the hull so the rubrails become the arms of its Y */}
      <path d={POST} fill={MAH} />
      <path d="M 146.6 193.5 C 146.9 202, 146.3 211, 145.6 217.5" fill="none"
        stroke={POSTHI} strokeWidth="1.5" strokeLinecap="round" opacity="0.8" />

      {/* hull: abutting fills, no outlines — rubrail, covering board, deck */}
      <path d={hull(0)} fill={MAH} />
      <path d={hull(3)} fill="url(#nbw-mah)" />
      <path d={hull(6.5)} fill={CREAM} />
      <path d={hull(11.5)} fill={CREAM_IN} />
      <path d={hull(16)} fill={DECK} />
      <g clipPath="url(#nbw-deck)">
        <rect x="20" y="232" width="260" height="172" fill="url(#nbw-grain)" />
        {planks}
        <g filter="url(#nbw-c)">
          <ellipse cx="167" cy="285" rx="23" ry="11" transform="rotate(-16 167 285)" fill={hexA(GLARE, 0.3)} />
          <ellipse cx="157" cy="264" rx="8" ry="5" fill={hexA(GLARE, 0.22)} />
        </g>
      </g>

      {/* the one hot mark on the card */}
      <circle cx="160" cy="244.7" r="3.4" fill={hexA(accent, 0.28)} filter="url(#nbw-c)" />
      <circle cx="160" cy="244.7" r="1.7" fill={accent} />

      {rigging}
    </svg>
  );
}

// ─── CitySkylineLineArt — city-skyline
function CitySkylineLineArt({ color, color2, accent, scale = 300 }) {
  const G = 346;
  const k = scale < 200 ? 1.22 : 1;
  const wFront = 1 * k;
  const wMid = 0.85 * k;
  const wBack = 0.6 * k;
  const wFine = 0.55 * k;
  const wGround = 1.45 * k;
  const n2 = (v) => Math.round(v * 100) / 100;
  /* ids stay unique when several palettes are rendered on one page */
  const uid = 'csk' + String('' + color + color2 + accent).replace(/[^a-zA-Z0-9]/g, '') + Math.round(scale);

  /* ── quadratic helpers (deterministic palm geometry) ───────────────── */
  const qP = (a, c, b, t) => {
    const u = 1 - t;
    return [u * u * a[0] + 2 * u * t * c[0] + t * t * b[0], u * u * a[1] + 2 * u * t * c[1] + t * t * b[1]];
  };
  const qD = (a, c, b, t) => {
    const u = 1 - t;
    return [2 * u * (c[0] - a[0]) + 2 * t * (b[0] - c[0]), 2 * u * (c[1] - a[1]) + 2 * t * (b[1] - c[1])];
  };
  const rot = (x, y, deg) => {
    const r = (deg * Math.PI) / 180;
    return [x * Math.cos(r) - y * Math.sin(r), x * Math.sin(r) + y * Math.cos(r)];
  };
  /* one frond = one arching stroke + 5 short barbs, never a filled leaf shape.
     sh = [tipX, tipY, ctrlX, ctrlY] in units of len, spun per palm. */
  const frond = (cx, cy, sh, len, spin) => {
    const tp = rot(sh[0], sh[1], spin);
    const cp = rot(sh[2], sh[3], spin);
    const a = [cx, cy];
    const b = [cx + tp[0] * len, cy + tp[1] * len];
    const c = [cx + cp[0] * len, cy + cp[1] * len];
    let d = 'M' + n2(a[0]) + ' ' + n2(a[1]) + ' Q' + n2(c[0]) + ' ' + n2(c[1]) + ' ' + n2(b[0]) + ' ' + n2(b[1]);
    const ts = [0.22, 0.38, 0.54, 0.7, 0.85];
    for (let i = 0; i < ts.length; i++) {
      const t = ts[i];
      const p = qP(a, c, b, t);
      const tv = qD(a, c, b, t);
      const m = Math.sqrt(tv[0] * tv[0] + tv[1] * tv[1]) || 1;
      const ux = tv[0] / m;
      const uy = tv[1] / m;
      /* barbs alternate sides, longest at the belly of the frond */
      const s = i % 2 === 0 ? 1 : -1;
      const lr = rot(ux, uy, (70 - 34 * t) * s);
      const gx = lr[0];
      const gy = lr[1] + 0.24;
      const gm = Math.sqrt(gx * gx + gy * gy) || 1;
      const bl = len * 0.19 * Math.sin(Math.PI * Math.pow(t, 0.8)) * (s > 0 ? 1.1 : 0.86);
      d += ' M' + n2(p[0]) + ' ' + n2(p[1]) + ' L' + n2(p[0] + (gx / gm) * bl) + ' ' + n2(p[1] + (gy / gm) * bl);
    }
    return d;
  };

  /* ── star field: 38 dots, loosely scattered, clear of the centre band ─ */
  const hash = (i, s) => {
    const v = Math.sin(i * 127.1 + s * 311.7) * 43758.5453;
    return v - Math.floor(v);
  };
  const stars = [];
  for (let i = 0; i < 38; i++) {
    const rx = hash(i, 1);
    const ry = hash(i, 2);
    const edge = i % 6 === 5;
    const x = edge ? (rx < 0.5 ? 18 + rx * 62 : 236 + (rx - 0.5) * 98) : 16 + rx * 270;
    const y = edge ? 118 + ry * 98 : 18 + ry * 100;
    /* clear a halo around the moon */
    const dm = Math.sqrt((x - 54) * (x - 54) + (y - 46) * (y - 46));
    stars.push({
      x: n2(x),
      y: n2(dm < 17 ? y + 20 : y),
      r: n2(0.26 + hash(i, 3) * 0.3),
      o: n2(0.22 + hash(i, 4) * 0.44),
    });
  }

  /* ── BACK PLANE — tops only, faint hairline ────────────────────────── */
  const backD = [
    'M4 346 V295 H30 V346',
    'M40 346 V292 H52 V284 H68 V346',
    'M98 346 V286 H124 V346',
    'M144 346 V294 H170 V346',
    'M148 294 V282',
    'M212 346 V288 H238 V346',
    'M256 346 V298 H290 V346',
  ];

  /* ── MID PLANE ─────────────────────────────────────────────────────── */
  const midD = [
    'M16 346 V301 H44 V346',
    'M56 346 V296 H68 V288 H86 V346',
    'M110 346 V284 H134 V346',
    'M120 284 V273',
    'M166 346 V290 H188 V346',
    'M232 346 V300 H258 V346',
    'M278 346 V304 H302 V346',
  ];

  /* ── FRONT PLANE ───────────────────────────────────────────────────── */
  const bridgeCable = 'M-6 324 Q1 301 12 292 Q36 332 59 292 Q70 308 84 324';
  const hangers = [
    [16.8, 299.2], [21.6, 304.8], [26.3, 308.8], [31, 311.2], [35.8, 312],
    [40.4, 311.2], [45.1, 308.8], [49.8, 304.8], [54.4, 299.2],
  ];
  const hangerD = hangers.map((h) => 'M' + h[0] + ' ' + h[1] + ' V328');

  const frontD = [
    /* suspension bridge, bleeding off the left trim */
    'M-6 328 H84',
    'M-6 331.3 H84',
    'M8 328 V292 M16 328 V292 M6.6 302 H17.4 M6.6 295.4 H17.4',
    'M55 328 V292 M63 328 V292 M53.6 302 H64.4 M53.6 295.4 H64.4',
    bridgeCable,
    /* domed hall */
    'M86 346 V306 A16 13 0 0 1 118 306 V346',
    'M83.6 306.2 H120.4',
    'M99 293 V288 H105 V293',
    'M102 288 V283.4',
    /* tower with cupola */
    'M126 346 V304 H146 V346',
    'M132 304 A4.6 4.2 0 0 1 141 304',
    'M136.5 299.8 V294.6',
    /* stepped tower with needle spire */
    'M152 346 V298 H160 V288 H172 V346',
    'M166 288 V271',
    'M163.4 279 H168.6',
    /* stepped tower, right of hero */
    'M218 346 V310 H226 V298 H242 V346',
    /* flat-topped tower */
    'M248 346 V294 H268 V346',
    /* low block, bleeding off the right trim */
    'M276 346 V314 H306 V346',
  ];

  /* fine interior detail — dome ribs & meridians */
  const detailD = [
    'M91 340 V311', 'M96.5 342 V311', 'M102 343 V311', 'M107.5 342 V311', 'M113 340 V311',
    'M87.6 300.6 Q92 294.6 102 293',
    'M102 305 V293',
    'M116.4 300.6 Q112 294.6 102 293',
  ];

  /* ── HERO LANDMARK — lattice tower, off-centre at 65% ─────────────── */
  const heroD = [
    'M178 346 Q185.5 314 190.5 272',
    'M212 346 Q204.5 314 199.5 272',
    'M190.5 272 L192.6 260 M199.5 272 L197.4 260 M192.6 260 H197.4',
    'M195 260 V250',
    'M180.5 332 Q195 320.5 209.5 332',
    'M181 320 H209',
    'M181.8 316.4 H208.2',
    'M186.6 292 H203.4',
    'M187.3 288.4 H202.7',
    'M184.1 316.4 L203.9 304 M205.9 316.4 L186.1 304',
    'M186.1 304 L202.1 292 M203.9 304 L187.9 292',
    'M188.3 288.4 L199.6 274.6 M201.7 288.4 L190.4 274.6',
    'M190.4 274.6 H199.6',
  ];

  /* ── PALMS — foreground, right trim ───────────────────────────────── */
  const palmSpecs = [
    { x: 262, h: 46, lean: -6, len: 21, spin: -7 },
    { x: 293, h: 31, lean: 6, len: 16.5, spin: 9 },
  ];
  /* a fountain crown: two long arching fronds, two mid, two upright */
  const frondShapes = [
    [1.02, 0.46, 0.62, -0.28],
    [-1.02, 0.46, -0.62, -0.28],
    [0.78, -0.24, 0.36, -0.66],
    [-0.78, -0.24, -0.36, -0.66],
    [0.46, -0.78, 0.12, -0.56],
    [-0.46, -0.78, -0.12, -0.56],
  ];
  const frondScale = [1, 0.94, 0.9, 0.86, 0.8, 0.76];
  const palmD = [];
  for (let i = 0; i < palmSpecs.length; i++) {
    const p = palmSpecs[i];
    const tx = p.x + p.lean;
    const ty = G - p.h;
    palmD.push('M' + (p.x - 1.1) + ' ' + G + ' Q' + n2(p.x + p.lean * 0.3 - 0.75) + ' ' + n2(G - p.h * 0.62) + ' ' + n2(tx - 0.42) + ' ' + ty);
    palmD.push('M' + (p.x + 1.1) + ' ' + G + ' Q' + n2(p.x + p.lean * 0.3 + 0.75) + ' ' + n2(G - p.h * 0.62) + ' ' + n2(tx + 0.42) + ' ' + ty);
    for (let j = 0; j < frondShapes.length; j++) {
      palmD.push(frond(tx, ty - 0.4, frondShapes[j], p.len * frondScale[j], p.spin));
    }
  }

  /* ── WINDOWS — 14 ticks on the whole card ─────────────────────────── */
  const windowD = [
    'M164 296 h4.4', 'M164 302 h4.4', 'M164 308 h4.4', 'M154 306 h3.6', 'M154 312 h3.6',
    'M230 306 h4.6', 'M230 313 h4.6', 'M230 320 h4.6',
    'M281 322 h4.4', 'M281 329 h4.4', 'M292 325 h4.4',
    'M131 312 h4.2', 'M131 319 h4.2', 'M138 315 h4.2',
  ];

  /* ── REFLECTION — broken dashes, never a mirrored copy ────────────── */
  const refl = [
    [180, 194, 350], [198, 210, 350], [88, 104, 350], [108, 116, 350], [251, 265, 350],
    [184, 206, 354], [92, 112, 354], [254, 263, 354],
    [188, 202, 358], [96, 107, 358], [256, 262, 358],
    [191, 200, 362], [99, 105, 362],
  ];

  /* knock-out strokes: front work genuinely breaks the lines behind it */
  const cuts = (arr, w, pfx) =>
    arr.map((d, i) => (
      <path key={pfx + i} d={d} fill="none" stroke="#000" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" />
    ));

  const frontCuts = (pfx) => [
    cuts(frontD, wFront + 2.2 * k, pfx + 'f'),
    cuts(detailD, wFine + 1.9 * k, pfx + 'd'),
    cuts(heroD, wFront + 2 * k, pfx + 'h'),
    cuts(palmD, wFront + 1.7 * k, pfx + 'p'),
    cuts(windowD, wFront + 1.5 * k, pfx + 'w'),
    cuts(hangerD, wFine + 1.7 * k, pfx + 'g'),
  ];

  return (
    <svg
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
    >
      <defs>
        <linearGradient id={uid + "-foil"} x1="0" y1="0" x2="1" y2="0.27">
          <stop offset="0%" stopColor={hexA(accent, 0.95)} />
          <stop offset="34%" stopColor={hexA(accent, 0.74)} />
          <stop offset="50%" stopColor={hexA(color, 0.72)} />
          <stop offset="66%" stopColor={hexA(accent, 0.74)} />
          <stop offset="100%" stopColor={hexA(accent, 0.95)} />
        </linearGradient>
        <radialGradient id={uid + "-glow"} cx="0.64" cy="0.86" r="0.52">
          <stop offset="0%" stopColor={hexA(accent, 0.11)} />
          <stop offset="60%" stopColor={hexA(accent, 0.03)} />
          <stop offset="100%" stopColor={hexA(accent, 0)} />
        </radialGradient>
        <mask id={uid + "-cutFront"} maskUnits="userSpaceOnUse" x="0" y="0" width="300" height="400">
          <rect x="0" y="0" width="300" height="400" fill="#fff" />
          {frontCuts('a')}
        </mask>
        <mask id={uid + "-cutAll"} maskUnits="userSpaceOnUse" x="0" y="0" width="300" height="400">
          <rect x="0" y="0" width="300" height="400" fill="#fff" />
          {frontCuts('b')}
          {cuts(midD, wMid + 2 * k, 'bm')}
        </mask>
      </defs>

      {/* horizon warmth */}
      <rect x="0" y="0" width="300" height="400" fill={"url(#" + uid + "-glow)"} />

      {/* hairline keyline */}
      <rect x="11" y="11" width="278" height="378" rx="1" fill="none" stroke={hexA(color2, 0.28)} strokeWidth={0.5 * k} />

      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {/* star field */}
        {stars.map((s, i) => (
          <circle key={'s' + i} cx={s.x} cy={s.y} r={s.r} fill={hexA(color, s.o)} />
        ))}

        {/* crescent moon */}
        <path d="M56.8 37.4 A9 9 0 1 0 56.8 54.6" stroke={hexA(accent, 0.72)} strokeWidth={0.9 * k} />

        {/* back plane */}
        <g mask={"url(#" + uid + "-cutAll)"} stroke={hexA(color2, 0.34)} strokeWidth={wBack}>
          {backD.map((d, i) => (
            <path key={'bk' + i} d={d} />
          ))}
        </g>

        {/* mid plane */}
        <g mask={"url(#" + uid + "-cutFront)"} stroke={hexA(color2, 0.62)} strokeWidth={wMid}>
          {midD.map((d, i) => (
            <path key={'md' + i} d={d} />
          ))}
        </g>

        {/* front plane */}
        <g stroke={hexA(color, 0.95)} strokeWidth={wFront}>
          {frontD.map((d, i) => (
            <path key={'fr' + i} d={d} />
          ))}
        </g>
        <g stroke={hexA(color, 0.5)} strokeWidth={wFine}>
          {detailD.map((d, i) => (
            <path key={'dt' + i} d={d} />
          ))}
          {hangerD.map((d, i) => (
            <path key={'hg' + i} d={d} />
          ))}
        </g>

        {/* hero landmark — the single foil element */}
        <g stroke={"url(#" + uid + "-foil)"} strokeWidth={wFront}>
          {heroD.map((d, i) => (
            <path key={'hr' + i} d={d} />
          ))}
        </g>
        <circle cx="195" cy="249.2" r={0.85 * k} fill={hexA(accent, 0.9)} />

        {/* palms */}
        <g stroke={hexA(color, 0.92)} strokeWidth={wFront}>
          {palmD.map((d, i) => (
            <path key={'pl' + i} d={d} />
          ))}
        </g>

        {/* window ticks */}
        <g stroke={hexA(color, 0.72)} strokeWidth={0.9 * k}>
          {windowD.map((d, i) => (
            <path key={'wn' + i} d={d} />
          ))}
        </g>

        {/* full-bleed ground line */}
        <path d="M0 346 H300" stroke={hexA(color, 0.9)} strokeWidth={wGround} />

        {/* broken reflection */}
        {refl.map((r, i) => (
          <path
            key={'rf' + i}
            d={'M' + r[0] + ' ' + r[2] + ' H' + r[1]}
            stroke={hexA(color, Math.max(0.06, 0.3 - (r[2] - 350) * 0.02))}
            strokeWidth={0.7 * k}
          />
        ))}
      </g>
    </svg>
  );
}

// ─── WatercolorMagnoliaBranch — magnolia-branch
function WatercolorMagnoliaBranch({ color = '#F3C7D1', color2 = '#9AA88C', accent = '#C8A951', scale = 300 }) {
  const PINK = color;
  const SAGE = color2;
  const GOLD = accent;
  const VEIL = '#FBEDEF';
  const ROSE = '#E39CAC';
  const DEEP = '#C9758B';
  const PLUM = '#9E5069';
  const OLIVE = '#6E7A5E';
  const BARK = '#8A7565';
  const BARKD = '#6E5648';

  // obovate-spatulate tepal: narrow base at origin, widest at ~65%, shallow notch
  // at the tip, and a `b` lean so no two tepals are the same shape
  const tepalD = (L, W, b) => {
    const t = (b || 0) * W;
    return (
      `M 0 0 C ${-W * 0.19} ${-L * 0.16} ${-W * 0.46 + t * 0.3} ${-L * 0.34} ${-W * 0.52 + t * 0.5} ${-L * 0.62}` +
      ` C ${-W * 0.57 + t * 0.8} ${-L * 0.86} ${-W * 0.3 + t} ${-L * 0.995} ${-W * 0.14 + t} ${-L}` +
      ` C ${-W * 0.07 + t} ${-L * 0.945} ${W * 0.07 + t} ${-L * 0.945} ${W * 0.14 + t} ${-L}` +
      ` C ${W * 0.3 + t} ${-L * 0.995} ${W * 0.57 + t * 0.8} ${-L * 0.86} ${W * 0.52 + t * 0.5} ${-L * 0.62}` +
      ` C ${W * 0.46 + t * 0.3} ${-L * 0.34} ${W * 0.19} ${-L * 0.16} 0 0 Z`
    );
  };

  const leafD = (L, W) =>
    `M 0 0 C ${-W * 0.22} ${-L * 0.18} ${-W * 0.5} ${-L * 0.4} ${-W * 0.46} ${-L * 0.66}` +
    ` C ${-W * 0.42} ${-L * 0.88} ${-W * 0.18} ${-L * 0.97} 0 ${-L}` +
    ` C ${W * 0.18} ${-L * 0.97} ${W * 0.42} ${-L * 0.88} ${W * 0.46} ${-L * 0.66}` +
    ` C ${W * 0.5} ${-L * 0.4} ${W * 0.22} ${-L * 0.18} 0 0 Z`;

  const budD = (L, W) =>
    `M 0 0 C ${-W * 0.52} ${-L * 0.18} ${-W * 0.46} ${-L * 0.6} 0 ${-L}` +
    ` C ${W * 0.46} ${-L * 0.6} ${W * 0.52} ${-L * 0.18} 0 0 Z`;

  // tapered filled twig: real width at the base, converging to a point
  const twigD = (x1, y1, x2, y2, w, bend) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const L = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = -dy / L;
    const ny = dx / L;
    const mx = (x1 + x2) / 2 + nx * (bend || 0);
    const my = (y1 + y2) / 2 + ny * (bend || 0);
    return (
      `M ${x1 + nx * w} ${y1 + ny * w} Q ${mx + nx * w * 0.5} ${my + ny * w * 0.5} ${x2} ${y2}` +
      ` Q ${mx - nx * w * 0.5} ${my - ny * w * 0.5} ${x1 - nx * w} ${y1 - ny * w} Z`
    );
  };

  // one tepal = three washes: base->tip gradient, feathered form shadow, hard throat
  const tepal = (k, x, y, rot, L, W, face, op, edge, sat, bend) => {
    const s = sat === undefined ? 1 : sat;
    const d = tepalD(L, W, bend);
    const inner = face === 'in';
    return (
      <g key={k} transform={`translate(${x} ${y}) rotate(${rot})`} opacity={op}>
        {/* wet halo: the wash bleeds a hair past the drawn edge */}
        <path d={d} transform="scale(1.05)" fill={hexA(inner ? VEIL : PINK, (inner ? 0.14 : 0.2) * s)} filter="url(#mgSoft)" />
        <path d={d} fill={`url(#mg-${inner ? 'in' : 'out'})`} />
        <path d={d} transform="scale(0.72 0.82)" fill={hexA(ROSE, (inner ? 0.13 : 0.28) * s)} filter="url(#mgSoft)" />
        <path
          d={
            `M ${-W * 0.2} 0 C ${-W * 0.28} ${-L * 0.13} ${-W * 0.15} ${-L * 0.26} 0 ${-L * 0.3}` +
            ` C ${W * 0.15} ${-L * 0.26} ${W * 0.28} ${-L * 0.13} ${W * 0.2} 0 Z`
          }
          fill={hexA(inner ? ROSE : DEEP, (inner ? 0.32 : 0.62) * s)}
        />
        <path
          d={`M ${-W * 0.02} ${-L * 0.08} C ${-W * 0.05} ${-L * 0.3} ${-W * 0.05} ${-L * 0.44} ${-W * 0.02} ${-L * 0.6} L ${W * 0.02} ${-L * 0.58} C ${W * 0.04} ${-L * 0.42} ${W * 0.04} ${-L * 0.3} ${W * 0.02} ${-L * 0.08} Z`}
          fill={hexA(inner ? DEEP : PLUM, 0.1 * s)}
        />
        {edge ? (
          <g>
            <clipPath id={`mgc-${k}`}>
              <path d={d} />
            </clipPath>
            <g clipPath={`url(#mgc-${k})`}>
              <path d={d} transform="translate(1.4 1.7)" fill={hexA(DEEP, 0.2 * s)} />
            </g>
          </g>
        ) : null}
      </g>
    );
  };

  const leaf = (k, x, y, rot, L, W, op) => (
    <g key={k} transform={`translate(${x} ${y}) rotate(${rot})`} opacity={op}>
      <path d={leafD(L, W)} transform="scale(1.06)" fill={hexA(SAGE, 0.16)} filter="url(#mgSoft)" />
      <path d={leafD(L, W)} fill={hexA(SAGE, 0.6)} />
      <path
        d={`M 0 0 C ${-W * 0.22} ${-L * 0.18} ${-W * 0.5} ${-L * 0.4} ${-W * 0.46} ${-L * 0.66} C ${-W * 0.42} ${-L * 0.88} ${-W * 0.18} ${-L * 0.97} 0 ${-L} Z`}
        fill={hexA(OLIVE, 0.2)}
        filter="url(#mgSoft)"
      />
      <path
        d={`M 0 ${-L * 0.08} C ${W * 0.05} ${-L * 0.35} ${W * 0.04} ${-L * 0.68} 0 ${-L * 0.88}`}
        stroke={hexA(OLIVE, 0.3)}
        strokeWidth="0.32"
        fill="none"
        strokeLinecap="round"
      />
    </g>
  );

  // silky-pubescent magnolia bud: pink body, fuzzy calyx with hair strokes
  const bud = (k, x, y, rot, L, W, op, fuzz) => (
    <g key={k} transform={`translate(${x} ${y}) rotate(${rot})`} opacity={op}>
      <path d={budD(L, W)} transform="scale(1.08)" fill={hexA(PINK, 0.16)} filter="url(#mgSoft)" />
      <path d={budD(L, W)} fill={hexA(PINK, 0.6)} />
      <path d={budD(L * 0.9, W * 0.5)} transform={`translate(${-W * 0.17} 0)`} fill={hexA(ROSE, 0.32)} filter="url(#mgSoft)" />
      <path d={budD(L * 0.26, W * 0.66)} fill={hexA(DEEP, 0.4)} />
      {/* silky-pubescent calyx: a small warm cup with hair strokes */}
      {(fuzz ? [0, 1, 2, 3] : []).map((i) => {
        const a = ((-58 + i * 39) * Math.PI) / 180;
        const r0 = W * 0.34;
        const r1 = W * 0.34 + 1.5;
        return (
          <path
            key={`${k}h${i}`}
            d={`M ${Math.sin(a) * r0} ${L * 0.02 - Math.cos(a) * r0 * 0.35} L ${Math.sin(a) * r1} ${L * 0.02 - Math.cos(a) * r1 * 0.35}`}
            stroke={hexA(OLIVE, 0.3)}
            strokeWidth="0.22"
            strokeLinecap="round"
            fill="none"
          />
        );
      })}
      <path
        d={`M ${-W * 0.34} ${L * 0.02} C ${-W * 0.38} ${-L * 0.09} ${W * 0.38} ${-L * 0.09} ${W * 0.34} ${L * 0.02} C ${W * 0.14} ${L * 0.09} ${-W * 0.14} ${L * 0.09} ${-W * 0.34} ${L * 0.02} Z`}
        fill={hexA(OLIVE, 0.28)}
      />
      <path d={`M ${-W * 0.26} ${L * 0.02} C ${-W * 0.12} ${-L * 0.05} ${W * 0.1} ${-L * 0.05} ${W * 0.24} ${L * 0.01}`} stroke={hexA(BARKD, 0.22)} strokeWidth="0.24" fill="none" />
    </g>
  );

  // hero bloom, base (250,38): 7 large tepals + 1 inner, uneven radial gaps
  const heroBack = [
    ['h0', 6, 28, 17, 'out', 0.7, false, 0.14],
    ['h1', 302, 27, 16, 'out', 0.66, false, -0.12],
    ['h2', 52, 31, 19, 'out', 0.86, false, 0.08],
    ['h3', 250, 30, 18, 'in', 0.88, false, -0.16],
  ];
  const heroFront = [
    ['h4', 100, 33, 20, 'out', 1, true, -0.1],
    ['h5', 152, 31, 19, 'out', 0.9, false, 0.13],
    ['h6', 200, 32, 20, 'in', 0.95, true, 0.06],
    ['h7', 176, 16, 11, 'in', 0.8, false, -0.18],
  ];

  // mid bloom, base (211,86): turned three-quarter, 80% saturation
  const midBack = [
    ['m0', 28, 22, 14, 'out', 0.6, false, 0.12],
    ['m1', 350, 24, 15, 'out', 0.62, false, -0.1],
  ];
  const midFront = [
    ['m2', 300, 25, 16, 'out', 0.86, true, 0.15],
    ['m3', 262, 24, 15, 'in', 0.8, false, -0.08],
    ['m4', 228, 21, 13, 'in', 0.68, false, 0.1],
    ['m5', 324, 17, 10.5, 'in', 0.55, false, -0.14],
  ];

  // small bloom at the terminal tip, base (191,117): 62% saturation
  const smallBack = [['s0', 20, 13, 9, 'out', 0.5, false, 0.12]];
  const smallFront = [
    ['s1', 336, 16, 10.5, 'out', 0.68, false, -0.12],
    ['s2', 306, 14, 9, 'in', 0.56, false, 0.16],
    ['s3', 4, 11, 7.5, 'in', 0.5, false, -0.08],
  ];

  // sub-pixel specks vanish in the gallery thumbnail — hold a floor there
  const dotMin = scale < 200 ? 0.8 : 0.42;

  // [x, y, r, opacity, elongated]
  const specks = [
    [288, 16, 1.0, 0.6, 0], [293, 48, 0.7, 0.5, 0], [277, 70, 1.25, 0.68, 1],
    [266, 7, 0.6, 0.45, 0], [231, 13, 0.9, 0.55, 0], [212, 60, 0.8, 0.5, 1],
    [225, 82, 1.05, 0.62, 0], [242, 90, 0.55, 0.4, 0], [201, 64, 0.75, 0.5, 0],
    [183, 92, 1.0, 0.58, 1], [174, 104, 0.7, 0.45, 0], [196, 127, 0.9, 0.5, 0],
    [256, 78, 0.5, 0.4, 0], [297, 30, 0.85, 0.55, 0], [261, 24, 0.45, 0.35, 0],
    [163, 136, 0.8, 0.45, 0], [143, 150, 0.6, 0.4, 1], [124, 164, 1.0, 0.48, 0],
    [102, 147, 0.5, 0.35, 0], [153, 118, 0.7, 0.42, 0], [170, 155, 0.9, 0.45, 0],
    [37, 62, 0.55, 0.3, 0], [71, 28, 0.85, 0.36, 0], [19, 117, 0.45, 0.28, 0],
    [30, 376, 0.9, 0.5, 0], [52, 363, 0.6, 0.4, 1], [16, 351, 1.05, 0.5, 0],
    [45, 388, 0.7, 0.45, 0], [63, 344, 0.5, 0.35, 0],
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="mg-out" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={VEIL} stopOpacity="0.12" />
          <stop offset="0.26" stopColor={PINK} stopOpacity="0.46" />
          <stop offset="0.6" stopColor={PINK} stopOpacity="0.62" />
          <stop offset="0.83" stopColor={ROSE} stopOpacity="0.72" />
          <stop offset="1" stopColor={DEEP} stopOpacity="0.86" />
        </linearGradient>
        <linearGradient id="mg-in" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FDFBF8" stopOpacity="0.05" />
          <stop offset="0.36" stopColor={VEIL} stopOpacity="0.3" />
          <stop offset="0.76" stopColor={PINK} stopOpacity="0.44" />
          <stop offset="1" stopColor={ROSE} stopOpacity="0.58" />
        </linearGradient>
        <linearGradient id="mg-bark" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0" stopColor={BARK} stopOpacity="0.6" />
          <stop offset="0.5" stopColor={BARK} stopOpacity="0.52" />
          <stop offset="1" stopColor={BARKD} stopOpacity="0.46" />
        </linearGradient>
        <filter id="mgSoft" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="1.5" />
        </filter>
        <filter id="mgHalo" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
        <filter id="mgWash" x="-14%" y="-14%" width="128%" height="128%">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed="5" result="g1" />
          <feColorMatrix in="g1" type="saturate" values="0" result="g2" />
          <feComponentTransfer in="g2" result="g3">
            <feFuncA type="linear" slope="0.1" intercept="0" />
          </feComponentTransfer>
          <feComposite in="g3" in2="SourceGraphic" operator="in" result="grain" />
          <feTurbulence type="fractalNoise" baseFrequency="0.045" numOctaves="2" seed="11" result="warp" />
          <feDisplacementMap in="SourceGraphic" in2="warp" scale="2.9" xChannelSelector="R" yChannelSelector="G" result="disp" />
          <feMerge>
            <feMergeNode in="disp" />
            <feMergeNode in="grain" />
          </feMerge>
        </filter>
        <filter id="mgEdge" x="-14%" y="-14%" width="128%" height="128%">
          <feTurbulence type="fractalNoise" baseFrequency="0.045" numOctaves="2" seed="3" result="w2" />
          <feDisplacementMap in="SourceGraphic" in2="w2" scale="1.4" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        {/* dry-brush skip: a bare-paper gap near one tepal edge */}
        <mask id="mgDry" maskUnits="userSpaceOnUse" x="0" y="0" width="300" height="400">
          <rect x="0" y="0" width="300" height="400" fill="#fff" />
          <ellipse cx="193.4" cy="76.4" rx="3.4" ry="0.6" transform="rotate(31 193.4 76.4)" fill="#000" />
        </mask>
      </defs>

      {/* backruns: pigment that pooled and dried outside the shapes */}
      <g>
        <ellipse cx="223" cy="66" rx="16" ry="9.5" fill={hexA(DEEP, 0.08)} filter="url(#mgHalo)" />
        <ellipse cx="274" cy="22" rx="13" ry="8" fill={hexA(PINK, 0.1)} filter="url(#mgHalo)" />
        <ellipse cx="197" cy="93" rx="9.5" ry="6" fill={hexA(PINK, 0.1)} filter="url(#mgHalo)" />
        <ellipse cx="184" cy="114" rx="8" ry="5" fill={hexA(PINK, 0.07)} filter="url(#mgHalo)" />
        <ellipse cx="30" cy="362" rx="12" ry="8" fill={hexA(PINK, 0.06)} filter="url(#mgHalo)" />
      </g>

      <g opacity="0.945">
        {/* z-layer 1 — tepals that pass BEHIND the branch */}
        <g filter="url(#mgWash)">
          {midBack.map((t) => tepal(t[0], 211, 86, t[1], t[2], t[3], t[4], t[5], t[6], 0.8, t[7]))}
          {smallBack.map((t) => tepal(t[0], 191, 117, t[1], t[2], t[3], t[4], t[5], t[6], 0.62, t[7]))}
        </g>

        {/* z-layer 2 — woody branch, forks, leaves, buds */}
        <g filter="url(#mgEdge)">
          {/* main stem: filled tapered path, 3u at the card edge -> a point at the tip */}
          <path
            d="M 308.3 25.5 C 284.1 45.3 262.9 51.5 240.6 63.1 C 214.5 76.9 200.5 92.5 191 117 C 199.5 91.5 213.5 75.1 239.4 60.9 C 261.1 48.5 281.9 42.7 305.7 22.5 Z"
            fill="url(#mg-bark)"
          />
          <path
            d="M 307.6 25.2 C 283.8 44.9 262.6 51.1 240.3 62.8 C 214.3 76.6 200.3 92.2 190.9 116.4"
            stroke={hexA(BARKD, 0.22)}
            strokeWidth="0.45"
            fill="none"
            strokeLinecap="round"
          />
          {/* dry-brush gap in the bark */}
          <path d="M 276 47.5 C 272 48.6 268.4 49.6 265.2 51 L 264.6 49.2 C 268 47.8 271.8 46.7 275.6 45.8 Z" fill={hexA('#FDFBF8', 0.5)} />
          {/* lenticels */}
          <circle cx="291" cy="31" r="0.4" fill={hexA(BARKD, 0.45)} />
          <circle cx="250" cy="57" r="0.36" fill={hexA(BARKD, 0.4)} />
          <circle cx="215" cy="80" r="0.32" fill={hexA(BARKD, 0.36)} />

          {/* forks — attached at nodes, 30-40 degrees off the stem */}
          <path d={twigD(266, 49, 255, 41, 1.0, 1.1)} fill={hexA(BARK, 0.6)} />
          <path d={twigD(266, 49, 286, 33.5, 0.95, -1.5)} fill={hexA(BARK, 0.56)} />
          <path d={twigD(221, 74, 210.5, 85, 0.9, 1.2)} fill={hexA(BARK, 0.56)} />
          <path d={twigD(221, 74, 231, 84, 0.7, -1)} fill={hexA(BARK, 0.5)} />
          <path d={twigD(194.5, 108, 200.5, 104.5, 0.5, -0.7)} fill={hexA(BARK, 0.45)} />
          {/* zone B twig */}
          <path d={twigD(-4, 375, 39, 352.5, 0.95, 3)} fill={hexA(BARK, 0.44)} />

          {/* leaves — desaturated sage, kept to ~15% of the inked area */}
          {leaf('l0', 268, 50, 116, 22, 10, 0.5)}
          {leaf('l1', 252, 44, 208, 19, 9, 0.4)}
          {leaf('l2', 221, 75, 112, 24, 11, 0.52)}
          {leaf('l3', 207, 90, 250, 17, 8, 0.38)}
          {leaf('l4', 26, 366, 56, 15, 7, 0.42)}
          {leaf('l5', 34.5, 356, -32, 12.5, 6.2, 0.36)}

          {/* buds */}
          {bud('b0', 287, 33, 40, 17, 7.6, 0.92, true)}
          {bud('b1', 231.5, 84.5, 150, 15, 6.8, 0.8)}
          {bud('b2', 200.5, 104.5, 24, 11.5, 5.4, 0.66)}
          {bud('b3', 39, 352.5, -18, 13.5, 6.3, 0.72, true)}
        </g>

        {/* z-layer 3 — blooms in front */}
        <g filter="url(#mgWash)">
          {/* small bloom — 0.55 scale, 62% saturation */}
          {smallFront.map((t) => tepal(t[0], 191, 117, t[1], t[2], t[3], t[4], t[5], t[6], 0.62, t[7]))}
          <ellipse cx="191" cy="115.5" rx="1.7" ry="1.3" fill={hexA(DEEP, 0.38)} />

          {/* mid bloom — 0.72 scale, 80% saturation, one dry-brush skip */}
          <g mask="url(#mgDry)">
            {midFront.map((t) => tepal(t[0], 211, 86, t[1], t[2], t[3], t[4], t[5], t[6], 0.8, t[7]))}
          </g>
          <ellipse cx="210.5" cy="84.5" rx="2.6" ry="1.9" fill={hexA(PLUM, 0.3)} />
          <path d="M 209.6 84 C 208.4 81.8 208.2 80 208.9 78.4" stroke={hexA(DEEP, 0.42)} strokeWidth="0.45" fill="none" strokeLinecap="round" />

          {/* hero bloom */}
          {heroBack.map((t) => tepal(t[0], 250, 38, t[1], t[2], t[3], t[4], t[5], t[6], 1, t[7]))}
          {heroFront.map((t) => tepal(t[0], 250, 38, t[1], t[2], t[3], t[4], t[5], t[6], 1, t[7]))}
          {/* throat + gynoecium column */}
          <ellipse cx="250" cy="38" rx="4.6" ry="3.4" fill={hexA(PLUM, 0.26)} filter="url(#mgSoft)" />
          <path d="M 248.5 39.6 C 247.9 36.8 248.5 34.6 249.9 32.9 C 251.4 34.6 251.9 36.8 251.3 39.6 Z" fill={hexA(PLUM, 0.6)} />
          <path d="M 249.4 37.2 C 249 35.6 249.4 34.3 250 33.4" stroke={hexA(PLUM, 0.75)} strokeWidth="0.38" fill="none" strokeLinecap="round" />
          {[0, 1, 2, 3, 4].map((i) => {
            const a = ((-52 + i * 26) * Math.PI) / 180;
            return (
              <path
                key={`st${i}`}
                d={`M ${250 + Math.sin(a) * 1.8} ${38 - Math.cos(a) * 1.8} L ${250 + Math.sin(a) * 3.6} ${38 - Math.cos(a) * 3.6}`}
                stroke={hexA(DEEP, 0.3)}
                strokeWidth="0.3"
                strokeLinecap="round"
                fill="none"
              />
            );
          })}

          {/* deliberate imperfections: washes that escape the petal boundary */}
          <ellipse cx="236" cy="70" rx="4.6" ry="2.6" transform="rotate(32 236 70)" fill={hexA(PINK, 0.2)} filter="url(#mgSoft)" />
          <ellipse cx="268" cy="14" rx="3.4" ry="2.2" transform="rotate(-18 268 14)" fill={hexA(PINK, 0.16)} filter="url(#mgSoft)" />

          {/* one detached tepal, drifting */}
          <g opacity="0.4">{tepal('fall', 227, 137, 206, 14.5, 10, 'out', 1, false, 0.7, 0.12)}</g>
        </g>

        {/* gold: 4-6 sizes, irregular clusters, a few elongated flecks */}
        <g>
          {specks.map((s, i) =>
            s[4] ? (
              <ellipse
                key={`g${i}`}
                cx={s[0]}
                cy={s[1]}
                rx={Math.max(s[2], dotMin) * 1.9}
                ry={Math.max(s[2], dotMin) * 0.6}
                transform={`rotate(${(i * 47) % 180} ${s[0]} ${s[1]})`}
                fill={hexA(GOLD, s[3])}
              />
            ) : (
              <circle key={`g${i}`} cx={s[0]} cy={s[1]} r={Math.max(s[2], dotMin)} fill={hexA(GOLD, s[3])} />
            )
          )}
        </g>
      </g>
    </svg>
  );
}

// ─── ConfettiBalloonCelebration — confetti-balloons
function ConfettiBalloonCelebration({ color, color2, accent, scale = 300 }) {
  const INK = '#22252A';
  const CREAM = '#FBF7F0';
  const HUES = [color, '#F5B93B', color2, '#2FA79B', accent, '#3D6FD1', '#5BB56B'];
  const RASP = '#DE3E7C';

  // deterministic hash -> [0,1)
  const rnd = (i, s) => {
    const x = Math.sin((i + 1) * 127.1 + s * 311.7) * 43758.5453;
    return x - Math.floor(x);
  };

  const shade = (hex, f) => {
    let h = String(hex || '#888888').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const r = parseInt(h.slice(0, 2), 16) || 0;
    const g = parseInt(h.slice(2, 4), 16) || 0;
    const b = parseInt(h.slice(4, 6), 16) || 0;
    return 'rgb(' + Math.round(r * f) + ',' + Math.round(g * f) + ',' + Math.round(b * f) + ')';
  };

  // ---------------------------------------------------------------- confetti
  const micro = scale < 170 ? 1.35 : 1;
  const tiers = [
    { n: 10, lo: 7.0, hi: 10.4, R: 64, s: 0, m: 1 },
    { n: 32, lo: 3.8, hi: 6.0, R: 116, s: 100, m: 1 },
    { n: 46, lo: 1.5, hi: 2.6, R: 162, s: 200, m: micro }
  ];
  // corner attractors: x, y, inward angle, weight (top corners heavier)
  const corners = [
    { x: 14, y: 13, a: 0.78 },
    { x: 287, y: 13, a: 2.36 },
    { x: 19, y: 388, a: -0.78 },
    { x: 281, y: 388, a: -2.36 }
  ];

  const pieces = [];
  tiers.forEach((tier, ti) => {
    for (let k = 0; k < tier.n; k++) {
      const i = tier.s + k;
      let placed = null;
      for (let att = 0; att < 8 && !placed; att++) {
        const u0 = rnd(i, 1 + att * 5);
        const ci = u0 < 0.3077 ? 0 : u0 < 0.6154 ? 1 : u0 < 0.8077 ? 2 : 3;
        const c = corners[ci];
        const ang = c.a + (rnd(i, 2 + att * 5) - 0.5) * 4.0;
        const rad = 9 + Math.pow(rnd(i, 3 + att * 5), 0.62) * tier.R;
        const x = c.x + Math.cos(ang) * rad;
        const y = c.y + Math.sin(ang) * rad * 1.12;
        if (x < -9 || x > 309 || y < -9 || y > 409) continue;
        // protected type well + feather
        if (x > 31 && x < 269 && y > 142 && y < 302) continue;
        // in the type band only tier-3 micro pieces, and only in the gutters
        if (y > 142 && y < 302 && (ti < 2 || (x > 26 && x < 274))) continue;
        const L = (tier.lo + rnd(i, 4) * (tier.hi - tier.lo)) * tier.m;
        let clash = false;
        if (i % 9 !== 0) {
          for (let j = 0; j < pieces.length; j++) {
            const p = pieces[j];
            if (Math.hypot(p.x - x, p.y - y) < (p.L + L) * 0.62) { clash = true; break; }
          }
        }
        if (clash) continue;
        placed = { x: x, y: y, L: L };
      }
      if (!placed) continue;

      const sh = rnd(i, 6);
      const shape = sh < 0.40 ? 'cap' : sh < 0.65 ? 'dot' : sh < 0.80 ? 'rho' : sh < 0.90 ? 'squ' : 'spk';

      let hue;
      if (rnd(i, 7) > 0.93) {
        hue = RASP;
        placed.hueIdx = -1;
      } else {
        let hx = (i * 3 + ti * 2) % HUES.length;
        for (let j = pieces.length - 1; j >= 0 && j > pieces.length - 10; j--) {
          const p = pieces[j];
          if (p.hueIdx === hx && Math.hypot(p.x - placed.x, p.y - placed.y) < 15) hx = (hx + 2) % HUES.length;
        }
        hue = HUES[hx];
        placed.hueIdx = hx;
      }

      const ou = rnd(i, 8);
      const op = ou < 0.70 ? 1 : ou < 0.90 ? 0.72 : 0.5;
      const rot = rnd(i, 9) < 0.7 ? (rnd(i, 10) - 0.5) * 70 : rnd(i, 10) * 360;

      pieces.push({ x: placed.x, y: placed.y, L: placed.L, hueIdx: placed.hueIdx, shape: shape, hue: hue, op: op, rot: rot, i: i });
    }
  });

  const drawPiece = (p) => {
    const L = p.L;
    const r = L / 2;
    let node;
    if (p.shape === 'cap') {
      node = (
        <path
          d={'M' + (-r).toFixed(2) + ' 0 Q 0 ' + (-L * 0.13).toFixed(2) + ' ' + r.toFixed(2) + ' 0'}
          fill="none"
          stroke={p.hue}
          strokeWidth={(L / 3.2).toFixed(2)}
          strokeLinecap="round"
        />
      );
    } else if (p.shape === 'dot') {
      node = <ellipse cx={(r * 0.05).toFixed(2)} cy={(-r * 0.06).toFixed(2)} rx={(r * 1.05).toFixed(2)} ry={(r * 0.94).toFixed(2)} fill={p.hue} />;
    } else if (p.shape === 'rho') {
      const h = L * 0.5;
      const kk = h * 0.55;
      node = (
        <path
          d={'M' + (-r).toFixed(2) + ' ' + (h / 2).toFixed(2) + ' L' + (-r + kk).toFixed(2) + ' ' + (-h / 2).toFixed(2) +
            ' L' + r.toFixed(2) + ' ' + (-h / 2).toFixed(2) + ' L' + (r - kk).toFixed(2) + ' ' + (h / 2).toFixed(2) + ' Z'}
          fill={p.hue}
        />
      );
    } else if (p.shape === 'squ') {
      const a = L * 0.30;
      node = (
        <g fill="none" stroke={p.hue} strokeLinecap="round">
          <path d={'M' + (-r).toFixed(2) + ' 0 C ' + (-L * 0.28).toFixed(2) + ' ' + (-a).toFixed(2) + ' ' + (-L * 0.10).toFixed(2) + ' ' + a.toFixed(2) + ' 0 0'} strokeWidth={(L * 0.17).toFixed(2)} />
          <path d={'M0 0 C ' + (L * 0.10).toFixed(2) + ' ' + (-a).toFixed(2) + ' ' + (L * 0.28).toFixed(2) + ' ' + a.toFixed(2) + ' ' + r.toFixed(2) + ' 0'} strokeWidth={(L * 0.10).toFixed(2)} />
        </g>
      );
    } else {
      const c = r * 0.26;
      node = (
        <path
          d={'M0 ' + (-r).toFixed(2) + ' C ' + c.toFixed(2) + ' ' + (-c).toFixed(2) + ' ' + c.toFixed(2) + ' ' + (-c).toFixed(2) + ' ' + r.toFixed(2) + ' 0' +
            ' C ' + c.toFixed(2) + ' ' + c.toFixed(2) + ' ' + c.toFixed(2) + ' ' + c.toFixed(2) + ' 0 ' + r.toFixed(2) +
            ' C ' + (-c).toFixed(2) + ' ' + c.toFixed(2) + ' ' + (-c).toFixed(2) + ' ' + c.toFixed(2) + ' ' + (-r).toFixed(2) + ' 0' +
            ' C ' + (-c).toFixed(2) + ' ' + (-c).toFixed(2) + ' ' + (-c).toFixed(2) + ' ' + (-c).toFixed(2) + ' 0 ' + (-r).toFixed(2) + ' Z'}
          fill={p.hue}
        />
      );
    }
    return (
      <g key={'p' + p.i} transform={'translate(' + p.x.toFixed(2) + ' ' + p.y.toFixed(2) + ') rotate(' + p.rot.toFixed(1) + ')'} opacity={p.op}>
        {node}
      </g>
    );
  };

  // ---------------------------------------------------------------- balloons
  const balloons = [
    { id: 'L1', x: 42, y: 37.5, rx: 24, ry: 28.5, rot: -8, fill: color, sway: 9, len: 64, ph: 0.4, dir: 1, ring: true },
    { id: 'L2', x: 82.5, y: 18.5, rx: 19, ry: 22.5, rot: 6, fill: '#F5B93B', sway: 7, len: 60, ph: 2.1, dir: -1, ring: false },
    { id: 'L3', x: 64.5, y: 80.5, rx: 14.5, ry: 17, rot: -14, fill: '#2FA79B', sway: -13, len: 36, ph: 1.2, dir: 1, ring: false },
    { id: 'R1', x: 261.5, y: 30.5, rx: 22, ry: 26, rot: 10, fill: color2, sway: -8, len: 70, ph: 3.0, dir: -1, ring: true },
    { id: 'R2', x: 225, y: 64.5, rx: 15.5, ry: 18.5, rot: -6, fill: '#5BB56B', sway: 6.5, len: 44, ph: 0.9, dir: 1, ring: false },
    { id: 'R3', x: 299, y: 75, rx: 12.5, ry: 15, rot: 12, fill: accent, sway: 5, len: 36, ph: 2.6, dir: -1, ring: false }
  ];

  const balloonPath = (rx, ry) =>
    'M0 ' + (-ry).toFixed(2) +
    ' C ' + (rx * 0.60).toFixed(2) + ' ' + (-ry).toFixed(2) + ' ' + rx.toFixed(2) + ' ' + (-ry * 0.66).toFixed(2) + ' ' + rx.toFixed(2) + ' ' + (-ry * 0.18).toFixed(2) +
    ' C ' + rx.toFixed(2) + ' ' + (ry * 0.36).toFixed(2) + ' ' + (rx * 0.60).toFixed(2) + ' ' + (ry * 0.76).toFixed(2) + ' ' + (rx * 0.21).toFixed(2) + ' ' + (ry * 0.96).toFixed(2) +
    ' C ' + (rx * 0.09).toFixed(2) + ' ' + (ry * 1.01).toFixed(2) + ' ' + (-rx * 0.09).toFixed(2) + ' ' + (ry * 1.01).toFixed(2) + ' ' + (-rx * 0.21).toFixed(2) + ' ' + (ry * 0.96).toFixed(2) +
    ' C ' + (-rx * 0.60).toFixed(2) + ' ' + (ry * 0.76).toFixed(2) + ' ' + (-rx).toFixed(2) + ' ' + (ry * 0.36).toFixed(2) + ' ' + (-rx).toFixed(2) + ' ' + (-ry * 0.18).toFixed(2) +
    ' C ' + (-rx).toFixed(2) + ' ' + (-ry * 0.66).toFixed(2) + ' ' + (-rx * 0.60).toFixed(2) + ' ' + (-ry).toFixed(2) + ' 0 ' + (-ry).toFixed(2) + ' Z';

  const poly = (pts) => 'M' + pts.map((q) => q[0].toFixed(2) + ' ' + q[1].toFixed(2)).join(' L ');

  const strings = balloons.map((b, bi) => {
    const h = b.ry * 1.16;
    const th = (b.rot * Math.PI) / 180;
    const kx = b.x - h * Math.sin(th);
    const ky = b.y + h * Math.cos(th);
    const n = 24;
    const pts = [];
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const x = kx + (Math.sin(b.ph + t * 3.6) - Math.sin(b.ph)) * b.sway * (0.3 + 0.7 * t) + Math.sin(t * 17 + b.ph * 3) * 0.32;
      pts.push([x, ky + b.len * t]);
    }
    const last = pts[n];
    const cr = 1.6 + b.ry * 0.13;
    const cpts = [];
    for (let i = 0; i <= 26; i++) {
      const t = i / 26;
      const a = -Math.PI / 2 + b.dir * t * 1.45 * Math.PI * 2;
      const rr = cr * (1 - 0.58 * t);
      cpts.push([last[0] + Math.cos(a) * rr, last[1] + cr + Math.sin(a) * rr]);
    }
    return (
      <g key={'s' + bi} fill="none" stroke={hexA(INK, 0.72)} strokeLinecap="round" strokeLinejoin="round">
        <path d={poly(pts.slice(0, 10))} strokeWidth="0.85" />
        <path d={poly(pts.slice(9, 18))} strokeWidth="0.58" />
        <path d={poly(pts.slice(17, 25))} strokeWidth="0.38" />
        <path d={poly(cpts)} strokeWidth="0.3" />
      </g>
    );
  });

  const balloonNodes = balloons.map((b, bi) => {
    const d = balloonPath(b.rx, b.ry);
    const cid = 'cbBall' + bi;
    return (
      <g key={b.id} transform={'translate(' + b.x + ' ' + b.y + ') rotate(' + b.rot + ')'}>
        <defs>
          <clipPath id={cid}>
            <path d={d} />
          </clipPath>
        </defs>
        <path
          d={'M' + (-b.rx * 0.14).toFixed(2) + ' ' + (b.ry * 0.97).toFixed(2) +
            ' L' + (b.rx * 0.14).toFixed(2) + ' ' + (b.ry * 0.97).toFixed(2) +
            ' L' + (b.rx * 0.09).toFixed(2) + ' ' + (b.ry * 1.18).toFixed(2) +
            ' L' + (-b.rx * 0.09).toFixed(2) + ' ' + (b.ry * 1.18).toFixed(2) + ' Z'}
          fill={shade(b.fill, 0.86)}
        />
        <path d={d} fill={b.fill} stroke={b.ring ? shade(b.fill, 0.78) : 'none'} strokeWidth={b.ring ? 0.85 : 0} />
        <g clipPath={'url(#' + cid + ')'}>
          <ellipse cx={(b.rx * 0.95).toFixed(2)} cy={(b.ry * 0.85).toFixed(2)} rx={(b.rx * 1.3).toFixed(2)} ry={(b.ry * 1.3).toFixed(2)} fill={shade(b.fill, 0.84)} />
          <ellipse cx={(-b.rx * 0.18).toFixed(2)} cy={(b.ry * 0.22).toFixed(2)} rx={(b.rx * 0.62).toFixed(2)} ry={(b.ry * 0.44).toFixed(2)} fill={shade(b.fill, 0.9)} opacity="0.28" />
          <path
            d={'M' + (-b.rx * 0.58).toFixed(2) + ' ' + (-b.ry * 0.24).toFixed(2) +
              ' C ' + (-b.rx * 0.66).toFixed(2) + ' ' + (-b.ry * 0.58).toFixed(2) +
              ' ' + (-b.rx * 0.34).toFixed(2) + ' ' + (-b.ry * 0.80).toFixed(2) +
              ' ' + (-b.rx * 0.04).toFixed(2) + ' ' + (-b.ry * 0.82).toFixed(2)}
            fill="none"
            stroke={hexA(CREAM, 0.4)}
            strokeWidth={(b.rx * 0.21).toFixed(2)}
            strokeLinecap="round"
            filter="url(#cbSoft)"
          />
        </g>
      </g>
    );
  });

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <filter id="cbWob" filterUnits="userSpaceOnUse" x="-24" y="-24" width="348" height="448">
          <feTurbulence type="fractalNoise" baseFrequency="0.34" numOctaves="2" seed="7" result="cbn" />
          <feDisplacementMap in="SourceGraphic" in2="cbn" scale="0.55" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id="cbWobB" filterUnits="userSpaceOnUse" x="-24" y="-24" width="348" height="448">
          <feTurbulence type="fractalNoise" baseFrequency="0.16" numOctaves="2" seed="3" result="cbn2" />
          <feDisplacementMap in="SourceGraphic" in2="cbn2" scale="0.3" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id="cbSoft" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="0.45" />
        </filter>
        <filter id="cbGrain" filterUnits="userSpaceOnUse" x="0" y="0" width="300" height="400">
          <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" />
        </filter>
      </defs>

      {/* strings, drawn under the balloons */}
      <g filter="url(#cbWobB)">{strings}</g>

      {/* balloons */}
      <g filter="url(#cbWobB)">{balloonNodes}</g>

      {/* confetti field — multiply so overlaps make genuine third colors */}
      <g style={{ mixBlendMode: 'multiply' }}>
        <g filter="url(#cbWob)">{pieces.map(drawPiece)}</g>
      </g>

      {/* paper grain */}
      <rect x="0" y="0" width="300" height="400" filter="url(#cbGrain)" opacity="0.05" style={{ mixBlendMode: 'multiply' }} />
    </svg>
  );
}

// ─── TropicalPalmMonstera — tropical-palm
function TropicalPalmMonstera({ color, color2, accent, scale = 300 }) {
  /* ---------------- palette ramp (4 greens + bloom) ---------------- */
  const norm = (h) => {
    let s = String(h || '').trim().replace('#', '');
    if (s.length === 3) s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
    return '#' + (s + '000000').slice(0, 6);
  };
  const mix = (a, b, t) => {
    const A = norm(a), B = norm(b);
    let o = '#';
    for (let i = 0; i < 3; i++) {
      const x = parseInt(A.substr(1 + i * 2, 2), 16);
      const y = parseInt(B.substr(1 + i * 2, 2), 16);
      const v = Math.max(0, Math.min(255, Math.round(x + (y - x) * t)));
      o += (v < 16 ? '0' : '') + v.toString(16);
    }
    return o;
  };
  const CREAM = '#F7F1E3';
  const G1 = norm(color);                                  // rainforest ink
  const G5 = norm(color2);                                 // frond lime
  const G3 = mix(mix(G1, G5, 0.45), '#1C6B4C', 0.5);       // palm forest
  const G2 = mix(G1, G3, 0.55);                            // deep jungle
  const G4 = mix(G3, G5, 0.52);                            // fern
  const BL = norm(accent);
  const BLD = mix(BL, '#2A0E08', 0.26);
  const BLL = mix(BL, CREAM, 0.48);
  const GOLD = mix(BL, '#FFD86A', 0.68);
  const fine = scale >= 190;

  /* ---------------- geometry helpers ---------------- */
  const rn = (n) => Math.round(n * 100) / 100;
  const P = (x, y) => rn(x) + ',' + rn(y);
  const RAD = Math.PI / 180;
  const cpt = (p0, p1, p2, p3, t) => {
    const u = 1 - t;
    return [
      u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0],
      u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1],
    ];
  };
  const ctan = (p0, p1, p2, p3, t) => {
    const u = 1 - t;
    return Math.atan2(
      3 * u * u * (p1[1] - p0[1]) + 6 * u * t * (p2[1] - p1[1]) + 3 * t * t * (p3[1] - p2[1]),
      3 * u * u * (p1[0] - p0[0]) + 6 * u * t * (p2[0] - p1[0]) + 3 * t * t * (p3[0] - p2[0])
    );
  };
  // filled taper: a stroke that narrows w0 -> w1 (half-widths)
  const taper = (pts, w0, w1) => {
    const up = [], dn = [];
    for (let i = 0; i < pts.length; i++) {
      const t = i / (pts.length - 1);
      const w = w0 + (w1 - w0) * t;
      const a = pts[Math.min(i + 1, pts.length - 1)], b = pts[Math.max(i - 1, 0)];
      const dx = a[0] - b[0], dy = a[1] - b[1];
      const l = Math.sqrt(dx * dx + dy * dy) || 1;
      up.push([pts[i][0] - dy / l * w, pts[i][1] + dx / l * w]);
      dn.push([pts[i][0] + dy / l * w, pts[i][1] - dx / l * w]);
    }
    dn.reverse();
    return 'M' + up.map((p) => P(p[0], p[1])).join('L') + 'L' + dn.map((p) => P(p[0], p[1])).join('L') + 'Z';
  };
  // one pointed leaflet / palm blade, asymmetric bezier halves
  const bladeD = (bx, by, dir, len, wid, bow) => {
    const c = Math.cos(dir), s = Math.sin(dir);
    const tx = bx + c * len, ty = by + s * len;
    const mx = (bx + tx) / 2, my = (by + ty) / 2;
    return 'M' + P(bx, by) +
      'Q' + P(mx - s * wid + c * bow, my + c * wid + s * bow) + ' ' + P(tx, ty) +
      'Q' + P(mx + s * wid * 0.72 + c * bow * 0.45, my - c * wid * 0.72 + s * bow * 0.45) + ' ' + P(bx, by) + 'Z';
  };
  const K = 0.5523;
  const ellD = (cx, cy, rx, ry, deg) => {
    const a = deg * RAD, ca = Math.cos(a), sa = Math.sin(a);
    const T = (px, py) => P(cx + px * ca - py * sa, cy + px * sa + py * ca);
    const kx = rx * K, ky = ry * K;
    return 'M' + T(rx, 0) + 'C' + T(rx, ky) + ' ' + T(kx, ry) + ' ' + T(0, ry) +
      'C' + T(-kx, ry) + ' ' + T(-rx, ky) + ' ' + T(-rx, 0) +
      'C' + T(-rx, -ky) + ' ' + T(-kx, -ry) + ' ' + T(0, -ry) +
      'C' + T(kx, -ry) + ' ' + T(rx, -ky) + ' ' + T(rx, 0) + 'Z';
  };

  /* ---------------- feather / coconut frond ---------------- */
  const frondD = (x, y, ang, len, n, lw, curve, fat) => {
    const A = ang * RAD, ca = Math.cos(A), sa = Math.sin(A);
    const F = fat || 1;
    const R = (px, py) => [x + px * ca - py * sa, y + px * sa + py * ca];
    const p0 = [0, 0],
      p1 = [len * 0.34, -len * 0.07 * curve],
      p2 = [len * 0.7, len * 0.03 * curve],
      p3 = [len, -len * 0.015 * curve];
    const rp = [];
    for (let i = 0; i <= 8; i++) { const q = cpt(p0, p1, p2, p3, i / 8); rp.push(R(q[0], q[1])); }
    let a = '', b = '';
    for (let i = 0; i < n; i++) {
      for (let sd = 0; sd < 2; sd++) {
        const t = ((i + (sd ? 0.5 : 0)) / (n - 0.4)) * 0.9 + 0.07;
        if (t > 0.995) continue;
        const q = cpt(p0, p1, p2, p3, t);
        const ph = ctan(p0, p1, p2, p3, t);
        const shp = Math.pow(Math.sin(Math.PI * (0.2 + 0.72 * t)), 0.8);
        const jit = 1 + 0.15 * Math.sin(i * 2.7 + sd * 1.9);
        const L = len * lw * shp * jit * (F > 1 ? 1.03 : 1);
        const off = (42 - 20 * t + 5 * Math.sin(i * 1.6 + sd * 2.2)) * RAD;
        const bb = R(q[0], q[1]);
        const d = bladeD(bb[0], bb[1], A + ph + (sd ? off : -off), L, L * 0.17 * F, -L * 0.06);
        if (sd) b += d; else a += d;
      }
    }
    return { rachis: taper(rp, len * 0.009 * F, len * 0.0016 * F), a: a, b: b };
  };

  /* ---------------- fan palm (Livistona) ---------------- */
  const fanD = (hx, hy, a0, a1, n, R0, seed) => {
    let blades = '', pad = '', crease = '';
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const a = (a0 + (a1 - a0) * t) * RAD;
      const rr = R0 * (0.8 + 0.2 * Math.sin((i + seed) * 1.15) + 0.06 * Math.cos(i * 2.3));
      const wid = (Math.abs(a1 - a0) * RAD / n) * rr * 0.34;
      const bx = hx + Math.cos(a) * rr * 0.1, by = hy + Math.sin(a) * rr * 0.1;
      blades += bladeD(bx, by, a, rr * 0.92, wid, -rr * 0.05);
      pad += bladeD(bx, by, a, rr * 0.96, wid * 1.7, -rr * 0.05);
      crease += taper(
        [[bx, by],
        [hx + Math.cos(a) * rr * 0.5, hy + Math.sin(a) * rr * 0.5],
        [hx + Math.cos(a) * rr * 0.82, hy + Math.sin(a) * rr * 0.82]],
        wid * 0.16, 0.04
      );
    }
    return { blades: blades, pad: pad, crease: crease };
  };

  /* ---------------- banana / traveller's paddle ---------------- */
  const paddleD = (x, y, ang, len, hw, upN, dnN) => {
    const A = ang * RAD, ca = Math.cos(A), sa = Math.sin(A);
    const R = (px, py) => [x + px * ca - py * sa, y + px * sa + py * ca];
    const prof = (u) => Math.pow(Math.sin(Math.PI * Math.pow(Math.max(0, Math.min(1, u)), 0.62)), 0.58);
    const pts = [];
    const N = 30;
    for (let i = 0; i <= N; i++) {
      const u = i / N;
      pts.push(R(u * len, -hw * prof(u)));
      for (let k = 0; k < upN.length; k++) {
        const nu = upN[k];
        if (nu > u && nu <= (i + 1) / N) {
          pts.push(R(nu * len, -hw * prof(nu)));
          pts.push(R((nu + 0.014) * len, -hw * prof(nu) * 0.16));
          pts.push(R((nu + 0.034) * len, -hw * prof(nu + 0.034)));
        }
      }
    }
    for (let i = N; i >= 0; i--) {
      const u = i / N;
      pts.push(R(u * len, hw * 0.92 * prof(u)));
      for (let k = 0; k < dnN.length; k++) {
        const nu = dnN[k];
        if (nu < u && nu >= (i - 1) / N) {
          pts.push(R(nu * len, hw * 0.92 * prof(nu)));
          pts.push(R((nu - 0.014) * len, hw * 0.18 * prof(nu)));
          pts.push(R((nu - 0.034) * len, hw * 0.92 * prof(nu - 0.034)));
        }
      }
    }
    return 'M' + pts.map((p) => P(p[0], p[1])).join('L') + 'Z';
  };

  /* ---------------- monstera deliciosa ---------------- */
  const BLADE =
    'M3,-2C12,-15 24,-25 38,-30C42,-22 44,-18 46,-12C52,-20 56,-25 63,-27' +
    'C66,-20 68,-16 70,-11C76,-16 82,-16 88,-12C93,-9 97,-6 101,0' +
    'C97,7 92,13 86,18C80,14 76,12 72,10C74,18 72,25 66,29' +
    'C58,34 48,36 38,34C34,28 31,25 28,21C26,27 22,29 16,27' +
    'C10,24 5,14 2,5L6,1Z';
  // [cx, cy, rx, ry, rot]
  const SLOTS = [
    [20, -12, 10, 2.5, -48], [32, -18, 10.5, 2.7, -46], [56, -16, 9, 2.3, -42],
    [78, -7, 7, 1.8, -34], [90, -4, 4.5, 1.2, -26],
    [17, 13, 9.5, 2.4, 44], [36, 20, 12.5, 3, 38], [55, 19, 11, 2.6, 32], [76, 8, 6.5, 1.7, 24],
  ];
  const SLOTS_LITE = [
    [26, -15, 10.5, 2.7, -46], [58, -14, 8.5, 2.2, -40], [30, 17, 10.5, 2.7, 38], [62, 15, 8.5, 2.2, 30],
  ];
  const slotsD = (S) => { let d = ''; for (let i = 0; i < S.length; i++) d += ellD(S[i][0], S[i][1], S[i][2], S[i][3], S[i][4]); return d; };
  const veinsD = (S) => {
    let d = taper([[4, 0], [35, -0.3], [70, -0.5], [99, -0.35]], 0.95, 0.16);
    for (let i = 0; i < S.length; i++) {
      const s = S[i], a = s[4] * RAD;
      const ex = s[0] + Math.cos(a) * s[2] * 0.62, ey = s[1] + Math.sin(a) * s[2] * 0.62;
      const sx = Math.max(3, s[0] - Math.cos(a) * s[2] * 0.95);
      d += taper([[sx, 0], [(sx + ex) / 2, ey * 0.4], [ex, ey]], 0.4, 0.05);
    }
    return d;
  };

  /* ---------------- hibiscus ---------------- */
  const PET = [
    [0.18, -0.2, 0.42, -0.33, 0.6, -0.36], [0.7, -0.4, 0.79, -0.31, 0.85, -0.29],
    [0.95, -0.25, 1.02, -0.13, 1, -0.02], [1.02, 0.1, 0.95, 0.22, 0.85, 0.28],
    [0.75, 0.34, 0.66, 0.29, 0.57, 0.33], [0.39, 0.37, 0.17, 0.22, 0, 0],
  ];

  /* ---------------- shape list (draw order = z order) ---------------- */
  const sh = [];
  const add = (o) => sh.push(o);

  const putFrond = (x, y, ang, len, n, lw, curve, f1, f2, pad) => {
    if (pad) {
      const p = frondD(x, y, ang, len, n, lw, curve, 1.8);
      add({ d: p.a + p.b + p.rachis, f: CREAM });
    }
    const g = frondD(x, y, ang, len, n, lw, curve);
    add({ d: g.rachis, f: f2 });
    add({ d: g.a, f: f1 });
    add({ d: g.b, f: f2 });
  };
  const putMonstera = (x, y, rot, len, flip, f, key, S) => {
    const s = len / 100;
    const base = 'translate(' + rn(x) + ',' + rn(y) + ') rotate(' + rot + ') scale(' + rn(s) + ',' + rn(s * flip) + ')';
    const d = BLADE + slotsD(S);
    if (key) add({ d: d, f: CREAM, e: 1, t: base + ' translate(50,0) scale(1.035) translate(-50,0)' });
    add({ d: d, f: f, e: 1, t: base });
    if (fine && len > 45) add({ d: veinsD(S), f: mix(f === 'url(#tpHero)' ? G3 : f, '#000000', 0.26), o: 0.9, t: base });
  };
  const putFan = (hx, hy, a0, a1, n, R0, seed, f) => {
    const g = fanD(hx, hy, a0, a1, n, R0, seed);
    add({ d: g.pad, f: CREAM });
    add({ d: g.blades, f: f });
    if (fine) add({ d: g.crease, f: mix(f, '#000000', 0.18), o: 0.5 });
  };
  const putBloom = (cx, cy, R, rot) => {
    let pad = '', pet = '', heart = '', vein = '';
    for (let i = 0; i < 5; i++) {
      const a = (rot - i * 72 + 5 * Math.sin(i * 1.7)) * RAD;
      const ca = Math.cos(a), sa = Math.sin(a);
      const sc = R * (1 + 0.07 * Math.sin(i * 2.1));
      const T = (px, py, k) => P(cx + (px * ca - py * sa) * sc * k, cy + (px * sa + py * ca) * sc * k);
      const build = (k) => {
        let d = 'M' + T(0, 0, k);
        for (let j = 0; j < PET.length; j++) {
          const c = PET[j];
          d += 'C' + T(c[0], c[1], k) + ' ' + T(c[2], c[3], k) + ' ' + T(c[4], c[5], k);
        }
        return d + 'Z';
      };
      pad += build(1.075);
      pet += build(1);
      heart += build(0.34);
      for (let v = 0; v < 4; v++) {
        const th = (-21 + v * 14) * RAD;
        vein += 'M' + T(0.12, 0.12 * Math.tan(th), 1) + 'L' + T(0.82 * Math.cos(th), 0.82 * Math.sin(th), 1);
      }
    }
    add({ d: pad, f: CREAM });
    add({ d: pet, f: BL });
    add({ d: heart, f: BLL, o: 0.85 });
    if (fine) add({ d: vein, f: 'none', s: BLD, w: R * 0.02, o: 0.5 });
    const a2 = (rot + 26) * RAD, ca2 = Math.cos(a2), sa2 = Math.sin(a2);
    const CP = (u, v) => [cx + ca2 * R * u - sa2 * R * v, cy + sa2 * R * u + ca2 * R * v];
    add({ d: taper([CP(0.04, 0), CP(0.42, 0.03), CP(0.78, 0.02)], R * 0.058, R * 0.034), f: mix(BL, CREAM, 0.66) });
    let st = '';
    for (let i = 0; i < 10; i++) {
      const u = 0.36 + i * 0.046;
      const q = CP(u, (i % 2 ? 0.09 : -0.09) + 0.01 * Math.sin(i * 2.2));
      st += ellD(q[0], q[1], R * 0.043, R * 0.036, 0);
    }
    add({ d: st, f: GOLD });
    let dots = '';
    for (let i = 0; i < 5; i++) {
      const q = CP(0.82 + 0.04 * Math.cos(i * 2.4), (i - 2) * 0.068);
      dots += ellD(q[0], q[1], R * 0.072, R * 0.062, 0);
    }
    add({ d: dots, f: BLD });
  };
  const putBud = (x, y, ang, L) => {
    const A = ang * RAD;
    add({ d: bladeD(x, y, A, L, L * 0.3, -L * 0.14), f: BL });
    add({
      d: taper([[x + Math.cos(A) * L * 0.2, y + Math.sin(A) * L * 0.2],
      [x + Math.cos(A + 0.5) * L * 0.5, y + Math.sin(A + 0.5) * L * 0.5],
      [x + Math.cos(A) * L * 0.78, y + Math.sin(A) * L * 0.78]], L * 0.05, L * 0.02),
      f: BLD, o: 0.55,
    });
    add({ d: bladeD(x - Math.cos(A) * L * 0.12, y - Math.sin(A) * L * 0.12, A + 0.55, L * 0.42, L * 0.1, 0) + bladeD(x - Math.cos(A) * L * 0.12, y - Math.sin(A) * L * 0.12, A - 0.5, L * 0.38, L * 0.09, 0), f: G2 });
  };

  /* ================= composition ================= */
  add({ d: 'M0,0H300V400H0Z', f: CREAM });

  // ghost frond — the only translucent element
  {
    const g = frondD(2, 410, -55, 150, 9, 0.26, 1.2);
    add({ d: g.rachis, f: hexA(G1, 0.11) });
    add({ d: g.a + g.b, f: hexA(G1, 0.09) });
  }

  /* ---- Cluster A : top-left hero, origin (-65,-75) ---- */
  putFrond(-65, -75, 29, 244, 15, 0.235, 1, G1, mix(G1, G3, 0.22), 1);              // L1 feather
  add({ d: paddleD(-65, -75, 66, 220, 34, [0.3, 0.55, 0.78], [0.22, 0.42, 0.62, 0.82]), f: G1 });
  add({ d: taper([[-65, -75], [-42, -18], [-16, 47], [12, 120]], 1.1, 0.3), f: mix(G1, CREAM, 0.16), o: 0.4 });
  putMonstera(-16, -2, 57, 92, -1, G2, false, SLOTS);                             // L2 secondary
  putFan(-18, 52, 12, 100, 10, 76, 0.6, G4);                                      // L3 fan palm
  add({ d: taper([[-36, -40], [-14, -19], [9, 3]], 1.7, 0.8), f: mix(G3, '#000000', 0.12) });
  putMonstera(10, 4, 43, 134, 1, 'url(#tpHero)', true, SLOTS);                    // L4 hero
  putFrond(-65, -75, 57, 254, 13, 0.215, 1.1, G5, mix(G5, G3, 0.32), 1);          // L5 lime — type contact

  /* ---- Cluster B : bottom-right, origin (355,445), 0.82x ---- */
  putFrond(355, 445, 205, 176, 12, 0.235, 1, G1, mix(G1, G3, 0.22), 1);
  putFrond(355, 445, 224, 198, 13, 0.225, -1, mix(G2, G3, 0.5), mix(G1, G2, 0.7), 1);
  putMonstera(287, 371, 232, 66, 1, G2, false, SLOTS);
  putMonstera(266, 369, 220, 48, -1, G4, false, SLOTS_LITE);
  putFan(332, 414, 158, 254, 10, 72, 1.4, G4);
  add({ d: taper([[352, 442], [330, 425], [302, 406]], 1.5, 0.8), f: mix(G3, '#000000', 0.12) });
  putMonstera(301, 406, 213, 105, 1, G3, true, SLOTS);
  putMonstera(276, 338, 205, 36, -1, G4, false, SLOTS_LITE);
  putFrond(355, 445, 241, 166, 10, 0.2, 1.2, G5, mix(G5, G3, 0.32), 1);

  /* ---- Accent C : lone frond tip, right edge ---- */
  putFrond(305, 169, 151, 46, 7, 0.3, 1.3, G4, mix(G4, G3, 0.4), 1);

  /* ---- micro accents : seed dots ---- */
  add({ d: ellD(214, 353, 1.6, 1.4, 0) + ellD(220, 348, 1.3, 1.1, 0) + ellD(217, 359, 1.1, 1, 0), f: G1, o: 0.75 });

  /* ---- L6 blooms : always on a leaf, never on bare cream ---- */
  putBloom(56, 50, 17.5, 128);
  putBloom(23, 102, 12.5, 8);
  putBud(100, 31, 124, 13);
  putBloom(268, 369, 15.5, 300);
  putBud(249, 351, 202, 11);

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="tpHero" gradientUnits="userSpaceOnUse" x1="19" y1="-4" x2="109" y2="72">
          <stop offset="0" stopColor={mix(G3, '#000000', 0.08)} />
          <stop offset="0.55" stopColor={G3} />
          <stop offset="1" stopColor={mix(G3, '#FFFFFF', 0.07)} />
        </linearGradient>
        <filter id="tpGrain" x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="2.2" numOctaves="3" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
      </defs>
      {sh.map((s, i) => (
        <path
          key={i}
          d={s.d}
          fill={s.f || 'none'}
          fillRule={s.e ? 'evenodd' : 'nonzero'}
          opacity={s.o === undefined ? 1 : s.o}
          stroke={s.s || 'none'}
          strokeWidth={s.w || 0}
          strokeLinecap="round"
          transform={s.t}
        />
      ))}
      <rect
        x="0" y="0" width="300" height="400"
        filter="url(#tpGrain)"
        opacity={fine ? 0.05 : 0.03}
        style={{ mixBlendMode: 'multiply' }}
      />
    </svg>
  );
}

// ─── DiscoNeonGlow — disco-neon
function DiscoNeonGlow({ color, color2, accent, scale = 300 }) {
  const CX = 150;
  const CY = 86;
  const R = 56;
  const P = (R * 2) / 16; // 16 dot columns across the diameter

  const rnd = (n) => {
    const s = Math.sin(n * 12.9898 + 4.137) * 43758.5453;
    return s - Math.floor(s);
  };

  // four-point sparkle with concave arms, long axis ~2:1 to short
  const starPath = (L) => {
    const S = L * 0.46;
    const k = L * 0.09;
    return `M0 ${-L} Q ${k} ${-k} ${S} 0 Q ${k} ${k} 0 ${L} Q ${-k} ${k} ${-S} 0 Q ${-k} ${-k} 0 ${-L} Z`;
  };

  // halftone: the DOTS are the dark holes. Merged at the core, small at the rim.
  const holes = [];
  const facets = [];
  for (let row = 0; row < 16; row++) {
    for (let col = 0; col < 16; col++) {
      const x = CX - R + P / 2 + col * P;
      const y = CY - R + P / 2 + row * P;
      const t = Math.hypot(x - CX, y - CY) / R;
      if (t <= 1.05) {
        const ratio = 0.45 + 0.7 * Math.pow(Math.max(0, 1 - t), 0.85);
        holes.push({ id: `h${row}-${col}`, x, y, r: (ratio * P) / 2 });
      }
      // a few lattice cells catch the neon
      const fx = x + P / 2;
      const fy = y + P / 2;
      const ft = Math.hypot(fx - CX, fy - CY) / R;
      if (ft > 0.4 && ft < 0.95 && (row * 5 + col * 3) % 9 === 0) {
        const p = (row + col) % 3;
        facets.push({
          id: `f${row}-${col}`,
          x: fx,
          y: fy,
          s: P * (0.26 + 0.18 * rnd(row * 16 + col)),
          c: p === 0 ? accent : p === 1 ? color2 : color,
          o: 0.3 + 0.35 * rnd(col * 16 + row + 5),
        });
      }
    }
  }

  // 36 rays, 10 deg pitch, apex at the ball surface, widening outward
  const rays = [];
  for (let i = 0; i < 36; i++) {
    const j = rnd(i + 3);
    rays.push({
      id: `r${i}`,
      a: i * 10,
      len: R + 58 + j * 22,
      w: 1.15 + j * 0.9,
      o: 0.09 + rnd(i * 2 + 7) * 0.1,
      warm: i % 5 !== 0,
    });
  }

  const sparks = [
    [74, 46, 7.6, -14, 0.95, color],
    [232, 132, 6.9, 10, 0.9, accent],
    [40, 118, 6.1, 6, 0.78, color],
    [116, 22, 4.6, -6, 0.85, color2],
    [212, 58, 4.4, 12, 0.82, color],
    [58, 156, 4.2, -8, 0.66, accent],
    [258, 32, 3.7, 4, 0.7, color],
    [52, 178, 3.2, 10, 0.5, color2],
    [268, 96, 2.7, -4, 0.6, color],
    [30, 72, 2.4, 8, 0.55, color],
    [244, 192, 2.5, -10, 0.42, accent],
    [140, 12, 2.2, 6, 0.58, color],
    [26, 322, 4.0, -8, 0.44, accent],
    [274, 348, 3.2, 8, 0.4, color2],
    [64, 374, 2.3, 4, 0.36, color],
  ];

  const glints = [];
  for (let i = 0; i < 16; i++) {
    const a = rnd(i * 3 + 1);
    const b = rnd(i * 7 + 2);
    let gx = 10 + a * 280;
    const gy = i < 11 ? 4 + b * 66 : 292 + b * 100;
    if (Math.hypot(gx - CX, gy - CY) < R + 10) {
      gx = gx < CX ? gx - (R + 16) : gx + (R + 16);
      gx = Math.max(8, Math.min(292, gx));
    }
    glints.push({ id: `g${i}`, x: gx, y: gy, r: 0.45 + b * 0.6, o: 0.26 + a * 0.4 });
  }

  const beam = (deg) => (
    <polygon
      key={`bm${deg}`}
      points={`${CX - 5},${CY} ${CX + 5},${CY} ${CX + 36},430 ${CX - 36},430`}
      fill="url(#dnBeam)"
      transform={`rotate(${deg} ${CX} ${CY})`}
    />
  );

  return (
    <svg
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
    >
      <defs>
        <radialGradient id="dnBall" cx="0.40" cy="0.32" r="0.74">
          <stop offset="0" stopColor={hexA(color, 0.7)} />
          <stop offset="0.55" stopColor={hexA(color, 0.9)} />
          <stop offset="0.86" stopColor={hexA(color, 1)} />
          <stop offset="1" stopColor={hexA(color, 0.9)} />
        </radialGradient>
        <radialGradient id="dnGlow" gradientUnits="userSpaceOnUse" cx={CX} cy={CY + 6} r={R * 1.75}>
          <stop offset="0" stopColor={hexA(accent, 0.3)} />
          <stop offset="0.45" stopColor={hexA(accent, 0.15)} />
          <stop offset="1" stopColor={hexA(accent, 0)} />
        </radialGradient>
        <radialGradient id="dnGlow2" gradientUnits="userSpaceOnUse" cx={CX - 28} cy={CY - 32} r={R * 1.3}>
          <stop offset="0" stopColor={hexA(color2, 0.17)} />
          <stop offset="1" stopColor={hexA(color2, 0)} />
        </radialGradient>
        <radialGradient id="dnTintA" gradientUnits="userSpaceOnUse" cx={CX + 27} cy={CY + 30} r={R * 0.92}>
          <stop offset="0" stopColor={hexA(accent, 0.5)} />
          <stop offset="1" stopColor={hexA(accent, 0)} />
        </radialGradient>
        <radialGradient id="dnTintB" gradientUnits="userSpaceOnUse" cx={CX - 29} cy={CY - 27} r={R * 0.85}>
          <stop offset="0" stopColor={hexA(color2, 0.42)} />
          <stop offset="1" stopColor={hexA(color2, 0)} />
        </radialGradient>
        <radialGradient id="dnCore" gradientUnits="userSpaceOnUse" cx={CX} cy={CY} r={R * 0.34}>
          <stop offset="0" stopColor="#000000" stopOpacity="1" />
          <stop offset="0.68" stopColor="#000000" stopOpacity="1" />
          <stop offset="1" stopColor="#000000" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="dnRayW" gradientUnits="userSpaceOnUse" cx={CX} cy={CY} r={142}>
          <stop offset="0.3" stopColor={hexA(color, 0.9)} />
          <stop offset="0.66" stopColor={hexA(color, 0.8)} />
          <stop offset="1" stopColor={hexA(color, 0)} />
        </radialGradient>
        <radialGradient id="dnRayA" gradientUnits="userSpaceOnUse" cx={CX} cy={CY} r={142}>
          <stop offset="0.3" stopColor={hexA(accent, 0.9)} />
          <stop offset="0.66" stopColor={hexA(accent, 0.75)} />
          <stop offset="1" stopColor={hexA(accent, 0)} />
        </radialGradient>
        <linearGradient id="dnBeam" gradientUnits="userSpaceOnUse" x1={CX} y1={CY} x2={CX} y2={400}>
          <stop offset="0" stopColor={hexA(accent, 0.075)} />
          <stop offset="1" stopColor={hexA(accent, 0)} />
        </linearGradient>
        <linearGradient id="dnFloor" gradientUnits="userSpaceOnUse" x1="0" y1="292" x2="0" y2="400">
          <stop offset="0" stopColor={hexA(accent, 0)} />
          <stop offset="1" stopColor={hexA(accent, 0.09)} />
        </linearGradient>
        <radialGradient id="dnVig" cx="0.5" cy="0.44" r="0.78">
          <stop offset="0.5" stopColor={hexA(accent, 0)} />
          <stop offset="1" stopColor={hexA(accent, 0.055)} />
        </radialGradient>
        <clipPath id="dnClip">
          <circle cx={CX} cy={CY} r={R} />
        </clipPath>
        <filter id="dnSpeck" x="-6%" y="-6%" width="112%" height="112%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" seed="11" result="n" />
          <feColorMatrix
            in="n"
            type="matrix"
            values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  1.5 0 0 0 -0.6"
          />
        </filter>
      </defs>

      {/* base */}
      <rect x="0" y="0" width="300" height="400" fill="#000000" />
      <rect x="0" y="0" width="300" height="400" fill="url(#dnVig)" />
      <rect x="0" y="292" width="300" height="108" fill="url(#dnFloor)" />

      {/* light-beam wedges from behind the ball */}
      <g>{[-25, 25].map((d) => beam(d))}</g>

      {/* 36 radial rays - felt before they are seen */}
      <g>
        {rays.map((r) => (
          <polygon
            key={r.id}
            points={`${CX},${CY - R} ${CX - r.w / 2},${CY - r.len} ${CX + r.w / 2},${CY - r.len}`}
            fill={r.warm ? "url(#dnRayW)" : "url(#dnRayA)"}
            opacity={r.o}
            transform={`rotate(${r.a} ${CX} ${CY})`}
          />
        ))}
      </g>

      {/* soft neon bloom behind the ball only */}
      <circle cx={CX} cy={CY + 6} r={R * 1.75} fill="url(#dnGlow)" />
      <circle cx={CX - 28} cy={CY - 32} r={R * 1.3} fill="url(#dnGlow2)" />

      {/* hanging cord */}
      <line x1={CX} y1="0" x2={CX} y2={CY - R + 2} stroke={hexA(color, 0.42)} strokeWidth="1.5" />
      <line x1={CX} y1="0" x2={CX} y2={CY - R + 2} stroke={hexA(color, 0.85)} strokeWidth="0.7" />

      {/* mirror ball */}
      <circle cx={CX} cy={CY} r={R} fill="url(#dnBall)" />
      <g clipPath="url(#dnClip)">
        <rect x={CX - R} y={CY - R} width={R * 2} height={R * 2} fill="url(#dnTintB)" />
        <rect x={CX - R} y={CY - R} width={R * 2} height={R * 2} fill="url(#dnTintA)" />
        <rect
          x={CX - R}
          y={CY - R}
          width={R * 2}
          height={R * 2}
          fill="#ffffff"
          filter="url(#dnSpeck)"
          opacity="0.32"
        />
        {holes.map((h) => (
          <circle key={h.id} cx={h.x} cy={h.y} r={h.r} fill="#000000" />
        ))}
        <rect x={CX - R} y={CY - R} width={R * 2} height={R * 2} fill="url(#dnCore)" />
        {facets.map((f) => (
          <rect
            key={f.id}
            x={f.x - f.s / 2}
            y={f.y - f.s / 2}
            width={f.s}
            height={f.s}
            fill={f.c}
            opacity={f.o}
          />
        ))}
      </g>
      <circle cx={CX} cy={CY} r={R} fill="none" stroke={hexA(color, 0.5)} strokeWidth="0.6" />
      <path
        d={`M ${CX - R * 0.94} ${CY + R * 0.34} A ${R} ${R} 0 0 0 ${CX + R * 0.5} ${CY + R * 0.87}`}
        fill="none"
        stroke={hexA(accent, 0.55)}
        strokeWidth="1.1"
        strokeLinecap="round"
      />
      <path
        d={`M ${CX - R * 0.72} ${CY - R * 0.69} A ${R} ${R} 0 0 1 ${CX + R * 0.3} ${CY - R * 0.95}`}
        fill="none"
        stroke={hexA(color2, 0.42)}
        strokeWidth="0.9"
        strokeLinecap="round"
      />

      {/* glint dots in the voids */}
      <g>
        {glints.map((g) => (
          <circle key={g.id} cx={g.x} cy={g.y} r={g.r} fill={color} opacity={g.o} />
        ))}
      </g>

      {/* sparkles - asymmetric clusters, never a ring */}
      <g>
        {sparks.map((s, i) => (
          <g key={`s${i}`} transform={`translate(${s[0]} ${s[1]}) rotate(${s[3]})`} opacity={s[4]}>
            <path d={starPath(s[2])} fill={s[5]} />
            <path d={starPath(s[2] * 0.4)} fill={hexA("#FFFFFF", 0.8)} />
          </g>
        ))}
      </g>
    </svg>
  );
}

// ─── NeonTubeWall — neon-sign
function NeonTubeWall({ color, color2, accent, scale = 300 }) {
  const P = "nsw";
  const WALL = "#0a0a12";
  const hot = accent || "#ff3d9a";
  const cool = color2 || "#3fe0ff";
  const tint = color || "#241653";
  const amber = "#ffc857";
  const detail = scale >= 190;

  const rnd = (i, s) => {
    const v = Math.sin(i * 12.9898 + s * 78.233) * 43758.5453;
    return v - Math.floor(v);
  };

  const BOWL = "M 114 30 L 186 30 L 150 71 Z";
  const STEM = "M 150 71 L 150 100";
  const BASE = "M 124 99 Q 150 104 176 99";
  const OLIVE = "M 155.4 45 A 4.6 4.6 0 1 1 164.6 45 A 4.6 4.6 0 1 1 155.4 45 Z";
  const PICK = "M 170 34 L 157 47";
  const FLOUR =
    "M 44 348 C 76 370 110 371 138 352 C 154 341 149 323 133 327 C 117 331 121 355 145 363 C 175 373 214 364 246 342";
  const RAILL = "M 30 120 L 30 328";
  const RAILR = "M 270 120 L 270 328";
  const HEART =
    "M 0 7 C -8 1.4 -8 -6 -3.4 -6 C -1 -6 0 -4.2 0 -3 C 0 -4.2 1 -6 3.4 -6 C 8 -6 8 1.4 0 7 Z";

  const runs = [
    { k: "bowl", d: BOWL, c: hot, w: 5.2 },
    { k: "stem", d: STEM, c: hot, w: 5.2 },
    { k: "base", d: BASE, c: hot, w: 5.2 },
    { k: "pick", d: PICK, c: amber, w: 2.2, dim: 0.9 },
    { k: "oliv", d: OLIVE, c: amber, w: 2.6, dim: 0.9 },
    { k: "flou", d: FLOUR, c: cool, w: 4.6 },
    { k: "hrtl", d: HEART, c: hot, w: 3, t: "translate(30 97) scale(1.15)", dim: 0.85 },
    { k: "hrtr", d: HEART, c: hot, w: 3, t: "translate(270 97) scale(1.15)", dim: 0.85 },
    { k: "rall", d: RAILL, c: cool, w: 3, dim: 0.6 },
    { k: "ralr", d: RAILR, c: cool, w: 3, dim: 0.6 }
  ];

  const pins = [
    [132, 30, 0], [168, 30, 0],
    [128, 46, 49], [172, 46, -49],
    [150, 88, 90],
    [136, 100.8, 6], [164, 100.8, -6],
    [82, 362, 15], [205, 362, -17],
    [30, 152, 90], [30, 224, 90], [30, 296, 90],
    [270, 152, 90], [270, 224, 90], [270, 296, 90]
  ];

  const caps = [
    [124, 99, 191], [176, 99, -11],
    [44, 348, 214], [246, 342, -34],
    [30, 120, -90], [30, 328, 90],
    [270, 120, -90], [270, 328, 90]
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id={`${P}-tint`} cx="50%" cy="20%" r="82%">
          <stop offset="0" stopColor={hexA(tint, 0.55)} />
          <stop offset="0.55" stopColor={hexA(tint, 0.24)} />
          <stop offset="1" stopColor={hexA(tint, 0)} />
        </radialGradient>
        <radialGradient id={`${P}-poolA`} cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor={hexA(hot, 0.32)} />
          <stop offset="0.42" stopColor={hexA(hot, 0.12)} />
          <stop offset="1" stopColor={hexA(hot, 0)} />
        </radialGradient>
        <radialGradient id={`${P}-poolB`} cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor={hexA(cool, 0.3)} />
          <stop offset="0.44" stopColor={hexA(cool, 0.11)} />
          <stop offset="1" stopColor={hexA(cool, 0)} />
        </radialGradient>
        <radialGradient id={`${P}-spill`} cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor={hexA(cool, 0.26)} />
          <stop offset="0.4" stopColor={hexA(cool, 0.1)} />
          <stop offset="1" stopColor={hexA(cool, 0)} />
        </radialGradient>
        <radialGradient id={`${P}-vig`} cx="50%" cy="40%" r="78%">
          <stop offset="0" stopColor={hexA("#000000", 0)} />
          <stop offset="0.55" stopColor={hexA("#000000", 0.16)} />
          <stop offset="1" stopColor={hexA("#000000", 0.74)} />
        </radialGradient>
        <filter id={`${P}-grain`} x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="1" seed="9" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <filter
          id={`${P}-fb`}
          filterUnits="userSpaceOnUse"
          x="-70"
          y="-70"
          width="440"
          height="540"
        >
          <feGaussianBlur stdDeviation="7.5" />
        </filter>
        <filter
          id={`${P}-fg`}
          filterUnits="userSpaceOnUse"
          x="-70"
          y="-70"
          width="440"
          height="540"
        >
          <feGaussianBlur stdDeviation="2.4" />
        </filter>
      </defs>

      {/* --- the wall --- */}
      <rect x="0" y="0" width="300" height="400" fill={WALL} />
      <rect x="0" y="0" width="300" height="400" fill={`url(#${P}-tint)`} />
      <ellipse cx="150" cy="64" rx="132" ry="88" fill={`url(#${P}-poolA)`} />
      <ellipse cx="146" cy="352" rx="152" ry="78" fill={`url(#${P}-poolB)`} />
      <ellipse cx="30" cy="224" rx="58" ry="152" fill={`url(#${P}-spill)`} />
      <ellipse cx="270" cy="224" rx="58" ry="152" fill={`url(#${P}-spill)`} />
      <rect
        x="0"
        y="0"
        width="300"
        height="400"
        fill="#000000"
        filter={`url(#${P}-grain)`}
        opacity="0.055"
      />
      <rect x="0" y="0" width="300" height="400" fill={`url(#${P}-vig)`} />

      {/* --- light thrown back onto the wall (offset + soft) --- */}
      <g
        filter={`url(#${P}-fb)`}
        transform="translate(1.6 4)"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.6"
      >
        {runs.map((r) => (
          <g key={`bl-${r.k}`} transform={r.t}>
            <path
              d={r.d}
              stroke={r.c}
              strokeWidth={r.w * 2.9}
              opacity={0.5 * (r.dim || 1)}
            />
          </g>
        ))}
      </g>

      {/* --- the halo hugging the glass --- */}
      <g filter={`url(#${P}-fg)`} fill="none" strokeLinecap="round" strokeLinejoin="round">
        {runs.map((r) => (
          <g key={`gl-${r.k}`} transform={r.t}>
            <path
              d={r.d}
              stroke={r.c}
              strokeWidth={r.w * 1.75}
              opacity={0.6 * (r.dim || 1)}
            />
          </g>
        ))}
      </g>

      {/* --- the tube itself: glass wall, gas, white-hot core --- */}
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {runs.map((r) => (
          <g key={`tb-${r.k}`} transform={r.t} opacity={r.dim || 1}>
            <path d={r.d} stroke={hexA(r.c, 0.4)} strokeWidth={r.w + 1.9} />
            <path d={r.d} stroke={hexA(r.c, 0.95)} strokeWidth={r.w} />
            <path d={r.d} stroke={hexA("#ffffff", 0.48)} strokeWidth={r.w * 0.46} />
            <path
              d={r.d}
              stroke={hexA("#ffffff", 0.92)}
              strokeWidth={Math.max(0.75, r.w * 0.2)}
            />
          </g>
        ))}
      </g>

      {/* --- electrode housings + wire tails --- */}
      <g>
        {caps.map((c, i) => (
          <g key={`cp-${i}`} transform={`translate(${c[0]} ${c[1]}) rotate(${c[2]})`}>
            <path
              d="M 4.2 0 L 12.5 0"
              stroke={hexA("#ffffff", 0.13)}
              strokeWidth="1.1"
              strokeLinecap="round"
              fill="none"
            />
            <rect
              x="-4.3"
              y="-3.2"
              width="8.6"
              height="6.4"
              rx="3.2"
              fill={WALL}
              opacity="0.88"
              stroke={hexA("#ffffff", 0.2)}
              strokeWidth="0.7"
            />
            <circle cx="-0.8" cy="0" r="1.05" fill={hexA("#ffffff", 0.45)} />
          </g>
        ))}
      </g>

      {/* --- mounting pins holding the tube off the wall --- */}
      {detail && (
        <g>
          {pins.map((p, i) => (
            <g key={`pn-${i}`} transform={`translate(${p[0]} ${p[1]}) rotate(${p[2]})`}>
              <rect
                x="-1.6"
                y="-5.6"
                width="3.2"
                height="11.2"
                rx="1.6"
                fill={WALL}
                opacity="0.5"
              />
              <rect
                x="-0.85"
                y="-5.2"
                width="1.7"
                height="10.4"
                rx="0.85"
                fill={hexA("#ffffff", 0.28)}
              />
              <circle cx="0" cy="0" r="1.1" fill={hexA("#ffffff", 0.4)} />
              <circle cx="0" cy="0" r="0.45" fill={hexA("#ffffff", 0.75)} />
            </g>
          ))}
        </g>
      )}

      {/* --- dust catching the light, kept to the margins --- */}
      <g>
        {Array.from({ length: 16 }).map((_, i) => {
          const top = i % 2 === 0;
          const x = 6 + rnd(i, 1) * 288;
          const y = top ? 8 + rnd(i, 2) * 96 : 308 + rnd(i, 3) * 84;
          const r = 0.45 + rnd(i, 4) * 1;
          const c = i % 3 === 0 ? hot : i % 3 === 1 ? cool : "#ffffff";
          return (
            <circle
              key={`sp-${i}`}
              cx={x}
              cy={y}
              r={r}
              fill={hexA(c, 0.12 + rnd(i, 5) * 0.26)}
            />
          );
        })}
      </g>
    </svg>
  );
}

// ─── Y2KChenillePatch — y2k-patch
function Y2KChenillePatch({ color, color2, accent, scale = 300 }) {
  const P = "y2kchn";
  const ink = "#3A2A46";
  const cream = "#FFFCF4";
  const butter = "#FFDF93";
  const yolk = "#FFC64F";
  const fine = scale >= 200;
  const f = (n) => Math.round(n * 100) / 100;

  const starD = (R, ir) => {
    let p = "";
    for (let i = 0; i < 10; i++) {
      const a = ((-90 + i * 36) * Math.PI) / 180;
      const rad = i % 2 ? ir : R;
      p += (i ? "L" : "M") + f(Math.cos(a) * rad) + " " + f(Math.sin(a) * rad);
    }
    return p + "Z";
  };

  const heartD = (s) =>
    "M0 " + f(0.76 * s) +
    "C" + f(-0.62 * s) + " " + f(0.3 * s) + " " + f(-1 * s) + " " + f(-0.04 * s) + " " + f(-1 * s) + " " + f(-0.4 * s) +
    "C" + f(-1 * s) + " " + f(-0.82 * s) + " " + f(-0.52 * s) + " " + f(-0.98 * s) + " " + f(-0.26 * s) + " " + f(-0.7 * s) +
    "C" + f(-0.13 * s) + " " + f(-0.56 * s) + " " + f(-0.05 * s) + " " + f(-0.44 * s) + " 0 " + f(-0.32 * s) +
    "C" + f(0.05 * s) + " " + f(-0.44 * s) + " " + f(0.13 * s) + " " + f(-0.56 * s) + " " + f(0.26 * s) + " " + f(-0.7 * s) +
    "C" + f(0.52 * s) + " " + f(-0.98 * s) + " " + f(1 * s) + " " + f(-0.82 * s) + " " + f(1 * s) + " " + f(-0.4 * s) +
    "C" + f(1 * s) + " " + f(-0.04 * s) + " " + f(0.62 * s) + " " + f(0.3 * s) + " 0 " + f(0.76 * s) + "Z";

  const boltD = (s) =>
    "M" + f(0.2 * s) + " " + f(-1 * s) +
    "L" + f(-0.64 * s) + " " + f(0.1 * s) +
    "L" + f(-0.12 * s) + " " + f(0.1 * s) +
    "L" + f(-0.3 * s) + " " + f(1 * s) +
    "L" + f(0.64 * s) + " " + f(-0.14 * s) +
    "L" + f(0.1 * s) + " " + f(-0.14 * s) + "Z";

  const daisyD = (R, n) => {
    let p = "";
    for (let i = 0; i < n; i++) {
      const a = ((-90 + i * (360 / n)) * Math.PI) / 180;
      const cx = Math.cos(a) * R * 0.56;
      const cy = Math.sin(a) * R * 0.56;
      const pr = R * 0.45;
      p += "M" + f(cx - pr) + " " + f(cy) +
        "a" + f(pr) + " " + f(pr) + " 0 1 0 " + f(pr * 2) + " 0" +
        "a" + f(pr) + " " + f(pr) + " 0 1 0 " + f(-pr * 2) + " 0Z";
    }
    p += "M" + f(-R * 0.34) + " 0" +
      "a" + f(R * 0.34) + " " + f(R * 0.34) + " 0 1 0 " + f(R * 0.68) + " 0" +
      "a" + f(R * 0.34) + " " + f(R * 0.34) + " 0 1 0 " + f(-R * 0.68) + " 0Z";
    return p;
  };

  const circD = (r) =>
    "M" + f(-r) + " 0a" + f(r) + " " + f(r) + " 0 1 0 " + f(r * 2) + " 0a" + f(r) + " " + f(r) + " 0 1 0 " + f(-r * 2) + " 0Z";

  const shieldD = (w, h) => {
    const a = w / 2;
    const b = h / 2;
    return "M" + f(-a + 7) + " " + f(-b) + "L" + f(a - 7) + " " + f(-b) +
      "Q" + f(a) + " " + f(-b) + " " + f(a) + " " + f(-b + 7) +
      "L" + f(a) + " " + f(h * 0.08) +
      "C" + f(a) + " " + f(b * 0.96) + " " + f(w * 0.2) + " " + f(b) + " 0 " + f(b) +
      "C" + f(-w * 0.2) + " " + f(b) + " " + f(-a) + " " + f(b * 0.96) + " " + f(-a) + " " + f(h * 0.08) + "Z";
  };

  const patch = (k, d, o) => {
    const rim = o.rim == null ? 3 : o.rim;
    const yarn = "url(#" + P + "-" + (o.small ? "yarnb" : "yarna") + ")";
    return (
      <g key={k} transform={"translate(" + o.x + " " + o.y + ") rotate(" + (o.rot || 0) + ")"}>
        {o.small ? (
          <g transform="translate(0.8 2)" opacity="0.16">
            <path d={d} fill={ink} stroke={ink} strokeWidth={f(rim * 2)} strokeLinejoin="round" />
          </g>
        ) : (
          <g>
            <g transform="translate(1.5 3.8)" opacity="0.1">
              <path d={d} fill={ink} stroke={ink} strokeWidth={f(rim * 2 + 3.2)} strokeLinejoin="round" />
            </g>
            <g transform="translate(1.1 2.9)" opacity="0.14">
              <path d={d} fill={ink} stroke={ink} strokeWidth={f(rim * 2 + 1.4)} strokeLinejoin="round" />
            </g>
            <g transform="translate(0.7 2)" opacity="0.16">
              <path d={d} fill={ink} stroke={ink} strokeWidth={f(rim * 2)} strokeLinejoin="round" />
            </g>
          </g>
        )}
        <path d={d} fill={o.edge} stroke={o.edge} strokeWidth={f(rim * 2)} strokeLinejoin="round" />
        <path d={d} fill="none" stroke={hexA(ink, 0.3)} strokeWidth={f(rim * 2)} strokeDasharray={f(rim * 0.5) + " " + f(rim * 0.92)} strokeLinejoin="round" />
        <path d={d} fill="none" stroke={hexA("#FFFFFF", 0.55)} strokeWidth={f(rim * 2)} strokeDasharray={f(rim * 0.4) + " " + f(rim * 1.02)} strokeDashoffset={f(rim * 0.7)} strokeLinejoin="round" />
        <path d={d} fill={o.face} />
        <path d={d} fill={yarn} />
        <path d={d} fill={"url(#" + P + "-plush)"} />
        <path d={d} fill="none" stroke={hexA(ink, 0.22)} strokeWidth={o.small ? 0.7 : 0.95} />
        {o.kids}
      </g>
    );
  };

  const smiley = (k, x, y, r, rot, edge) =>
    patch(k, circD(r), {
      x: x, y: y, rot: rot, face: butter, edge: edge, rim: r < 14 ? 2.1 : 3, small: r < 14,
      kids: (
        <g>
          {fine ? (
            <g>
              <circle cx={f(-r * 0.54)} cy={f(r * 0.2)} r={f(r * 0.15)} fill={hexA(accent, 0.42)} />
              <circle cx={f(r * 0.54)} cy={f(r * 0.2)} r={f(r * 0.15)} fill={hexA(accent, 0.42)} />
            </g>
          ) : null}
          <g stroke={ink} strokeWidth={f(r * 0.17)} strokeLinecap="round" fill="none">
            <path d={"M" + f(-r * 0.36) + " " + f(-r * 0.36) + "V" + f(-r * 0.04)} />
            <path d={"M" + f(r * 0.36) + " " + f(-r * 0.36) + "V" + f(-r * 0.04)} />
            <path d={"M" + f(-r * 0.44) + " " + f(r * 0.16) + "Q0 " + f(r * 0.66) + " " + f(r * 0.44) + " " + f(r * 0.16)} />
          </g>
          {fine ? (
            <g stroke={hexA("#FFFFFF", 0.34)} strokeWidth={f(r * 0.05)} strokeLinecap="round" fill="none" transform={"translate(0 " + f(-r * 0.05) + ")"}>
              <path d={"M" + f(-r * 0.36) + " " + f(-r * 0.32) + "V" + f(-r * 0.08)} />
              <path d={"M" + f(r * 0.36) + " " + f(-r * 0.32) + "V" + f(-r * 0.08)} />
              <path d={"M" + f(-r * 0.4) + " " + f(r * 0.16) + "Q0 " + f(r * 0.58) + " " + f(r * 0.4) + " " + f(r * 0.16)} />
            </g>
          ) : null}
        </g>
      )
    });

  const daisy = (k, x, y, R, rot, edge) =>
    patch(k, daisyD(R, 6), {
      x: x, y: y, rot: rot, face: cream, edge: edge, rim: R < 14 ? 2.1 : 2.8, small: R < 14,
      kids: (
        <g>
          <circle r={f(R * 0.33)} fill={yolk} />
          <circle r={f(R * 0.33)} fill={"url(#" + P + "-yarnb)"} />
          <circle r={f(R * 0.33)} fill="none" stroke={hexA(ink, 0.28)} strokeWidth="0.8" />
          {fine ? <circle cx={f(-R * 0.1)} cy={f(-R * 0.11)} r={f(R * 0.12)} fill={hexA("#FFFFFF", 0.4)} /> : null}
        </g>
      )
    });

  const rules = [];
  for (let i = 0; i < 27; i++) rules.push(20 + i * 14);

  const tape = (k, x, y, w, h, rot) => {
    const d = "M0 0L" + w + " 0L" + f(w - 2.2) + " " + f(h * 0.32) + "L" + w + " " + f(h * 0.56) +
      "L" + f(w - 2.6) + " " + f(h * 0.8) + "L" + w + " " + h + "L0 " + h +
      "L2.4 " + f(h * 0.76) + "L0 " + f(h * 0.5) + "L2.1 " + f(h * 0.24) + "Z";
    return (
      <g key={k} transform={"translate(" + x + " " + y + ") rotate(" + rot + ")"} opacity="0.9">
        <path d={d} fill={hexA(color2, 0.48)} />
        <path d={d} fill={"url(#" + P + "-stripe)"} />
        <path d={d} fill="none" stroke={hexA("#FFFFFF", 0.5)} strokeWidth="0.8" />
      </g>
    );
  };

  const sideMarks = [
    { t: "h", x: 28, y: 144, s: 9.5, r: -12 },
    { t: "d", x: 29, y: 196, s: 11, r: 9 },
    { t: "s", x: 27, y: 246, s: 10, r: -16 },
    { t: "h", x: 30, y: 292, s: 8.5, r: 16 },
    { t: "d", x: 272, y: 150, s: 12, r: 12 },
    { t: "s", x: 271, y: 202, s: 10, r: -9 },
    { t: "h", x: 274, y: 250, s: 9, r: 14 },
    { t: "d", x: 272, y: 296, s: 11, r: -14 }
  ];

  const faces = [accent, color2, color, accent];
  const edges = [color2, accent, color2, color];

  const stitchMarks = [
    [40, 124], [259, 120], [39, 266], [260, 278], [20, 322], [283, 314], [16, 170], [287, 230]
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id={P + "-paper"} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0" stopColor="#FFFDF8" />
          <stop offset="0.5" stopColor="#FFF9F2" />
          <stop offset="1" stopColor="#FFF6EE" />
        </linearGradient>
        <radialGradient id={P + "-topwash"} cx="0.5" cy="0.02" r="0.72">
          <stop offset="0" stopColor={accent} stopOpacity="0.24" />
          <stop offset="1" stopColor={accent} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={P + "-botwash"} cx="0.5" cy="1" r="0.72">
          <stop offset="0" stopColor={color2} stopOpacity="0.24" />
          <stop offset="1" stopColor={color2} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={P + "-lift"} cx="0.5" cy="0.5" r="0.6">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.68" />
          <stop offset="0.65" stopColor="#FFFFFF" stopOpacity="0.26" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={P + "-plush"} cx="0.34" cy="0.26" r="0.86">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.44" />
          <stop offset="0.48" stopColor="#FFFFFF" stopOpacity="0.06" />
          <stop offset="1" stopColor="#2A1B33" stopOpacity="0.2" />
        </radialGradient>
        <linearGradient id={P + "-quietg"} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="0.22" stopColor="#FFFFFF" />
          <stop offset="0.27" stopColor="#141414" />
          <stop offset="0.77" stopColor="#141414" />
          <stop offset="0.82" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#FFFFFF" />
        </linearGradient>
        <mask id={P + "-quiet"}>
          <rect x="0" y="0" width="300" height="400" fill={"url(#" + P + "-quietg)"} />
        </mask>
        <pattern id={P + "-yarna"} width="4.6" height="4.6" patternUnits="userSpaceOnUse" patternTransform="rotate(-20)">
          <ellipse cx="1.15" cy="1.1" rx="1.34" ry="1.02" fill="#FFFFFF" opacity="0.5" />
          <ellipse cx="1.15" cy="2.24" rx="1.2" ry="0.6" fill="#2A1B33" opacity="0.13" />
          <ellipse cx="3.45" cy="3.4" rx="1.34" ry="1.02" fill="#FFFFFF" opacity="0.34" />
          <ellipse cx="3.45" cy="4.52" rx="1.2" ry="0.55" fill="#2A1B33" opacity="0.11" />
        </pattern>
        <pattern id={P + "-yarnb"} width="2.8" height="2.8" patternUnits="userSpaceOnUse" patternTransform="rotate(-20)">
          <ellipse cx="0.7" cy="0.66" rx="0.82" ry="0.62" fill="#FFFFFF" opacity="0.5" />
          <ellipse cx="0.7" cy="1.36" rx="0.74" ry="0.38" fill="#2A1B33" opacity="0.12" />
          <ellipse cx="2.1" cy="2.06" rx="0.82" ry="0.62" fill="#FFFFFF" opacity="0.34" />
          <ellipse cx="2.1" cy="2.72" rx="0.74" ry="0.34" fill="#2A1B33" opacity="0.1" />
        </pattern>
        <pattern id={P + "-stripe"} width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect x="0" y="0" width="3.3" height="7" fill={hexA(accent, 0.32)} />
        </pattern>
        <pattern id={P + "-tooth"} width="11" height="11" patternUnits="userSpaceOnUse" patternTransform="rotate(17)">
          <circle cx="1.8" cy="2.4" r="0.6" fill="#5C4A66" opacity="0.11" />
          <circle cx="7.3" cy="1.2" r="0.42" fill="#5C4A66" opacity="0.08" />
          <circle cx="4.4" cy="6.1" r="0.55" fill="#5C4A66" opacity="0.1" />
          <circle cx="9.4" cy="5.4" r="0.38" fill="#5C4A66" opacity="0.07" />
          <circle cx="2.3" cy="9.2" r="0.48" fill="#5C4A66" opacity="0.09" />
          <circle cx="7.9" cy="9.6" r="0.36" fill="#5C4A66" opacity="0.07" />
        </pattern>
      </defs>

      {/* ---- paper ground ---- */}
      <rect x="0" y="0" width="300" height="400" fill={"url(#" + P + "-paper)"} />
      <rect x="0" y="0" width="300" height="400" fill={hexA(color2, 0.09)} />
      <rect x="0" y="0" width="300" height="230" fill={"url(#" + P + "-topwash)"} />
      <rect x="0" y="170" width="300" height="230" fill={"url(#" + P + "-botwash)"} />

      {/* ---- ruled lines, fully quieted across the type band ---- */}
      <g mask={"url(#" + P + "-quiet)"}>
        {rules.map((y, i) => (
          <line key={"rl" + i} x1="14" x2="286" y1={y} y2={y} stroke={hexA(color2, 0.5)} strokeWidth="0.85" />
        ))}
      </g>

      {/* ---- soft lift so overlaid type sits clean ---- */}
      <rect x="24" y="94" width="252" height="232" fill={"url(#" + P + "-lift)"} />

      {/* ---- stitched card border ---- */}
      <rect x="8.5" y="8.5" width="283" height="383" rx="15" fill="none" stroke={hexA(accent, 0.5)} strokeWidth="1.5" strokeDasharray="5 4.6" strokeLinecap="round" />
      <rect x="12" y="12" width="276" height="376" rx="12" fill="none" stroke={hexA(ink, 0.09)} strokeWidth="0.8" />

      {/* ---- washi tape, under the patches ---- */}
      {tape("tp1", -20, 22, 92, 15, -15)}
      {tape("tp2", 196, 322, 122, 13, 10)}

      {/* ---- top cluster: everything resolves above y=105 ---- */}
      {patch("bolt-t", boltD(20), { x: 236, y: 48, rot: 11, face: color2, edge: accent, rim: 2.6 })}
      {patch("hrt-t1", heartD(10), { x: 106, y: 20, rot: -14, face: accent, edge: color2, rim: 2.2, small: true })}
      {patch("hrt-t2", heartD(8.5), { x: 200, y: 17, rot: 13, face: color2, edge: accent, rim: 2, small: true })}
      {patch("str-t1", starD(9, 3.9), { x: 24, y: 96, rot: -18, face: color, edge: accent, rim: 2.1, small: true })}
      {patch("str-t2", starD(8.5, 3.7), { x: 276, y: 97, rot: 14, face: accent, edge: color2, rim: 2.1, small: true })}
      {smiley("smi-t", 62, 58, 21, -8, accent)}
      {patch("shield", shieldD(72, 76), {
        x: 152, y: 50, rot: -5, face: color, edge: accent, rim: 3.4,
        kids: (
          <g>
            {patch("shield-star", starD(20, 8.7), { x: 0, y: -7, rot: 0, face: cream, edge: color2, rim: 2.6 })}
            {patch("shield-bar", "M-16 21h32a3.4 3.4 0 0 1 0 6.8h-32a3.4 3.4 0 0 1 0 -6.8Z", { x: 0, y: 0, rot: 0, face: color2, edge: cream, rim: 1.7, small: true })}
          </g>
        )
      })}

      {/* ---- bottom cluster: everything starts below y=315 ---- */}
      {daisy("dsy-b1", 46, 350, 23, -8, color2)}
      {daisy("dsy-b2", 100, 388, 16, 16, accent)}
      {smiley("smi-b", 152, 360, 19, 7, color2)}
      {patch("hrt-b", heartD(12), { x: 206, y: 386, rot: -10, face: accent, edge: color2, rim: 2.3, small: true })}
      {patch("bolt-b", boltD(21), { x: 250, y: 354, rot: 8, face: color, edge: accent, rim: 2.7 })}
      {patch("str-b", starD(12, 5.2), { x: 292, y: 388, rot: -14, face: color2, edge: accent, rim: 2.3, small: true })}

      {/* ---- side margins, clear of the x=55..245 type column ---- */}
      {sideMarks.map((m, i) => {
        const fc = faces[i % 4];
        const ec = edges[i % 4];
        if (m.t === "d") return daisy("sm" + i, m.x, m.y, m.s, m.r, ec);
        if (m.t === "h") return patch("sm" + i, heartD(m.s), { x: m.x, y: m.y, rot: m.r, face: fc, edge: ec, rim: 2.1, small: true });
        return patch("sm" + i, starD(m.s, m.s * 0.43), { x: m.x, y: m.y, rot: m.r, face: fc, edge: ec, rim: 2.1, small: true });
      })}

      {/* ---- hand cross-stitches in the margins ---- */}
      {stitchMarks.map((p, i) => (
        <g key={"xs" + i} transform={"translate(" + p[0] + " " + p[1] + ") rotate(" + (i * 17 - 30) + ")"} stroke={hexA(ink, 0.32)} strokeWidth="1.3" strokeLinecap="round">
          <line x1="-3.2" y1="-3.2" x2="3.2" y2="3.2" />
          <line x1="3.2" y1="-3.2" x2="-3.2" y2="3.2" />
        </g>
      ))}

      {/* ---- paper tooth: pattern, not a filter ---- */}
      {fine ? (
        <rect x="0" y="0" width="300" height="400" fill={"url(#" + P + "-tooth)"} opacity="0.55" style={{ mixBlendMode: "multiply" }} />
      ) : null}
    </svg>
  );
}

// ─── PoolTileCaustics — pool-tiles
function PoolTileCaustics({ color, color2, accent, scale = 300 }) {
  const P = "ptc";
  const neonA = accent;
  const neonB = color2;
  const deep = "#02313F";
  const boost = scale < 190 ? 1.28 : 1;
  const rnd = (n) => {
    const s = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
    return s - Math.floor(s);
  };
  const f = (v) => Math.round(v * 10) / 10;

  /* caustic net — every cell is parked in a margin band, never in the text box */
  const cellSpecs = [];
  for (let c = 0; c < 8; c++) {
    cellSpecs.push([-6 + c * 44, 44, 34, 19, 0.85, 11 + c * 7]);
    cellSpecs.push([16 + c * 44, 88, 32, 17, 0.78, 61 + c * 5]);
    cellSpecs.push([-6 + c * 44, 322, 32, 16, 0.8, 131 + c * 9]);
    cellSpecs.push([16 + c * 44, 360, 34, 17, 0.9, 191 + c * 3]);
  }
  for (let r = 0; r < 5; r++) {
    cellSpecs.push([18, 132 + r * 42, 20, 20, 0.62, 241 + r * 11]);
    cellSpecs.push([284, 126 + r * 42, 20, 20, 0.62, 301 + r * 13]);
  }
  const cells = cellSpecs.map((sp) => {
    const cx = sp[0], cy = sp[1], rx = sp[2], ry = sp[3], sd = sp[5], n = 11;
    const pts = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + (rnd(sd + i * 3) - 0.5) * 0.6;
      const k = 0.6 + rnd(sd * 5 + i * 11) * 0.7;
      pts.push([cx + Math.cos(a) * rx * k, cy + Math.sin(a) * ry * k]);
    }
    const mid = (i) => {
      const p = pts[i], q = pts[(i + 1) % n];
      return [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];
    };
    let d = "M" + f(mid(n - 1)[0]) + " " + f(mid(n - 1)[1]);
    for (let i = 0; i < n; i++) {
      const m = mid(i);
      d += " Q" + f(pts[i][0]) + " " + f(pts[i][1]) + " " + f(m[0]) + " " + f(m[1]);
    }
    return { id: "c" + sd, d: d + "Z", o: sp[4], hx: f(pts[2][0]), hy: f(pts[2][1]) };
  });

  /* surface ripple highlights, top and bottom bands only */
  const streaks = [];
  for (let i = 0; i < 15; i++) {
    const y = i < 8 ? 8 + i * 13 : 310 + (i - 8) * 11;
    let d = "M-16 " + f(y);
    for (let s = 0; s < 5; s++) {
      const x0 = -16 + s * 67;
      const a = (rnd(i * 7 + s * 13 + 1) - 0.5) * 12;
      const b = (rnd(i * 11 + s * 5 + 2) - 0.5) * 12;
      d += " C" + f(x0 + 22) + " " + f(y + a) + " " + f(x0 + 45) + " " + f(y + b) + " " + f(x0 + 67) + " " + f(y);
    }
    streaks.push({ id: "s" + i, d, o: f(0.06 + rnd(i * 3 + 5) * 0.12), w: f(0.8 + rnd(i * 9 + 4) * 1.2) });
  }

  /* mottled ceramic — tonal variance across the tile floor */
  const tiles = [];
  for (let r = 0; r < 16; r++) {
    for (let c = 0; c < 12; c++) {
      const h = rnd(r * 37 + c * 19 + 11);
      if (h < 0.46) continue;
      const light = h > 0.74;
      tiles.push(
        <rect
          key={"t" + r + "-" + c}
          x={c * 25 + 1}
          y={r * 25 + 1}
          width="23"
          height="23"
          fill={light ? hexA("#FFFFFF", f(0.05 + (h - 0.74) * 0.22)) : hexA(deep, f(0.05 + (0.74 - h) * 0.3))}
        />
      );
    }
  }

  /* waterline mosaic courses, top and bottom */
  const swatch = (i) => {
    const m = i % 7;
    if (m === 0) return hexA(neonA, 0.72);
    if (m === 1) return hexA("#FFFFFF", 0.2);
    if (m === 2) return hexA(neonB, 0.72);
    if (m === 3) return hexA(deep, 0.22);
    if (m === 4) return hexA(neonA, 0.34);
    if (m === 5) return hexA("#FFFFFF", 0.3);
    return hexA(neonB, 0.32);
  };
  const course = [];
  for (let r = 0; r < 2; r++) {
    for (let c = 0; c < 24; c++) {
      course.push(<rect key={"mt" + r + "-" + c} x={c * 12.5 + 0.7} y={r * 12.5 + 0.7} width="11.1" height="11.1" fill={swatch(c * 3 + r * 5)} />);
      course.push(<rect key={"mb" + r + "-" + c} x={c * 12.5 + 0.7} y={375.7 + r * 12.5} width="11.1" height="11.1" fill={swatch(c * 3 + r * 5 + 2)} />);
    }
  }

  const bubbles = [[16, 296, 5.5], [30, 270, 3.4], [12, 248, 6.6], [26, 228, 3], [18, 200, 4.4], [31, 172, 2.6], [14, 152, 3.8]];

  /* neon glass tubes — palm, pineapple, shades, beach ball, waves */
  const neon = [
    { id: "trunk", hue: neonA, w: 1.05, d: "M12 118 C18 102 22 82 39 59" },
    { id: "frond1", hue: neonA, d: "M40 58 Q66 32 96 38 Q68 45 40 58Z" },
    { id: "frond2", hue: neonA, d: "M40 58 Q64 52 88 72 Q62 61 40 58Z" },
    { id: "frond3", hue: neonA, d: "M40 58 Q31 34 44 17 Q47 39 40 58Z" },
    { id: "frond4", hue: neonA, d: "M40 58 Q14 39 -9 47 Q18 51 40 58Z" },
    { id: "frond5", hue: neonA, d: "M40 58 Q15 66 -1 88 Q25 71 40 58Z" },
    { id: "nut1", hue: neonB, w: 0.6, d: "M31 63 a3 3 0 1 0 6 0 a3 3 0 1 0 -6 0" },
    { id: "nut2", hue: neonB, w: 0.6, d: "M40 66 a3 3 0 1 0 6 0 a3 3 0 1 0 -6 0" },
    { id: "pine", hue: neonB, d: "M244 64 C244 51 276 51 276 64 C278 84 268 98 260 98 C252 98 242 84 244 64 Z" },
    { id: "crown1", hue: neonB, w: 0.8, d: "M254 54 C246 44 244 34 246 26" },
    { id: "crown2", hue: neonB, w: 0.8, d: "M258 52 C254 40 253 32 254 21" },
    { id: "crown3", hue: neonB, w: 0.8, d: "M261 51 C261 39 262 30 263 19" },
    { id: "crown4", hue: neonB, w: 0.8, d: "M264 52 C268 40 271 33 274 25" },
    { id: "crown5", hue: neonB, w: 0.8, d: "M267 54 C273 46 278 41 283 36" },
    { id: "hatchA", hue: neonB, w: 0.4, d: "M247 70 L271 86 M248 82 L266 95 M250 60 L274 75" },
    { id: "hatchB", hue: neonB, w: 0.4, d: "M273 70 L249 86 M272 82 L254 95 M270 60 L246 75" },
    { id: "lensL", hue: neonA, d: "M16 324 C16 317 44 317 44 324 C44 339 36 347 29 347 C21 347 16 334 16 324 Z" },
    { id: "lensR", hue: neonA, d: "M50 324 C50 317 78 317 78 324 C78 339 70 347 63 347 C55 347 50 334 50 324 Z" },
    { id: "bridge", hue: neonA, w: 0.75, d: "M44 325 C46 320 48 320 50 325" },
    { id: "armL", hue: neonA, w: 0.75, d: "M16 324 C10 319 6 317 1 316" },
    { id: "armR", hue: neonA, w: 0.75, d: "M78 324 C82 321 86 320 91 319" },
    { id: "ball", hue: neonB, d: "M226 340 a24 24 0 1 0 48 0 a24 24 0 1 0 -48 0" },
    { id: "seg1", hue: neonA, w: 0.75, d: "M250 316 C239 327 239 353 250 364" },
    { id: "seg2", hue: neonB, w: 0.75, d: "M250 316 C261 327 261 353 250 364" },
    { id: "seg3", hue: neonA, w: 0.55, d: "M250 316 L250 364" },
    { id: "swell", hue: neonB, w: 0.8, d: "M96 358 q11 -9 22 0 t22 0 t22 0 t22 0 t22 0" },
    { id: "wave1", hue: neonA, w: 0.75, d: "M260 176 q8 -7 16 0 t16 0 t16 0" },
    { id: "wave2", hue: neonB, w: 0.75, d: "M260 204 q8 -7 16 0 t16 0 t16 0" },
    { id: "wave3", hue: neonA, w: 0.75, d: "M260 232 q8 -7 16 0 t16 0 t16 0" }
  ];

  const star = (cx, cy, r) => {
    const w = r * 0.17;
    return "M" + cx + " " + (cy - r) +
      " C" + (cx + w) + " " + (cy - w) + " " + (cx + w) + " " + (cy - w) + " " + (cx + r) + " " + cy +
      " C" + (cx + w) + " " + (cy + w) + " " + (cx + w) + " " + (cy + w) + " " + cx + " " + (cy + r) +
      " C" + (cx - w) + " " + (cy + w) + " " + (cx - w) + " " + (cy + w) + " " + (cx - r) + " " + cy +
      " C" + (cx - w) + " " + (cy - w) + " " + (cx - w) + " " + (cy - w) + " " + cx + " " + (cy - r) + "Z";
  };
  const sparks = [[112, 40, 8.5, neonB], [206, 96, 6, neonA], [152, 74, 5, neonB], [32, 316, 6.5, neonA], [276, 302, 5.5, neonB], [116, 344, 4.5, neonA], [34, 210, 5, neonA], [22, 128, 4, neonB]]
    .map((s, i) => ({ id: "sp" + i, hue: s[3], d: star(s[0], s[1], s[2]), d2: star(s[0], s[1], s[2] * 0.44) }));

  const nw = (s, base) => f((s.w || 1) * base * boost);

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id={P + "-depth"} x1="0" y1="0" x2="26" y2="400" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={hexA("#FFFFFF", 0.14)} />
          <stop offset="0.1" stopColor={hexA("#FFFFFF", 0.04)} />
          <stop offset="0.26" stopColor={hexA(deep, 0.14)} />
          <stop offset="0.5" stopColor={hexA(deep, 0.32)} />
          <stop offset="0.74" stopColor={hexA(deep, 0.26)} />
          <stop offset="0.9" stopColor={hexA(deep, 0.05)} />
          <stop offset="1" stopColor={hexA("#FFFFFF", 0.12)} />
        </linearGradient>
        <radialGradient id={P + "-wash"} cx="150" cy="208" r="150" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={hexA(deep, 0.2)} />
          <stop offset="0.55" stopColor={hexA(deep, 0.13)} />
          <stop offset="1" stopColor={hexA(deep, 0)} />
        </radialGradient>
        <radialGradient id={P + "-vig"} cx="150" cy="200" r="238" gradientUnits="userSpaceOnUse">
          <stop offset="0.5" stopColor={hexA(deep, 0)} />
          <stop offset="1" stopColor={hexA(deep, 0.24)} />
        </radialGradient>
        <linearGradient id={P + "-fadeg"} x1="0" y1="0" x2="0" y2="400" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.17" stopColor="#e2e2e2" />
          <stop offset="0.26" stopColor="#3c3c3c" />
          <stop offset="0.78" stopColor="#3c3c3c" />
          <stop offset="0.86" stopColor="#dcdcdc" />
          <stop offset="1" stopColor="#ffffff" />
        </linearGradient>
        <mask id={P + "-fade"} maskUnits="userSpaceOnUse" x="0" y="0" width="300" height="400">
          <rect width="300" height="400" fill={"url(#" + P + "-fadeg)"} />
        </mask>
        <pattern id={P + "-grout"} width="25" height="25" patternUnits="userSpaceOnUse">
          <path d="M0 0.8 H25 M0.8 0 V25" stroke={hexA("#FFFFFF", 0.26)} strokeWidth="1.6" />
          <path d="M0 2.4 H25 M2.4 0 V25" stroke={hexA(deep, 0.2)} strokeWidth="1" />
        </pattern>
        <filter id={P + "-glow"} x="-70%" y="-70%" width="240%" height="240%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
        <filter id={P + "-hair"} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="0.7" />
        </filter>
        <filter id={P + "-soft"} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.4" />
        </filter>
        <filter id={P + "-bloom"} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="7" />
        </filter>
        <filter id={P + "-haze"} x="-70%" y="-70%" width="240%" height="240%">
          <feGaussianBlur stdDeviation="15" />
        </filter>
      </defs>

      <rect width="300" height="400" fill={color} />

      <g mask={"url(#" + P + "-fade)"}>
        {tiles}
        <rect width="300" height="400" fill={"url(#" + P + "-grout)"} />
      </g>

      <rect width="300" height="400" fill={"url(#" + P + "-depth)"} />
      <rect width="300" height="400" fill={"url(#" + P + "-wash)"} />

      <g>{course}</g>
      <rect x="0" y="25" width="300" height="1.6" fill={hexA("#FFFFFF", 0.3)} />
      <rect x="0" y="26.6" width="300" height="1.2" fill={hexA(deep, 0.32)} />
      <rect x="0" y="373.8" width="300" height="1.2" fill={hexA(deep, 0.32)} />
      <rect x="0" y="372.2" width="300" height="1.6" fill={hexA("#FFFFFF", 0.3)} />

      <g filter={"url(#" + P + "-haze)"}>
        <ellipse cx="128" cy="178" rx="64" ry="46" fill={hexA("#FFFFFF", 0.05)} />
        <ellipse cx="190" cy="256" rx="56" ry="40" fill={hexA("#FFFFFF", 0.04)} />
      </g>

      <g fill="none" stroke="#FFFFFF" strokeLinecap="round">
        {streaks.map((s) => (
          <path key={s.id} d={s.d} strokeOpacity={s.o} strokeWidth={s.w} />
        ))}
      </g>

      <g filter={"url(#" + P + "-bloom)"} fill="none" stroke="#FFFFFF" strokeWidth="12">
        {cells.map((c) => (
          <path key={c.id + "b"} d={c.d} strokeOpacity={f(0.19 * c.o)} />
        ))}
      </g>
      <g filter={"url(#" + P + "-soft)"} fill="none" stroke="#FFFFFF" strokeWidth="4.2">
        {cells.map((c) => (
          <path key={c.id + "m"} d={c.d} strokeOpacity={f(0.3 * c.o)} />
        ))}
      </g>
      <g filter={"url(#" + P + "-hair)"} fill="none" stroke="#FFFFFF" strokeWidth={f(1.3 * boost)}>
        {cells.map((c) => (
          <path key={c.id + "h"} d={c.d} strokeOpacity={f(0.3 * c.o)} />
        ))}
      </g>
      <g filter={"url(#" + P + "-soft)"} fill="#FFFFFF">
        {cells.map((c) => (
          <circle key={c.id + "d"} cx={c.hx} cy={c.hy} r="2.2" opacity={f(0.5 * c.o)} />
        ))}
      </g>

      <g fill={hexA("#FFFFFF", 0.07)} stroke={hexA("#FFFFFF", 0.45)} strokeWidth="1">
        {bubbles.map((b, i) => (
          <circle key={"bu" + i} cx={b[0]} cy={b[1]} r={b[2]} />
        ))}
      </g>

      <rect width="300" height="400" fill={"url(#" + P + "-vig)"} />

      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        <g filter={"url(#" + P + "-glow)"}>
          {neon.map((s) => (
            <path key={s.id + "g"} d={s.d} stroke={s.hue} strokeWidth={nw(s, 8.5)} strokeOpacity="0.78" />
          ))}
        </g>
        <g>
          {neon.map((s) => (
            <path key={s.id + "t"} d={s.d} stroke={s.hue} strokeWidth={nw(s, 3.2)} strokeOpacity="0.95" />
          ))}
        </g>
        <g>
          {neon.map((s) => (
            <path key={s.id + "c"} d={s.d} stroke="#FFFFFF" strokeWidth={nw(s, 1.1)} strokeOpacity="0.9" />
          ))}
        </g>
      </g>

      <g>
        <g filter={"url(#" + P + "-glow)"}>
          {sparks.map((s) => (
            <path key={s.id + "g"} d={s.d} fill={s.hue} opacity="0.7" />
          ))}
        </g>
        {sparks.map((s) => (
          <path key={s.id + "f"} d={s.d} fill={s.hue} opacity="0.92" />
        ))}
        {sparks.map((s) => (
          <path key={s.id + "w"} d={s.d2} fill="#FFFFFF" opacity="0.9" />
        ))}
      </g>
    </svg>
  );
}

// ─── GoldFoilNumberBalloons — foil-balloons
function GoldFoilNumberBalloons({ color, color2, accent, scale = 300 }) {
  const P = "gfnb";
  const fine = scale >= 220;
  const fr = (n) => n - Math.floor(n);

  const D3 = "M 13 25 C 19 13 47 12 47 28 C 47 39 35 44 28 45 C 36 45 50 49 50 61 C 50 77 21 81 12 69";
  const D0 = "M 30 14 C 40.5 14 49 28.3 49 46 C 49 63.7 40.5 78 30 78 C 19.5 78 11 63.7 11 46 C 11 28.3 19.5 14 30 14 Z";

  const digits = [
    { d: D3, x: 112, rot: -3.5, spec: [[19, 20, 4.6, 2.4, -28], [43, 63, 3.4, 1.8, 22], [30, 44, 2.0, 1.1, 6]] },
    { d: D0, x: 188, rot: 3.2, spec: [[16, 31, 4.4, 2.3, -38], [45, 60, 3.2, 1.7, 30], [30, 16, 3.0, 1.4, -6]] }
  ];
  const T = (dg, y, sx, sy) =>
    "translate(" + dg.x + " " + y + ") rotate(" + dg.rot + ") scale(" + sx + " " + sy + ") translate(-30 -46)";

  const conf = [];
  for (let i = 0; i < 42; i++) {
    const a = fr(Math.sin(i * 1.7) * 43.71);
    const b = fr(Math.cos(i * 2.31) * 57.13);
    const c = fr(Math.sin(i * 0.93 + 1.4) * 91.37);
    const z = i % 5;
    let cx, cy;
    if (z === 0 || z === 1) { cx = 6 + a * 288; cy = 3 + b * 104; }
    else if (z === 2) { cx = 2 + a * 50; cy = 110 + b * 186; }
    else if (z === 3) { cx = 247 + a * 51; cy = 110 + b * 186; }
    else { cx = 8 + a * 284; cy = 298 + b * 98; }
    conf.push({
      i: i, cx: cx, cy: cy,
      w: 2.0 + c * 3.6,
      h: 0.8 + fr(c * 3.1) * 1.5,
      rot: -80 + fr(a * 7.3) * 160,
      op: 0.2 + fr(b * 5.7) * 0.72,
      pale: i % 3 === 0,
      blur: i % 8 === 3,
      front: i % 4 === 1
    });
  }

  const flake = (p) => (
    <g
      key={"f" + p.i}
      transform={"translate(" + p.cx.toFixed(1) + " " + p.cy.toFixed(1) + ") rotate(" + p.rot.toFixed(1) + ")"}
      filter={p.blur ? "url(#" + P + "-soft)" : undefined}
    >
      <rect
        x={-p.w / 2} y={-p.h / 2} width={p.w} height={p.h} rx={p.h * 0.4}
        fill={p.pale ? hexA(accent, p.op) : hexA(color2, p.op)}
      />
      <rect
        x={-p.w / 2} y={-p.h / 2} width={p.w} height={p.h * 0.42} rx={p.h * 0.2}
        fill={hexA(accent, p.op * 0.55)}
      />
    </g>
  );

  const sparks = [[64, 26, 3.6, 0.85], [244, 42, 4.4, 0.9], [36, 90, 2.8, 0.6], [270, 100, 2.2, 0.5],
    [150, 12, 2.6, 0.6], [22, 322, 2.6, 0.55], [278, 338, 3.2, 0.6], [128, 372, 2.4, 0.45]];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id={P + "-body-0"} x1="0.05" y1="0" x2="0.92" y2="1">
          <stop offset="0" stopColor={hexA(color2, 0.45)} />
          <stop offset="0.06" stopColor={hexA(color2, 0.95)} />
          <stop offset="0.16" stopColor={hexA(accent, 0.98)} />
          <stop offset="0.195" stopColor={hexA(color2, 0.8)} />
          <stop offset="0.31" stopColor={hexA(color2, 0.4)} />
          <stop offset="0.4" stopColor={accent} />
          <stop offset="0.45" stopColor={hexA(accent, 0.92)} />
          <stop offset="0.485" stopColor={hexA(color2, 0.78)} />
          <stop offset="0.61" stopColor={hexA(color2, 0.3)} />
          <stop offset="0.72" stopColor={hexA(accent, 0.72)} />
          <stop offset="0.755" stopColor={hexA(color2, 0.62)} />
          <stop offset="0.89" stopColor={hexA(color2, 0.85)} />
          <stop offset="1" stopColor={hexA(color2, 0.28)} />
        </linearGradient>
        <linearGradient id={P + "-body-1"} x1="0.96" y1="0.04" x2="0.08" y2="1">
          <stop offset="0" stopColor={hexA(color2, 0.42)} />
          <stop offset="0.07" stopColor={hexA(color2, 0.92)} />
          <stop offset="0.185" stopColor={hexA(accent, 0.95)} />
          <stop offset="0.22" stopColor={hexA(color2, 0.78)} />
          <stop offset="0.34" stopColor={hexA(color2, 0.38)} />
          <stop offset="0.44" stopColor={accent} />
          <stop offset="0.485" stopColor={hexA(accent, 0.88)} />
          <stop offset="0.52" stopColor={hexA(color2, 0.74)} />
          <stop offset="0.64" stopColor={hexA(color2, 0.28)} />
          <stop offset="0.75" stopColor={hexA(accent, 0.68)} />
          <stop offset="0.785" stopColor={hexA(color2, 0.6)} />
          <stop offset="0.91" stopColor={hexA(color2, 0.82)} />
          <stop offset="1" stopColor={hexA(color2, 0.25)} />
        </linearGradient>
        <linearGradient id={P + "-rim"} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor={hexA(color2, 0.9)} />
          <stop offset="0.35" stopColor={hexA(color2, 0.5)} />
          <stop offset="0.75" stopColor={hexA(color, 0.7)} />
          <stop offset="1" stopColor={hexA(color, 0.95)} />
        </linearGradient>
        <linearGradient id={P + "-shade"} x1="0" y1="0" x2="0.25" y2="1">
          <stop offset="0" stopColor={hexA(color, 0)} />
          <stop offset="0.45" stopColor={hexA(color, 0.08)} />
          <stop offset="1" stopColor={hexA(color, 0.55)} />
        </linearGradient>
        <linearGradient id={P + "-puff"} x1="0.1" y1="0" x2="0.75" y2="1">
          <stop offset="0" stopColor={hexA(accent, 0.9)} />
          <stop offset="0.4" stopColor={hexA(accent, 0.3)} />
          <stop offset="1" stopColor={hexA(accent, 0)} />
        </linearGradient>
        <radialGradient id={P + "-hot"}>
          <stop offset="0" stopColor={hexA("#ffffff", 0.95)} />
          <stop offset="0.45" stopColor={hexA(accent, 0.6)} />
          <stop offset="1" stopColor={hexA(accent, 0)} />
        </radialGradient>
        <radialGradient id={P + "-halo"} cx="0.5" cy="0.5">
          <stop offset="0" stopColor={hexA(color2, 0.34)} />
          <stop offset="0.55" stopColor={hexA(color2, 0.11)} />
          <stop offset="1" stopColor={hexA(color2, 0)} />
        </radialGradient>
        <radialGradient id={P + "-pool"} cx="0.5" cy="0.5">
          <stop offset="0" stopColor={hexA(accent, 0.3)} />
          <stop offset="0.5" stopColor={hexA(color2, 0.13)} />
          <stop offset="1" stopColor={hexA(color2, 0)} />
        </radialGradient>
        <radialGradient id={P + "-vig"} cx="0.5" cy="0.34" r="0.78">
          <stop offset="0.45" stopColor={hexA(color, 0)} />
          <stop offset="1" stopColor={hexA(color, 0.85)} />
        </radialGradient>
        <linearGradient id={P + "-string"} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={hexA(accent, 0.6)} />
          <stop offset="0.35" stopColor={hexA(accent, 0.26)} />
          <stop offset="1" stopColor={hexA(accent, 0)} />
        </linearGradient>
        <linearGradient id={P + "-reflg"} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor={hexA(accent, 0.5)} />
          <stop offset="0.5" stopColor={hexA(color2, 0.5)} />
          <stop offset="1" stopColor={hexA(color2, 0.1)} />
        </linearGradient>
        <linearGradient id={P + "-floor"} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={hexA(accent, 0)} />
          <stop offset="0.5" stopColor={hexA(accent, 0.16)} />
          <stop offset="1" stopColor={hexA(accent, 0)} />
        </linearGradient>
        <linearGradient id={P + "-sheeng"} gradientUnits="userSpaceOnUse" x1="52" y1="0" x2="256" y2="124">
          <stop offset="0" stopColor="#000000" />
          <stop offset="0.3" stopColor="#000000" />
          <stop offset="0.395" stopColor="#ffffff" />
          <stop offset="0.46" stopColor="#0a0a0a" />
          <stop offset="0.72" stopColor="#000000" />
          <stop offset="0.8" stopColor="#5a5a5a" />
          <stop offset="0.9" stopColor="#000000" />
        </linearGradient>
        <mask id={P + "-sheen"} maskUnits="userSpaceOnUse" x="0" y="0" width="300" height="140">
          <rect x="0" y="0" width="300" height="140" fill={"url(#" + P + "-sheeng)"} />
        </mask>
        <filter id={P + "-soft"} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
        <filter id={P + "-spec"} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="1.2" />
        </filter>
        <filter id={P + "-glow"} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="7" />
        </filter>
        <filter id={P + "-refl"} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="6.5" />
        </filter>
      </defs>

      <rect x="0" y="0" width="300" height="400" fill={color} />
      <ellipse cx="150" cy="52" rx="168" ry="92" fill={"url(#" + P + "-halo)"} />

      <ellipse cx="150" cy="368" rx="128" ry="40" fill={"url(#" + P + "-pool)"} />
      <rect x="18" y="343.4" width="264" height="1.1" fill={"url(#" + P + "-floor)"} />
      <g opacity="0.3" filter={"url(#" + P + "-refl)"}>
        {digits.map((dg, i) => (
          <g key={"rf" + i} transform={T(dg, 366, 1.12, -0.4)}>
            <path d={dg.d} fill="none" stroke={"url(#" + P + "-reflg)"} strokeWidth="21" strokeLinecap="round" strokeLinejoin="round" />
          </g>
        ))}
      </g>

      <g>{conf.filter((p) => !p.front).map(flake)}</g>

      <g opacity="0.32" filter={"url(#" + P + "-glow)"}>
        <ellipse cx="112" cy="60" rx="40" ry="50" fill={hexA(color2, 0.38)} />
        <ellipse cx="188" cy="60" rx="40" ry="50" fill={hexA(color2, 0.38)} />
      </g>

      <g>
        <path d="M 94 97 C 92.5 112 90.5 126 88 141" fill="none" stroke={"url(#" + P + "-string)"} strokeWidth="0.7" strokeLinecap="round" />
        <path d="M 186 105 C 187.5 119 189 131 191 146" fill="none" stroke={"url(#" + P + "-string)"} strokeWidth="0.7" strokeLinecap="round" />
      </g>

      {digits.map((dg, i) => (
        <g key={"dg" + i} transform={T(dg, 60, 1.1, 1.1)}>
          <path d={dg.d} fill="none" stroke={"url(#" + P + "-rim)"} strokeWidth="24.4" strokeLinecap="round" strokeLinejoin="round" />
          <path
            d={dg.d} fill="none" stroke={hexA(color, 0.7)} strokeWidth="24.4" strokeLinecap="butt"
            strokeDasharray={fine ? "1 4.6" : "1.5 6"} opacity="0.5"
          />
          <path d={dg.d} fill="none" stroke={"url(#" + P + "-body-" + i + ")"} strokeWidth="21" strokeLinecap="round" strokeLinejoin="round" />
          <path d={dg.d} fill="none" stroke={"url(#" + P + "-shade)"} strokeWidth="21" strokeLinecap="round" strokeLinejoin="round" />
          <g transform="translate(-0.7 -1.6)">
            <path d={dg.d} fill="none" stroke={"url(#" + P + "-puff)"} strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" opacity="0.42" />
          </g>
          <g transform="translate(-1.2 -3.6)" opacity="0.34" filter={"url(#" + P + "-spec)"}>
            <path d={dg.d} fill="none" stroke={hexA(accent, 0.9)} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          <g transform="translate(1.1 5.2)" opacity="0.4">
            <path d={dg.d} fill="none" stroke={hexA(color2, 0.9)} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          <g transform="translate(0.8 4.2)" opacity="0.3">
            <path d={dg.d} fill="none" stroke={hexA(accent, 0.6)} strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          {dg.spec.map((s, k) => (
            <ellipse
              key={"sp" + k}
              cx={s[0]} cy={s[1]} rx={s[2]} ry={s[3]}
              transform={"rotate(" + s[4] + " " + s[0] + " " + s[1] + ")"}
              fill={"url(#" + P + "-hot)"} opacity="0.75" filter={"url(#" + P + "-spec)"}
            />
          ))}
        </g>
      ))}

      <g mask={"url(#" + P + "-sheen)"} opacity="0.3">
        {digits.map((dg, i) => (
          <g key={"sh" + i} transform={T(dg, 60, 1.1, 1.1)}>
            <path d={dg.d} fill="none" stroke={hexA(accent, 0.9)} strokeWidth="13" strokeLinecap="round" strokeLinejoin="round" />
          </g>
        ))}
      </g>

      <g>{conf.filter((p) => p.front).map(flake)}</g>

      <g>
        {sparks.map((s, i) => (
          <path
            key={"sk" + i}
            transform={"translate(" + s[0] + " " + s[1] + ") scale(" + s[2] + ")"}
            d="M 0 -1 Q 0.15 -0.15 1 0 Q 0.15 0.15 0 1 Q -0.15 0.15 -1 0 Q -0.15 -0.15 0 -1 Z"
            fill={hexA(accent, s[3])}
          />
        ))}
      </g>

      <rect x="0" y="0" width="300" height="400" fill={"url(#" + P + "-vig)"} />
    </svg>
  );
}

// ─── RacquetClubArch — racquet-club
function RacquetClubArch({ color, color2, accent, scale = 300 }) {
  const s = scale || 300;
  const hair = s < 200 ? 0.85 : 0.6;
  const rule = s < 200 ? 1.4 : 1.15;
  const heavy = s < 200 ? 2.2 : 1.8;

  const AX0 = 20;
  const AX1 = 280;
  const ASP = 152;
  const BASE = 352;

  const archD = `M${AX0} ${BASE} L${AX0} ${ASP} A130 130 0 0 1 ${AX1} ${ASP} L${AX1} ${BASE} Z`;
  const innerD = `M25 ${BASE} L25 ${ASP} A125 125 0 0 1 275 ${ASP} L275 ${BASE} Z`;
  const bladeD =
    "M-10.8 -40 L-13.2 -50 C-13.2 -58 -13.4 -68 -13.4 -74 C-13.4 -83 -8.6 -86.5 0 -86.5 C8.6 -86.5 13.4 -83 13.4 -74 C13.4 -68 13.2 -58 13.2 -50 L10.8 -40 Z";
  const bladeInD =
    "M-8.7 -43 L-10.6 -51.5 C-10.6 -58.5 -10.8 -68 -10.8 -74 C-10.8 -80.8 -6.9 -83.9 0 -83.9 C6.9 -83.9 10.8 -80.8 10.8 -74 C10.8 -68 10.6 -58.5 10.6 -51.5 L8.7 -43 Z";

  const stripes = Array.from({ length: 41 }, (_, i) => i * 7.5 - 1.4);
  const paddles = [
    { id: "pl", x: 166, rot: -42, dir: 1 },
    { id: "pr", x: 134, rot: 42, dir: -1 }
  ];
  const hatch = Array.from({ length: 10 }, (_, i) => ({
    id: i,
    x: -11 + i * 1.78,
    o: 0.3 - i * 0.028
  }));
  const wrap = Array.from({ length: 5 }, (_, i) => ({ id: i, y: -24 + i * 5.4 }));
  const holes = Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i + Math.PI / 6;
    return { id: i, x: 150 + 6.6 * Math.cos(a), y: 86.2 + 6.6 * Math.sin(a) };
  });

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="rqclub-wash" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color2} stopOpacity="0.09" />
          <stop offset="0.38" stopColor={color2} stopOpacity="0.028" />
          <stop offset="0.78" stopColor={color2} stopOpacity="0.032" />
          <stop offset="1" stopColor={color2} stopOpacity="0.075" />
        </linearGradient>
        <linearGradient id="rqclub-blade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color2} stopOpacity="0.02" />
          <stop offset="1" stopColor={color2} stopOpacity="0.12" />
        </linearGradient>
        <mask id="rqclub-outside" maskUnits="userSpaceOnUse" x="0" y="0" width="300" height="400">
          <rect x="0" y="0" width="300" height="400" fill="#fff" />
          <path d={archD} fill="#000" />
        </mask>
        <clipPath id="rqclub-foot">
          <rect x="0" y={BASE} width="300" height="48" />
        </clipPath>
        <clipPath id="rqclub-face">
          <path d={bladeInD} />
        </clipPath>
        <clipPath id="rqclub-ball">
          <circle cx="150" cy="86.2" r="11.4" />
        </clipPath>
        <filter id="rqclub-grain" x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="7" result="n" />
          <feColorMatrix
            in="n"
            type="matrix"
            values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0.24 0.18 0.12 0 0"
          />
        </filter>
      </defs>

      {/* cotton stock */}
      <rect x="0" y="0" width="300" height="400" fill={color} />

      {/* cabana awning — margins only, masked out of the arch window */}
      <g mask="url(#rqclub-outside)">
        {stripes.map((x, i) => (
          <g key={`st-${i}`}>
            <rect x={x} y="0" width="2.7" height={BASE} fill={hexA(color2, 0.16)} />
            <rect x={x + 4.3} y="0" width="0.9" height={BASE} fill={hexA(color2, 0.1)} />
          </g>
        ))}
        <rect x="0" y="0" width="300" height={BASE} fill={hexA(color, 0.12)} />
      </g>

      {/* arch window — flat wash, calm behind the type */}
      <path d={archD} fill="url(#rqclub-wash)" />

      {/* plinth */}
      <rect x="0" y={BASE} width="300" height="48" fill={color2} />
      <g clipPath="url(#rqclub-foot)">
        {stripes.map((x, i) => (
          <rect key={`ft-${i}`} x={x + 2} y={BASE} width="1.5" height="48" fill={hexA(color, 0.09)} />
        ))}
      </g>
      <line x1="0" y1={BASE + 5.5} x2="300" y2={BASE + 5.5} stroke={accent} strokeWidth={hair} opacity="0.55" />
      <line x1="28" y1="379" x2="136" y2="379" stroke={color} strokeWidth={hair} opacity="0.34" />
      <line x1="164" y1="379" x2="272" y2="379" stroke={color} strokeWidth={hair} opacity="0.34" />
      <path d="M150 372.4 L154.8 379 L150 385.6 L145.2 379 Z" fill={color} opacity="0.85" />
      <circle cx="150" cy="379" r="1.5" fill={color2} />

      {/* the arch, double-ruled */}
      <path d={archD} fill="none" stroke={color2} strokeWidth={heavy} opacity="0.92" />
      <path d={innerD} fill="none" stroke={color2} strokeWidth={hair} opacity="0.5" />
      <path d={innerD} fill="none" stroke={accent} strokeWidth={hair * 0.8} opacity="0.32" />

      {/* impost blocks at the springline */}
      {[AX0, AX1].map((x, i) => (
        <g key={`imp-${i}`}>
          <line x1={x - 9} y1={ASP} x2={x + 9} y2={ASP} stroke={color2} strokeWidth={rule} opacity="0.75" />
          <line x1={x - 9} y1={ASP + 3.4} x2={x + 9} y2={ASP + 3.4} stroke={color2} strokeWidth={hair} opacity="0.4" />
          <path
            d={`M${x} ${ASP - 8.4} L${x + 2.4} ${ASP - 6} L${x} ${ASP - 3.6} L${x - 2.4} ${ASP - 6} Z`}
            fill={accent}
            opacity="0.7"
          />
        </g>
      ))}

      {/* crest — paddles crossed in saltire, ball in front */}
      <g>
        {paddles.map((p) => (
          <g key={p.id} transform={`translate(${p.x} 104) rotate(${p.rot})`}>
            <path d="M-4.4 -27 L-11.2 -41 L11.2 -41 L4.4 -27 Z" fill={color2} opacity="0.9" />
            <rect x="-4.4" y="-30" width="8.8" height="32" rx="2.9" fill={color2} opacity="0.92" />
            <rect x="-6.1" y="-1.4" width="12.2" height="4.2" rx="1.6" fill={color2} opacity="0.92" />
            {wrap.map((w) => (
              <path
                key={`w-${p.id}-${w.id}`}
                d={`M-4.4 ${w.y} L4.4 ${w.y - 3.1}`}
                stroke={color}
                strokeWidth="0.75"
                opacity="0.4"
                fill="none"
              />
            ))}
            <path d={bladeD} fill={color} />
            <path d={bladeD} fill="url(#rqclub-blade)" />
            <g clipPath="url(#rqclub-face)">
              {hatch.map((h) => (
                <line
                  key={`h-${p.id}-${h.id}`}
                  x1={h.x * p.dir}
                  y1="-86"
                  x2={h.x * p.dir}
                  y2="-40"
                  stroke={color2}
                  strokeWidth="0.72"
                  opacity={h.o}
                />
              ))}
              <line
                x1={10 * p.dir}
                y1="-82"
                x2={10 * p.dir}
                y2="-44"
                stroke={color}
                strokeWidth="1.6"
                opacity="0.55"
              />
            </g>
            <path d={bladeInD} fill="none" stroke={color2} strokeWidth={hair} opacity="0.5" />
            <path d={bladeD} fill="none" stroke={color2} strokeWidth={rule * 1.25} opacity="0.95" />
          </g>
        ))}

        <circle cx="150" cy="86.2" r="13.4" fill={color} />
        <circle cx="150" cy="86.2" r="11.4" fill={color} stroke={color2} strokeWidth={rule * 1.25} opacity="0.95" />
        <g clipPath="url(#rqclub-ball)">
          <circle cx="157.5" cy="94" r="11.6" fill={hexA(color2, 0.11)} />
        </g>
        <circle cx="150" cy="86.2" r="8.9" fill="none" stroke={color2} strokeWidth={hair} opacity="0.22" />
        <circle cx="150" cy="86.2" r="1.95" fill={hexA(color2, 0.82)} />
        {holes.map((h) => (
          <circle key={`hl-${h.id}`} cx={h.x} cy={h.y} r="1.95" fill={hexA(color2, 0.8)} />
        ))}
      </g>

      {/* closing flourish, below the type block */}
      <line x1="100" y1="332" x2="138" y2="332" stroke={color2} strokeWidth={hair} opacity="0.38" />
      <line x1="162" y1="332" x2="200" y2="332" stroke={color2} strokeWidth={hair} opacity="0.38" />
      <path d="M150 327.4 L153.6 332 L150 336.6 L146.4 332 Z" fill="none" stroke={color2} strokeWidth={hair} opacity="0.55" />
      <circle cx="150" cy="332" r="1.1" fill={hexA(color2, 0.5)} />

      {/* paper tooth */}
      <rect
        x="0"
        y="0"
        width="300"
        height="400"
        filter="url(#rqclub-grain)"
        opacity="0.13"
        style={{ mixBlendMode: "multiply" }}
      />

      {/* engraved outer rule */}
      <rect x="7" y="7" width="286" height="386" fill="none" stroke={accent} strokeWidth={hair} opacity="0.5" />
      {[
        [7, 7],
        [293, 7],
        [7, 393],
        [293, 393]
      ].map((c, i) => (
        <path
          key={`cd-${i}`}
          d={`M${c[0]} ${c[1] - 3} L${c[0] + 3} ${c[1]} L${c[0]} ${c[1] + 3} L${c[0] - 3} ${c[1]} Z`}
          fill={accent}
          opacity="0.62"
        />
      ))}
    </svg>
  );
}

// ─── CivicMasthead — civic
function CivicMasthead({ color, color2, accent, scale = 300 }) {
  const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
  const lumOf = (h) => {
    let s = String(h).slice(1);
    if (s.length === 3) s = s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
    const r = parseInt(s.slice(0, 2), 16);
    const g = parseInt(s.slice(2, 4), 16);
    const b = parseInt(s.slice(4, 6), 16);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  };
  const pool = [color, color2, accent]
    .filter((c) => typeof c === "string" && HEX_RE.test(c))
    .sort((a, b) => lumOf(a) - lumOf(b));
  const lo = pool[0];
  const hi = pool[pool.length - 1];
  const ink = lo && lumOf(lo) < 0.55 ? lo : "#16294A";
  const paper = hi && lumOf(hi) > 0.74 ? hi : "#F8F7F2";
  const midPick = pool.filter((c) => c !== ink && c !== paper)[0];
  const brass =
    midPick && lumOf(midPick) > 0.3 && lumOf(midPick) < 0.86
      ? midPick
      : "#A98A4E";

  // defs ids must vary with the resolved palette, otherwise two cards of this
  // same component in one document share gradients and the second inherits the
  // first card's ink.
  const uid =
    "civicEd" + [ink, paper, brass].join("").replace(/[^0-9a-zA-Z]/g, "");
  const idStock = uid + "-stock";
  const idVig = uid + "-vig";
  const idCol = uid + "-col";

  const r2 = (n) => Math.round(n * 100) / 100;
  const hair = scale < 220 ? 0.78 : 0.6;
  const thin = (w) => (w < hair ? hair : w);

  const topLeader = Array.from({ length: 14 }, (_, i) => r2(46 + i * 5.6));
  const footLeader = Array.from({ length: 19 }, (_, i) => r2(46 + i * 5.6));
  const rosette = Array.from({ length: 24 }, (_, i) => {
    const a = (i * Math.PI) / 12;
    const cx = Math.cos(a);
    const cy = Math.sin(a);
    return {
      x1: r2(150 + cx * 18.6),
      y1: r2(68 + cy * 18.6),
      x2: r2(150 + cx * 21.4),
      y2: r2(68 + cy * 21.4),
    };
  });
  const colTicks = [156, 208, 260];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id={idStock} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={hexA(ink, 0.05)} />
          <stop offset="0.42" stopColor={hexA(ink, 0)} />
          <stop offset="1" stopColor={hexA(ink, 0.055)} />
        </linearGradient>
        <radialGradient id={idVig} cx="50%" cy="46%" r="74%">
          <stop offset="0.5" stopColor={hexA(ink, 0)} />
          <stop offset="1" stopColor={hexA(ink, 0.08)} />
        </radialGradient>
        <linearGradient
          id={idCol}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="118"
          x2="0"
          y2="300"
        >
          <stop offset="0" stopColor={hexA(ink, 0.62)} />
          <stop offset="0.55" stopColor={hexA(ink, 0.3)} />
          <stop offset="1" stopColor={hexA(ink, 0.1)} />
        </linearGradient>
      </defs>

      {/* paper stock */}
      <rect x="0" y="0" width="300" height="400" fill={paper} />
      <rect x="0" y="0" width="300" height="400" fill={"url(#" + idStock + ")"} />
      <rect x="0" y="0" width="300" height="400" fill={"url(#" + idVig + ")"} />

      {/* plate impression, just outside the frame */}
      <rect
        x="6.5"
        y="6.5"
        width="287"
        height="387"
        rx="13.5"
        fill="none"
        stroke={hexA(ink, 0.07)}
        strokeWidth="2.4"
      />

      {/* slim double border, softly rounded */}
      <rect
        x="12.6"
        y="12.6"
        width="274.8"
        height="374.8"
        rx="11"
        fill="none"
        stroke={hexA(ink, 0.12)}
        strokeWidth="1.3"
        transform="translate(0.5 1)"
      />
      <rect
        x="12.6"
        y="12.6"
        width="274.8"
        height="374.8"
        rx="11"
        fill="none"
        stroke={hexA(ink, 0.9)}
        strokeWidth="1.3"
      />
      <rect
        x="19"
        y="19"
        width="262"
        height="362"
        rx="6.5"
        fill="none"
        stroke={hexA(ink, 0.4)}
        strokeWidth={hair}
      />

      {/* masthead rules */}
      <line
        x1="46"
        y1="34"
        x2="254"
        y2="34"
        stroke={hexA(ink, 0.85)}
        strokeWidth="1.5"
      />
      <line
        x1="60"
        y1="38.6"
        x2="240"
        y2="38.6"
        stroke={hexA(ink, 0.45)}
        strokeWidth={hair}
      />

      {/* dotted leaders, broken by the seal */}
      {topLeader.map((x, i) => (
        <g key={uid + "-tl-" + i}>
          <circle cx={x} cy="68" r="0.8" fill={hexA(ink, 0.5)} />
          <circle cx={r2(300 - x)} cy="68" r="0.8" fill={hexA(ink, 0.5)} />
        </g>
      ))}

      {/* engraved seal */}
      {rosette.map((t, i) => (
        <line
          key={uid + "-rs-" + i}
          x1={t.x1}
          y1={t.y1}
          x2={t.x2}
          y2={t.y2}
          stroke={hexA(ink, i % 2 === 0 ? 0.38 : 0.2)}
          strokeWidth={hair}
          strokeLinecap="round"
        />
      ))}
      <circle
        cx="150"
        cy="68"
        r="17.4"
        fill="none"
        stroke={hexA(ink, 0.45)}
        strokeWidth={thin(0.7)}
      />
      <circle
        cx="150"
        cy="68"
        r="13.8"
        fill="none"
        stroke={hexA(ink, 0.25)}
        strokeWidth={hair}
      />
      <circle
        cx="150"
        cy="68"
        r="8.4"
        fill="none"
        stroke={hexA(brass, 0.85)}
        strokeWidth={thin(0.85)}
      />
      <circle cx="150" cy="68" r="5.2" fill={hexA(ink, 0.92)} />
      <circle cx="150" cy="66.4" r="1.5" fill={hexA(paper, 0.55)} />

      {/* drop rule into the title */}
      <line
        x1="150"
        y1="90"
        x2="150"
        y2="104"
        stroke={hexA(ink, 0.6)}
        strokeWidth="1.1"
      />
      <circle cx="150" cy="107.2" r="1.5" fill={hexA(brass, 0.95)} />

      {/* column rules flanking the text block */}
      <line
        x1="44"
        y1="119"
        x2="44"
        y2="297"
        stroke={"url(#" + idCol + ")"}
        strokeWidth={thin(0.95)}
      />
      <line
        x1="256"
        y1="119"
        x2="256"
        y2="297"
        stroke={"url(#" + idCol + ")"}
        strokeWidth={thin(0.95)}
      />
      <line
        x1="39.5"
        y1="119"
        x2="48.5"
        y2="119"
        stroke={hexA(ink, 0.62)}
        strokeWidth={thin(0.95)}
      />
      <line
        x1="251.5"
        y1="119"
        x2="260.5"
        y2="119"
        stroke={hexA(ink, 0.62)}
        strokeWidth={thin(0.95)}
      />
      <line
        x1="40.5"
        y1="297"
        x2="47.5"
        y2="297"
        stroke={hexA(ink, 0.22)}
        strokeWidth={thin(0.85)}
      />
      <line
        x1="252.5"
        y1="297"
        x2="259.5"
        y2="297"
        stroke={hexA(ink, 0.22)}
        strokeWidth={thin(0.85)}
      />
      {colTicks.map((y, i) => (
        <g key={uid + "-ct-" + i}>
          <line
            x1="38.6"
            y1={y}
            x2="44"
            y2={y}
            stroke={hexA(i === 0 ? brass : ink, i === 0 ? 0.7 : 0.28)}
            strokeWidth={hair}
          />
          <line
            x1="256"
            y1={y}
            x2="261.4"
            y2={y}
            stroke={hexA(i === 0 ? brass : ink, i === 0 ? 0.7 : 0.28)}
            strokeWidth={hair}
          />
        </g>
      ))}

      {/* footer leader, mirrored on the centre line */}
      {footLeader.map((x, i) => (
        <g key={uid + "-fl-" + i}>
          <circle cx={x} cy="316" r="0.75" fill={hexA(ink, 0.38)} />
          <circle cx={r2(300 - x)} cy="316" r="0.75" fill={hexA(ink, 0.38)} />
        </g>
      ))}

      {/* footer rules, mirrored from the masthead */}
      <line
        x1="60"
        y1="336"
        x2="240"
        y2="336"
        stroke={hexA(ink, 0.45)}
        strokeWidth={hair}
      />
      <line
        x1="46"
        y1="340.6"
        x2="254"
        y2="340.6"
        stroke={hexA(ink, 0.85)}
        strokeWidth="1.5"
      />

      {/* printer's ornament */}
      <line
        x1="100"
        y1="358"
        x2="138"
        y2="358"
        stroke={hexA(ink, 0.32)}
        strokeWidth={hair}
      />
      <line
        x1="162"
        y1="358"
        x2="200"
        y2="358"
        stroke={hexA(ink, 0.32)}
        strokeWidth={hair}
      />
      <path
        d="M150 354.4 L153.4 358 L150 361.6 L146.6 358 Z"
        fill={hexA(brass, 0.95)}
      />
      <circle cx="96.2" cy="358" r="0.9" fill={hexA(ink, 0.3)} />
      <circle cx="203.8" cy="358" r="0.9" fill={hexA(ink, 0.3)} />
    </svg>
  );
}

// ─── KidDoodleBalloons — kid-doodle
function KidDoodleBalloons({ color, color2, accent, scale = 300 }) {
  const uid = "kdbal";
  const ink = "#33302B";
  const hues = [color, color2, accent, "#F2B33D", "#57A863", "#8C6FC0"];
  const k = scale < 190 ? 1.2 : 1;
  const fine = scale >= 200;
  const n = (v) => Math.round(v * 10) / 10;
  const wob = (i, s) => {
    const v = Math.sin(i * 127.1 + s * 311.7) * 43758.5453;
    return (v - Math.floor(v)) * 2 - 1;
  };

  const balloon = (cx, cy, r, f, i) => {
    const ry = r * f;
    const w = (s) => wob(i, s) * (r * 0.14);
    const lw = 1 + wob(i, 61) * 0.07;
    const rw = 1 - wob(i, 62) * 0.07;
    return [
      "M", n(cx + w(1)), n(cy - ry),
      "C", n(cx + r * 0.62 * rw + w(2)), n(cy - ry * 0.98 + w(3)),
      n(cx + r * rw + w(4)), n(cy - ry * 0.38 + w(5)),
      n(cx + r * 0.96 * rw + w(6)), n(cy + ry * 0.14),
      "C", n(cx + r * 0.9 * rw + w(7)), n(cy + ry * 0.63 + w(8)),
      n(cx + r * 0.44 * rw + w(9)), n(cy + ry * 0.99),
      n(cx + w(10)), n(cy + ry * 1.02),
      "C", n(cx - r * 0.44 * lw + w(11)), n(cy + ry * 0.99),
      n(cx - r * 0.9 * lw + w(12)), n(cy + ry * 0.63 + w(13)),
      n(cx - r * 0.96 * lw + w(14)), n(cy + ry * 0.14),
      "C", n(cx - r * lw + w(15)), n(cy - ry * 0.38 + w(16)),
      n(cx - r * 0.62 * lw + w(17)), n(cy - ry * 0.98 + w(18)),
      n(cx + w(1)), n(cy - ry),
      "Z"
    ].join(" ");
  };

  const downStroke = (cx, cy, r, f, i) => {
    const ry = r * f;
    const w = (s) => wob(i, s) * (r * 0.14);
    const rw = 1 - wob(i, 62) * 0.07;
    return [
      "M", n(cx + w(1)), n(cy - ry),
      "C", n(cx + r * 0.62 * rw + w(2)), n(cy - ry * 0.98 + w(3)),
      n(cx + r * rw + w(4)), n(cy - ry * 0.38 + w(5)),
      n(cx + r * 0.96 * rw + w(6)), n(cy + ry * 0.14),
      "C", n(cx + r * 0.9 * rw + w(7)), n(cy + ry * 0.63 + w(8)),
      n(cx + r * 0.44 * rw + w(9)), n(cy + ry * 0.99),
      n(cx + w(10)), n(cy + ry * 1.02)
    ].join(" ");
  };

  const knot = (cx, cy, r, f, i) => {
    const y0 = cy + r * f * 1.0;
    return `M ${n(cx - r * 0.18)} ${n(y0)} L ${n(cx + r * 0.19)} ${n(y0 + 0.8)} L ${n(cx + wob(i, 21) * 1.5)} ${n(y0 + r * 0.34)} Z`;
  };

  const tail = (cx, cy, r, f, i, len, curl) => {
    const sy = cy + r * f * 1.02 + r * 0.33;
    const amp = 5 + wob(i, 31) * 3.2;
    let d =
      `M ${n(cx)} ${n(sy)} C ${n(cx + amp)} ${n(sy + len * 0.3)} ` +
      `${n(cx - amp * 0.95)} ${n(sy + len * 0.67)} ${n(cx + amp * 0.35)} ${n(sy + len)}`;
    if (curl) d += " a 3.2 3.2 0 0 1 4.4 3 a 2.1 2.1 0 0 1 -3.3 1.8";
    return d;
  };

  const squiggle = (x, y, amp, len, waves, i, horiz) => {
    let d = `M ${n(x)} ${n(y)}`;
    let used = 0;
    for (let s = 0; s < waves; s++) {
      const rem = waves - s;
      const seg =
        rem === 1 ? len - used : ((len - used) / rem) * (0.78 + Math.abs(wob(i, s + 40)) * 0.44);
      used += seg;
      const dir = s % 2 === 0 ? 1 : -1;
      const a = dir * amp * 2 * (0.7 + Math.abs(wob(i, s)) * 0.5);
      d += horiz
        ? ` q ${n(seg * 0.5)} ${n(a)} ${n(seg)} 0`
        : ` q ${n(a)} ${n(seg * 0.5)} 0 ${n(seg)}`;
    }
    return d;
  };

  const spark4 = (s) =>
    `M 0 ${n(-s)} C ${n(s * 0.12)} ${n(-s * 0.3)} ${n(s * 0.3)} ${n(-s * 0.12)} ${n(s)} 0 ` +
    `C ${n(s * 0.3)} ${n(s * 0.12)} ${n(s * 0.12)} ${n(s * 0.3)} 0 ${n(s)} ` +
    `C ${n(-s * 0.12)} ${n(s * 0.3)} ${n(-s * 0.3)} ${n(s * 0.12)} ${n(-s)} 0 ` +
    `C ${n(-s * 0.3)} ${n(-s * 0.12)} ${n(-s * 0.12)} ${n(-s * 0.3)} 0 ${n(-s)} Z`;

  const star5 = (s, i) => {
    let d = "";
    for (let p = 0; p < 10; p++) {
      const rr = (p % 2 === 0 ? s : s * 0.44) * (1 + wob(i, p + 7) * 0.13);
      const a = (Math.PI * p) / 5 - Math.PI / 2;
      d += `${p === 0 ? "M" : "L"} ${n(Math.cos(a) * rr)} ${n(Math.sin(a) * rr)} `;
    }
    return d + "Z";
  };

  const plus = (s, i) =>
    `M ${n(-s)} ${n(wob(i, 3) * 0.7)} L ${n(s * 0.94)} ${n(wob(i, 4) * 0.7)} ` +
    `M ${n(wob(i, 5) * 0.7)} ${n(-s * 0.94)} L ${n(wob(i, 6) * 0.7)} ${n(s)}`;

  const topB = [
    { cx: 16, cy: 34, r: 25, h: 0, g: -12, len: 24, c: 1 },
    { cx: 54, cy: 12, r: 15, h: 3, g: 9, len: 54, c: 1 },
    { cx: 88, cy: 40, r: 20, h: 1, g: -6, len: 24, c: 0 },
    { cx: 130, cy: 10, r: 23, h: 4, g: 13, len: 46, c: 1 },
    { cx: 172, cy: 44, r: 13, h: 2, g: -10, len: 22, c: 1 },
    { cx: 208, cy: 18, r: 21, h: 5, g: 7, len: 44, c: 1 },
    { cx: 250, cy: 42, r: 18, h: 1, g: -14, len: 24, c: 0 },
    { cx: 288, cy: 16, r: 22, h: 4, g: 10, len: 48, c: 1 }
  ];

  const botB = [
    { cx: 18, cy: 350, r: 24, h: 2, g: 11, len: 60, c: 0 },
    { cx: 64, cy: 378, r: 15, h: 5, g: -8, len: 30, c: 0 },
    { cx: 106, cy: 352, r: 20, h: 3, g: 6, len: 55, c: 1 },
    { cx: 152, cy: 382, r: 17, h: 0, g: -13, len: 26, c: 0 },
    { cx: 196, cy: 356, r: 22, h: 4, g: 9, len: 50, c: 1 },
    { cx: 244, cy: 384, r: 16, h: 1, g: -6, len: 24, c: 0 },
    { cx: 286, cy: 352, r: 21, h: 5, g: 12, len: 52, c: 1 }
  ];

  const sideB = [
    { cx: 28, cy: 238, r: 15, h: 4, g: -10, len: 24, c: 1 },
    { cx: 272, cy: 196, r: 14, h: 0, g: 12, len: 22, c: 1 }
  ];

  const streamers = [
    { d: squiggle(18, 120, 9, 172, 5, 71, false), h: 3, w: 3.4 },
    { d: squiggle(282, 124, 9, 168, 5, 72, false), h: 5, w: 3.4 },
    { d: squiggle(-8, 316, 7, 128, 3, 73, true), h: 1, w: 3.4 },
    { d: squiggle(186, 328, 7, 122, 3, 80, true), h: 2, w: 3.4 },
    { d: squiggle(40, 4, 6, 62, 3, 74, false), h: 4, w: 3.1 },
    { d: squiggle(158, 2, 5, 54, 3, 78, false), h: 0, w: 2.9 },
    { d: squiggle(230, 4, 6, 58, 3, 75, false), h: 2, w: 3.1 },
    { d: squiggle(84, 330, 6, 66, 3, 76, false), h: 0, w: 3.1 },
    { d: squiggle(224, 336, 6, 58, 3, 77, false), h: 4, w: 3.1 },
    { d: squiggle(140, 344, 5, 50, 3, 79, false), h: 5, w: 2.9 }
  ];

  const conf = [
    { x: 44, y: 82, t: 0, h: 3, s: 5.5, a: 24 },
    { x: 76, y: 94, t: 1, h: 1, s: 4.6, a: 0 },
    { x: 112, y: 8, t: 2, h: 4, s: 4, a: 0 },
    { x: 156, y: 74, t: 0, h: 5, s: 6, a: -32 },
    { x: 196, y: 90, t: 4, h: 2, s: 5.2, a: 12 },
    { x: 8, y: 92, t: 2, h: 1, s: 3.6, a: 0 },
    { x: 268, y: 92, t: 5, h: 4, s: 5, a: 0 },
    { x: 180, y: 102, t: 3, h: 3, s: 4.4, a: -14 },
    { x: 60, y: 60, t: 5, h: 5, s: 4.6, a: 0 },
    { x: 36, y: 128, t: 0, h: 4, s: 5, a: -20 },
    { x: 34, y: 152, t: 1, h: 2, s: 5, a: 0 },
    { x: 14, y: 188, t: 4, h: 5, s: 5.4, a: -18 },
    { x: 44, y: 200, t: 2, h: 0, s: 3.6, a: 0 },
    { x: 10, y: 246, t: 5, h: 2, s: 5, a: 0 },
    { x: 20, y: 290, t: 3, h: 4, s: 4.6, a: 20 },
    { x: 44, y: 272, t: 1, h: 1, s: 4.4, a: 0 },
    { x: 264, y: 132, t: 0, h: 2, s: 5.2, a: -26 },
    { x: 290, y: 164, t: 1, h: 5, s: 4.8, a: 0 },
    { x: 254, y: 210, t: 5, h: 4, s: 4.8, a: 0 },
    { x: 292, y: 222, t: 4, h: 3, s: 4.6, a: 14 },
    { x: 258, y: 254, t: 2, h: 3, s: 3.8, a: 0 },
    { x: 284, y: 280, t: 0, h: 1, s: 5.2, a: 26 },
    { x: 260, y: 294, t: 1, h: 4, s: 4.4, a: 0 },
    { x: 46, y: 336, t: 0, h: 1, s: 5.6, a: 28 },
    { x: 128, y: 322, t: 4, h: 4, s: 5, a: -12 },
    { x: 172, y: 334, t: 3, h: 2, s: 4.8, a: -22 },
    { x: 214, y: 324, t: 2, h: 5, s: 4, a: 0 },
    { x: 262, y: 338, t: 5, h: 3, s: 5.2, a: 0 },
    { x: 296, y: 322, t: 1, h: 0, s: 4.6, a: 0 },
    { x: 12, y: 322, t: 1, h: 5, s: 4.4, a: 0 },
    { x: 116, y: 394, t: 0, h: 0, s: 5.2, a: 40 },
    { x: 178, y: 312, t: 1, h: 3, s: 4, a: 0 }
  ];

  const drawBalloon = (b, i, tag) => {
    const j = i + tag;
    const col = hues[b.h % hues.length];
    const f = 1.08 + Math.abs(wob(j, 51)) * 0.17;
    const p = balloon(b.cx, b.cy, b.r, f, j);
    const kp = knot(b.cx, b.cy, b.r, f, j);
    const sw = (2.3 + Math.abs(wob(j, 52)) * 0.9) * k;
    return (
      <g key={`${tag}-${i}`} transform={`rotate(${b.g} ${b.cx} ${b.cy})`}>
        <path
          d={tail(b.cx, b.cy, b.r, f, j, b.len, b.c)}
          fill="none"
          stroke={hexA(ink, 0.6)}
          strokeWidth={1.7 * k}
          strokeLinecap="round"
        />
        <path
          d={p}
          fill={hexA(col, 0.94)}
          transform={`translate(${n(wob(j, 41) * 2.2)} ${n(wob(j, 42) * 2.2)})`}
        />
        <path d={kp} fill={hexA(col, 0.94)} />
        <path d={kp} fill="none" stroke={ink} strokeWidth={1.9 * k} strokeLinejoin="round" />
        {fine && (
          <path
            d={p}
            fill="none"
            stroke={hexA(ink, 0.22)}
            strokeWidth={1.3 * k}
            strokeLinejoin="round"
            transform="translate(1.4 -1.1)"
          />
        )}
        <path
          d={p}
          fill="none"
          stroke={hexA(ink, 0.92)}
          strokeWidth={sw}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <path
          d={downStroke(b.cx, b.cy, b.r, f, j)}
          fill="none"
          stroke={hexA(ink, 0.92)}
          strokeWidth={sw * 1.34}
          strokeLinecap="round"
        />
        <path
          d={`M ${n(b.cx - b.r * 0.5)} ${n(b.cy - b.r * 0.34)} q ${n(b.r * 0.08)} ${n(-b.r * 0.28)} ${n(b.r * 0.34)} ${n(-b.r * 0.4)}`}
          fill="none"
          stroke={hexA("#FFFFFF", 0.45)}
          strokeWidth={1.8 * k}
          strokeLinecap="round"
        />
      </g>
    );
  };

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id={`${uid}-halo`} cx="50%" cy="52%" r="60%">
          <stop offset="0%" stopColor={hexA("#FFFFFF", 0.58)} />
          <stop offset="55%" stopColor={hexA("#FFFFFF", 0.34)} />
          <stop offset="100%" stopColor={hexA("#FFFFFF", 0)} />
        </radialGradient>
        <filter id={`${uid}-grain`} x="0" y="0" width="100%" height="100%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.9"
            numOctaves="2"
            seed="7"
            stitchTiles="stitch"
          />
          <feColorMatrix type="saturate" values="0" />
        </filter>
      </defs>

      <rect x="0" y="0" width="300" height="400" fill={`url(#${uid}-halo)`} />

      {streamers.map((s, i) => (
        <g key={`st-${i}`}>
          <path
            d={s.d}
            fill="none"
            stroke={hexA(hues[s.h % hues.length], 0.28)}
            strokeWidth={s.w * k}
            strokeLinecap="round"
            transform="translate(1.8 -1.5)"
          />
          <path
            d={s.d}
            fill="none"
            stroke={hexA(hues[s.h % hues.length], 0.95)}
            strokeWidth={s.w * k}
            strokeLinecap="round"
          />
        </g>
      ))}

      {topB.map((b, i) => drawBalloon(b, i, 3))}
      {botB.map((b, i) => drawBalloon(b, i, 17))}
      {sideB.map((b, i) => drawBalloon(b, i, 29))}

      {conf.map((c, i) => {
        const col = hues[c.h % hues.length];
        const tr = `translate(${c.x} ${c.y}) rotate(${c.a})`;
        if (c.t === 0)
          return (
            <path
              key={`cf-${i}`}
              transform={tr}
              d={`M ${n(-c.s)} ${n(c.s * 0.35)} Q 0 ${n(-c.s * 0.15)} ${n(c.s)} ${n(-c.s * 0.35)}`}
              fill="none"
              stroke={hexA(col, 0.92)}
              strokeWidth={2.9 * k}
              strokeLinecap="round"
            />
          );
        if (c.t === 1)
          return <path key={`cf-${i}`} transform={tr} d={spark4(c.s)} fill={hexA(col, 0.92)} />;
        if (c.t === 2)
          return (
            <circle
              key={`cf-${i}`}
              cx={c.x}
              cy={c.y}
              r={c.s * 0.78}
              fill="none"
              stroke={hexA(col, 0.9)}
              strokeWidth={2.1 * k}
            />
          );
        if (c.t === 4)
          return (
            <path
              key={`cf-${i}`}
              transform={tr}
              d={star5(c.s, i)}
              fill={hexA(col, 0.9)}
              strokeLinejoin="round"
            />
          );
        if (c.t === 5)
          return (
            <path
              key={`cf-${i}`}
              transform={tr}
              d={plus(c.s, i)}
              fill="none"
              stroke={hexA(col, 0.9)}
              strokeWidth={2.2 * k}
              strokeLinecap="round"
            />
          );
        return (
          <path
            key={`cf-${i}`}
            transform={tr}
            d={`M 0 ${n(-c.s)} L ${n(c.s * 0.9)} ${n(c.s * 0.7)} L ${n(-c.s * 0.86)} ${n(c.s * 0.66)} Z`}
            fill={hexA(col, 0.9)}
            strokeLinejoin="round"
          />
        );
      })}

      {scale >= 170 && (
        <rect
          x="0"
          y="0"
          width="300"
          height="400"
          fill="#FFFFFF"
          filter={`url(#${uid}-grain)`}
          opacity="0.055"
        />
      )}
    </svg>
  );
}

// ─── DesertLastRodeo — western
function DesertLastRodeo({ color, color2, accent, scale = 300 }) {
  const fine = scale < 200 ? 1.4 : 1;

  // retro sunset banding: gaps thin near the horizon, dome solid at the top
  const stripes = [];
  let cur = 110;
  for (let i = 0; i < 5; i++) {
    cur -= 2 + i * 1.8;
    const gap = 3.2 - i * 0.2;
    cur -= gap;
    stripes.push({ i: i, y: cur, h: gap });
  }

  const rope =
    "M26,14 H274 A12,12 0 0 1 286,26 V374 A12,12 0 0 1 274,386 H26 A12,12 0 0 1 14,374 V26 A12,12 0 0 1 26,14 Z";
  const keyA =
    "M32,24 H268 A8,8 0 0 1 276,32 V368 A8,8 0 0 1 268,376 H32 A8,8 0 0 1 24,368 V32 A8,8 0 0 1 32,24 Z";
  const keyB =
    "M40,33 H260 A7,7 0 0 1 267,40 V360 A7,7 0 0 1 260,367 H40 A7,7 0 0 1 33,360 V40 A7,7 0 0 1 40,33 Z";

  const conchos = [
    [150, 24], [150, 376],
    [24, 150], [276, 150],
    [24, 200], [276, 200],
    [24, 250], [276, 250]
  ];

  const spark =
    "M0,-1 C0.16,-0.34 0.34,-0.16 1,0 C0.34,0.16 0.16,0.34 0,1 C-0.16,0.34 -0.34,0.16 -1,0 C-0.34,-0.16 -0.16,-0.34 0,-1 Z";
  const sparks = [
    [52, 46, 5, 0.4, 0],
    [250, 40, 6, 0.5, 1],
    [36, 72, 3.2, 0.3, 0],
    [266, 78, 3.6, 0.3, 0],
    [80, 316, 5.5, 0.7, 1],
    [220, 316, 5.5, 0.7, 1]
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="lrdz-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={hexA(accent, 0.24)} />
          <stop offset="0.55" stopColor={hexA(accent, 0.09)} />
          <stop offset="1" stopColor={hexA(color, 0.06)} />
        </linearGradient>
        <linearGradient id="lrdz-ground" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={hexA(color, 0.11)} />
          <stop offset="0.4" stopColor={hexA(color, 0.045)} />
          <stop offset="1" stopColor={hexA(color, 0.15)} />
        </linearGradient>
        <linearGradient id="lrdz-sun" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={hexA(color, 0.34)} />
          <stop offset="1" stopColor={hexA(color, 0.72)} />
        </linearGradient>
        <radialGradient id="lrdz-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor={hexA(color, 0.2)} />
          <stop offset="0.5" stopColor={hexA(color, 0.08)} />
          <stop offset="1" stopColor={hexA(color, 0)} />
        </radialGradient>
        <radialGradient id="lrdz-vig" cx="0.5" cy="0.46" r="0.72">
          <stop offset="0.5" stopColor={hexA(color, 0)} />
          <stop offset="1" stopColor={hexA(color, 0.17)} />
        </radialGradient>
        <mask id="lrdz-sunmask" maskUnits="userSpaceOnUse" x="90" y="50" width="120" height="60">
          <rect x="90" y="50" width="120" height="60" fill="#000" />
          <circle cx="150" cy="110" r="56" fill="#fff" />
          {stripes.map(function (s) {
            return (
              <rect
                key={"lrdz-st" + s.i}
                x="90"
                y={s.y}
                width="120"
                height={s.h}
                fill="#000"
              />
            );
          })}
        </mask>
        <filter id="lrdz-grain" x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" seed="11" result="n" />
          <feColorMatrix in="n" type="saturate" values="0" />
        </filter>
      </defs>

      <rect x="0" y="0" width="300" height="400" fill={color2} />
      <rect x="0" y="110" width="300" height="290" fill="url(#lrdz-ground)" />
      <rect x="0" y="0" width="300" height="110" fill="url(#lrdz-sky)" />

      <circle cx="150" cy="110" r="118" fill="url(#lrdz-glow)" />
      <rect x="90" y="50" width="120" height="60" fill="url(#lrdz-sun)" mask="url(#lrdz-sunmask)" />

      {sparks.map(function (s, i) {
        return (
          <path
            key={"lrdz-sp" + i}
            d={spark}
            transform={"translate(" + s[0] + "," + s[1] + ") scale(" + s[2] + ")"}
            fill={hexA(s[4] ? accent : color, s[3])}
          />
        );
      })}

      <path
        d="M0,110 L0,101 L26,94 L44,99 L70,91 L96,98 L124,90 L152,96 L182,88 L210,95 L238,87 L266,95 L300,89 L300,110 Z"
        fill={hexA(color, 0.18)}
      />
      <path d="M-6,110 L-6,88 L4,84 L9,79 L44,79 L50,84 L56,85 L62,95 L69,110 Z" fill={hexA(color, 0.9)} />
      <path d="M82,110 L86,80 L90,72 L99,70 L104,77 L107,110 Z" fill={hexA(color, 0.9)} />
      <path
        d="M306,110 L306,92 L296,88 L290,82 L252,82 L244,75 L230,72 L216,79 L208,90 L203,110 Z"
        fill={hexA(color, 0.9)}
      />
      <path d="M110,110 L112,106 L116,104 L121,107 L123,110 Z" fill={hexA(color, 0.72)} />
      <path d="M182,110 L185,105 L190,103 L194,107 L196,110 Z" fill={hexA(color, 0.72)} />

      <line x1="0" y1="110" x2="300" y2="110" stroke={hexA(color, 0.5)} strokeWidth={1.2 * fine} />
      <line x1="24" y1="113.6" x2="276" y2="113.6" stroke={hexA(color, 0.16)} strokeWidth={0.9 * fine} />

      <g transform="translate(15,33.5) scale(0.9)">
        <path
          d="M92,341 C104,345 116,348 132,349 C144,350 156,350 168,349 C184,348 196,345 208,341 C209,347 198,356 178,360 C160,364 140,364 122,360 C102,356 91,347 92,341 Z"
          fill={hexA(color, 0.93)}
        />
        <path
          d="M118,346 C117,330 120,316 128,309 C133,305 139,306 141,312 C144,317 156,317 159,312 C161,306 167,305 172,309 C180,316 183,330 182,346 Z"
          fill={hexA(color, 0.93)}
        />
        <path d="M118.2,346 L118.6,337.6 C130,341 170,341 181.4,337.6 L181.8,346 Z" fill={hexA(accent, 0.92)} />
        <rect x="144.5" y="337.4" width="11" height="6.4" rx="1.2" fill={hexA(color2, 0.9)} />
        <rect
          x="144.5"
          y="337.4"
          width="11"
          height="6.4"
          rx="1.2"
          fill="none"
          stroke={hexA(color, 0.55)}
          strokeWidth={0.8 * fine}
        />
        <path
          d="M97,347 C110,354 128,359 150,360.4 C172,359 190,354 203,347"
          fill="none"
          stroke={hexA(color2, 0.5)}
          strokeWidth={0.9 * fine}
          strokeDasharray="2.4 2.6"
        />
      </g>

      {[0, 1].map(function (m) {
        return (
          <g
            key={"lrdz-sc" + m}
            transform={m ? "translate(254,342) scale(-1,1)" : "translate(46,342)"}
          >
            <path d="M3,2 C11,8 21,9 29,5 C24,14 10,13 3,2 Z" fill={hexA(color, 0.42)} />
            <g fill="none" stroke={hexA(color, 0.6)} strokeWidth={1.1 * fine} strokeLinecap="round">
              <path d="M-14,3 C-8,3 -3,2 0,0 C13,1 22,-3 30,-11 C36,-17 46,-14 45,-6 C44,-0.5 36,1.5 33,-4" />
              <path d="M2,-5 C7,-10 14,-11 19,-8" />
            </g>
            <circle cx="12" cy="-9" r="1.5" fill={hexA(accent, 0.85)} />
          </g>
        );
      })}

      <path d={keyA} fill="none" stroke={hexA(color, 0.4)} strokeWidth={1 * fine} />
      <path
        d={keyB}
        fill="none"
        stroke={hexA(color, 0.28)}
        strokeWidth={0.85 * fine}
        strokeDasharray="2.6 3.4"
      />

      {conchos.map(function (c, i) {
        return (
          <g key={"lrdz-co" + i} transform={"translate(" + c[0] + "," + c[1] + ")"}>
            <path
              d="M0,-5.4 L4.2,0 L0,5.4 L-4.2,0 Z"
              fill={hexA(color, 0.5)}
              stroke={hexA(color, 0.78)}
              strokeWidth={0.7 * fine}
            />
            <circle r="1.5" fill={hexA(accent, 0.95)} />
          </g>
        );
      })}

      <g fill="none" strokeLinejoin="round" strokeLinecap="round">
        <path d="M134,389 C126,395 120,396 112,394" stroke={hexA(color, 0.75)} strokeWidth="4.4" />
        <path d="M166,389 C174,395 180,396 188,394" stroke={hexA(color, 0.75)} strokeWidth="4.4" />
        <path d="M134,389 C126,395 120,396 112,394" stroke={hexA(color2, 0.98)} strokeWidth="3" />
        <path d="M166,389 C174,395 180,396 188,394" stroke={hexA(color2, 0.98)} strokeWidth="3" />
        <path d="M134,389 C126,395 120,396 112,394" stroke={hexA(color, 0.16)} strokeWidth="3" />
        <path d="M166,389 C174,395 180,396 188,394" stroke={hexA(color, 0.16)} strokeWidth="3" />

        <path d={rope} stroke={hexA(color, 0.75)} strokeWidth="6.2" />
        <path d={rope} stroke={hexA(color2, 0.98)} strokeWidth="4.6" />
        <path d={rope} stroke={hexA(color, 0.15)} strokeWidth="4.6" />
        <path
          d={rope}
          stroke={hexA(color, 0.42)}
          strokeWidth="4.6"
          strokeDasharray="1.5 3.5"
          strokeLinecap="butt"
        />

        <ellipse cx="150" cy="386" rx="16" ry="8.5" stroke={hexA(color, 0.75)} strokeWidth="4.8" />
        <ellipse cx="150" cy="386" rx="16" ry="8.5" stroke={hexA(color2, 0.98)} strokeWidth="3.4" />
        <ellipse cx="150" cy="386" rx="16" ry="8.5" stroke={hexA(color, 0.15)} strokeWidth="3.4" />
        <ellipse
          cx="150"
          cy="386"
          rx="16"
          ry="8.5"
          stroke={hexA(color, 0.42)}
          strokeWidth="3.4"
          strokeDasharray="1.4 3.2"
          strokeLinecap="butt"
        />
      </g>

      <rect x="0" y="0" width="300" height="400" fill="url(#lrdz-vig)" />
      <rect
        x="0"
        y="0"
        width="300"
        height="400"
        filter="url(#lrdz-grain)"
        opacity="0.1"
        style={{ mixBlendMode: "multiply" }}
      />
    </svg>
  );
}

// ─── LiquidChromeY2K — chrome
function LiquidChromeY2K({ color, color2, accent, scale = 300 }) {
  const hair = scale < 200 ? 1.4 : 1;
  const cx = 150;
  const cy = -6;
  const R = 96;

  /* molten (non-circular) ring: deterministic radius wobble */
  const wob = (t) => 1 + 0.032 * Math.sin(3 * t + 0.6) + 0.022 * Math.cos(5 * t + 2.1);
  const onRing = (deg, rad) => {
    const t = (deg * Math.PI) / 180;
    const r = rad * wob(t);
    return [cx + r * Math.cos(t), cy + r * Math.sin(t)];
  };
  const ringPath = (rad) => {
    const n = 16;
    const p = [];
    for (let i = 0; i < n; i++) {
      const t = (i / n) * Math.PI * 2;
      const r = rad * wob(t);
      p.push([cx + r * Math.cos(t), cy + r * Math.sin(t)]);
    }
    const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const s = mid(p[n - 1], p[0]);
    let d = "M " + s[0].toFixed(2) + " " + s[1].toFixed(2);
    for (let i = 0; i < n; i++) {
      const m = mid(p[i], p[(i + 1) % n]);
      d +=
        " Q " + p[i][0].toFixed(2) + " " + p[i][1].toFixed(2) +
        " " + m[0].toFixed(2) + " " + m[1].toFixed(2);
    }
    return d + " Z";
  };

  const topEdge =
    "M -6 318 C 20 296, 46 300, 62 322 C 82 348, 118 340, 150 344 C 186 348, 214 334, 236 318 C 258 302, 286 296, 306 312";
  const poolPath = topEdge + " L 306 406 L -6 406 Z";

  const ribbons = [
    {
      k: "rib-l",
      d: "M 40 66 C 24 96, 40 148, 24 186 C 12 214, 36 250, 24 292 C 16 320, 32 334, 26 350",
      cx: 40,
      cy: 66,
    },
    {
      k: "rib-r",
      d: "M 260 66 C 276 96, 260 148, 276 186 C 288 214, 264 250, 276 292 C 284 320, 268 334, 274 350",
      cx: 260,
      cy: 66,
    },
  ];

  const specs = [
    { k: "sp0", a: 143, rx: 14, ry: 3.8, o: 0.95 },
    { k: "sp1", a: 166, rx: 8, ry: 2.4, o: 0.55 },
    { k: "sp2", a: 100, rx: 18, ry: 3.4, o: 0.8 },
    { k: "sp3", a: 30, rx: 10, ry: 3, o: 0.8 },
    { k: "sp4", a: 8, rx: 6, ry: 2, o: 0.45 },
  ];

  const beads = [
    { k: "bd0", x: 19, y: 134, r: 9.5 },
    { k: "bd1", x: 42, y: 198, r: 5.5 },
    { k: "bd2", x: 14, y: 258, r: 7 },
    { k: "bd3", x: 283, y: 142, r: 6.5 },
    { k: "bd4", x: 262, y: 208, r: 10 },
    { k: "bd5", x: 287, y: 272, r: 4.6 },
    { k: "bd6", x: 47, y: 92, r: 4.2 },
    { k: "bd7", x: 252, y: 86, r: 3.4 },
    { k: "bd8", x: 98, y: 322, r: 5.6 },
    { k: "bd9", x: 202, y: 317, r: 4 },
  ];

  const star = (x, y, s) =>
    "M " + x + " " + (y - s) +
    " Q " + (x + s * 0.15) + " " + (y - s * 0.15) + " " + (x + s) + " " + y +
    " Q " + (x + s * 0.15) + " " + (y + s * 0.15) + " " + x + " " + (y + s) +
    " Q " + (x - s * 0.15) + " " + (y + s * 0.15) + " " + (x - s) + " " + y +
    " Q " + (x - s * 0.15) + " " + (y - s * 0.15) + " " + x + " " + (y - s) + " Z";

  const sparks = [
    { k: "st0", x: 33, y: 112, s: 11 },
    { k: "st1", x: 273, y: 124, s: 8 },
    { k: "st2", x: 99, y: 93, s: 8.5 },
    { k: "st5", x: 214, y: 76, s: 6 },
    { k: "st3", x: 20, y: 292, s: 6.5 },
    { k: "st4", x: 288, y: 322, s: 9 },
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        {/* the reflected environment: sky -> hard horizon -> ground, banded */}
        <linearGradient
          id="lqc-chrome"
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="-120"
          x2="0"
          y2="110"
        >
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="0.45" stopColor="#F0F6FF" />
          <stop offset="0.522" stopColor="#DCE6F5" />
          <stop offset="0.60" stopColor="#9BABC3" />
          <stop offset="0.6045" stopColor="#0E131E" />
          <stop offset="0.70" stopColor="#2B3345" />
          <stop offset="0.7045" stopColor="#F2F7FF" />
          <stop offset="0.78" stopColor="#7A889F" />
          <stop offset="0.7845" stopColor={color2} />
          <stop offset="0.85" stopColor="#D2DEEE" />
          <stop offset="0.905" stopColor="#68768D" />
          <stop offset="0.9055" stopColor="#090D16" />
          <stop offset="0.955" stopColor="#4B5661" />
          <stop offset="0.9555" stopColor={accent} />
          <stop offset="1" stopColor="#8C9AB1" />
        </linearGradient>

        {/* light-direction sheen laid over the chrome bands */}
        <linearGradient id="lqc-sheen" x1="0.05" y1="0" x2="0.95" y2="1">
          <stop offset="0" stopColor={hexA("#FFFFFF", 0.8)} />
          <stop offset="0.18" stopColor={hexA("#FFFFFF", 0.1)} />
          <stop offset="0.42" stopColor={hexA("#FFFFFF", 0)} />
          <stop offset="0.63" stopColor={hexA("#05080F", 0.38)} />
          <stop offset="0.85" stopColor={hexA("#05080F", 0.04)} />
          <stop offset="1" stopColor={hexA("#FFFFFF", 0.45)} />
        </linearGradient>

        {/* cross-section of a liquid tube; repeats so every bend shades correctly */}
        <linearGradient
          id="lqc-chrome-h"
          gradientUnits="userSpaceOnUse"
          x1="4"
          y1="0"
          x2="32"
          y2="0"
          spreadMethod="repeat"
        >
          <stop offset="0" stopColor="#0B101B" />
          <stop offset="0.14" stopColor="#66738A" />
          <stop offset="0.22" stopColor="#F7FAFF" />
          <stop offset="0.32" stopColor="#B4C2D6" />
          <stop offset="0.4" stopColor="#4E5871" />
          <stop offset="0.405" stopColor="#080C15" />
          <stop offset="0.54" stopColor="#242C3C" />
          <stop offset="0.545" stopColor={color2} />
          <stop offset="0.66" stopColor="#DCE7F5" />
          <stop offset="0.76" stopColor="#6E7B93" />
          <stop offset="0.85" stopColor={accent} />
          <stop offset="0.9" stopColor="#161D2B" />
          <stop offset="1" stopColor="#0B101B" />
        </linearGradient>

        <linearGradient id="lqc-chrome-hi" x1="0" y1="0" x2="0.7" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="0.3" stopColor="#E6EFFA" />
          <stop offset="0.55" stopColor="#AFBFD5" />
          <stop offset="0.8" stopColor="#F4F8FF" />
          <stop offset="1" stopColor="#94A3BA" />
        </linearGradient>

        <linearGradient
          id="lqc-pool"
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="296"
          x2="0"
          y2="402"
        >
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="0.05" stopColor="#D2DFF0" />
          <stop offset="0.11" stopColor="#6D7B93" />
          <stop offset="0.115" stopColor="#0F141F" />
          <stop offset="0.25" stopColor="#394254" />
          <stop offset="0.255" stopColor={accent} />
          <stop offset="0.33" stopColor="#ECF3FD" />
          <stop offset="0.42" stopColor="#7A889F" />
          <stop offset="0.49" stopColor={color2} />
          <stop offset="0.55" stopColor="#161C29" />
          <stop offset="0.7" stopColor="#93A2B8" />
          <stop offset="0.78" stopColor="#080C15" />
          <stop offset="0.9" stopColor="#5F6C84" />
          <stop offset="1" stopColor="#0D121D" />
        </linearGradient>

        <linearGradient id="lqc-irid" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={accent} />
          <stop offset="0.3" stopColor="#EAF6FF" />
          <stop offset="0.55" stopColor={color2} />
          <stop offset="0.78" stopColor="#FFD9E8" />
          <stop offset="1" stopColor={accent} />
        </linearGradient>

        <radialGradient id="lqc-bead" cx="0.33" cy="0.26" r="0.86">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="0.16" stopColor="#DCE8F7" />
          <stop offset="0.38" stopColor="#8B9AB1" />
          <stop offset="0.42" stopColor="#1E2634" />
          <stop offset="0.7" stopColor="#3B4659" />
          <stop offset="0.86" stopColor={color2} />
          <stop offset="1" stopColor={accent} />
        </radialGradient>

        {/* inside of the arch: open, with light bouncing off the far wall */}
        <radialGradient id="lqc-hole" cx="0.5" cy="0.34" r="0.72">
          <stop offset="0" stopColor={hexA("#05080F", 0.18)} />
          <stop offset="0.66" stopColor={hexA("#05080F", 0.34)} />
          <stop offset="0.9" stopColor={hexA(color2, 0.3)} />
          <stop offset="1" stopColor={hexA(accent, 0.38)} />
        </radialGradient>

        <radialGradient id="lqc-sky" cx="0.5" cy="0.06" r="0.72">
          <stop offset="0" stopColor={hexA(color2, 0.3)} />
          <stop offset="0.45" stopColor={hexA(color2, 0.1)} />
          <stop offset="1" stopColor={hexA(color2, 0)} />
        </radialGradient>

        <radialGradient id="lqc-wash" cx="0.5" cy="0.52" r="0.62">
          <stop offset="0" stopColor={hexA(accent, 0.13)} />
          <stop offset="0.6" stopColor={hexA(accent, 0.045)} />
          <stop offset="1" stopColor={hexA(accent, 0)} />
        </radialGradient>

        <radialGradient id="lqc-vig" cx="0.5" cy="0.48" r="0.78">
          <stop offset="0.5" stopColor={hexA("#050810", 0)} />
          <stop offset="1" stopColor={hexA("#050810", 0.58)} />
        </radialGradient>

        <filter id="lqc-soft" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
        <filter id="lqc-soft2" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
        <filter id="lqc-grain" x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" seed="11" result="n" />
          <feColorMatrix in="n" type="saturate" values="0" />
        </filter>

        <clipPath id="lqc-poolclip">
          <path d={poolPath} />
        </clipPath>
      </defs>

      {/* ---------- ground ---------- */}
      <rect x="0" y="0" width="300" height="400" fill={color} />
      <rect x="0" y="0" width="300" height="400" fill="url(#lqc-sky)" />
      <rect x="0" y="0" width="300" height="400" fill="url(#lqc-wash)" />
      <ellipse cx="150" cy="60" rx="128" ry="64" fill={hexA(accent, 0.07)} filter="url(#lqc-soft)" />

      {/* ---------- TOP: molten chrome arch ---------- */}
      <g>
        <path
          d={ringPath(R)}
          fill="none"
          stroke={hexA(accent, 0.5)}
          strokeWidth="18"
          filter="url(#lqc-soft)"
          opacity="0.32"
        />
        <path d={ringPath(R - 13)} fill="url(#lqc-hole)" />
        <path d={ringPath(R)} fill="none" stroke="url(#lqc-chrome)" strokeWidth="26" />
        <path d={ringPath(R)} fill="none" stroke="url(#lqc-sheen)" strokeWidth="26" />
        <path
          d={ringPath(R + 9.4)}
          fill="none"
          stroke="url(#lqc-chrome-hi)"
          strokeWidth="3.4"
          opacity="0.8"
        />
        <path
          d={ringPath(R - 9.8)}
          fill="none"
          stroke={hexA("#05080F", 0.6)}
          strokeWidth="3.4"
        />
        <path
          d={ringPath(R + 13)}
          fill="none"
          stroke="url(#lqc-irid)"
          strokeWidth={1.5 * hair}
          opacity="0.9"
        />
        <path
          d={ringPath(R + 15)}
          fill="none"
          stroke={hexA(accent, 0.38)}
          strokeWidth={0.9 * hair}
        />
        <path
          d={ringPath(R - 13)}
          fill="none"
          stroke="url(#lqc-irid)"
          strokeWidth={1.2 * hair}
          opacity="0.55"
        />
        {specs.map((s) => {
          const p = onRing(s.a, R + 1);
          return (
            <g
              key={s.k}
              transform={"translate(" + p[0].toFixed(2) + "," + p[1].toFixed(2) + ") rotate(" + (s.a + 90) + ")"}
            >
              <ellipse rx={s.rx} ry={s.ry} fill="#FFFFFF" opacity={s.o * 0.5} filter="url(#lqc-soft2)" />
              <ellipse rx={s.rx * 0.5} ry={s.ry * 0.45} fill="#FFFFFF" opacity={s.o} />
            </g>
          );
        })}
      </g>

      {/* ---------- SIDES: liquid chrome rails ---------- */}
      {ribbons.map((r) => (
        <g key={r.k}>
          <path
            d={r.d}
            fill="none"
            stroke={hexA(accent, 0.3)}
            strokeWidth="16"
            strokeLinecap="round"
            filter="url(#lqc-soft)"
            opacity="0.45"
          />
          <path d={r.d} fill="none" stroke="url(#lqc-chrome-h)" strokeWidth="13" strokeLinecap="round" />
          <path
            d={r.d}
            fill="none"
            stroke={hexA("#FFFFFF", 0.55)}
            strokeWidth={1.8 * hair}
            strokeLinecap="round"
            transform="translate(-2.8,0)"
          />
          <path
            d={r.d}
            fill="none"
            stroke={hexA("#05080F", 0.62)}
            strokeWidth={2.2 * hair}
            strokeLinecap="round"
            transform="translate(3,0)"
          />
          <path
            d={r.d}
            fill="none"
            stroke="url(#lqc-irid)"
            strokeWidth={1.2 * hair}
            strokeLinecap="round"
            opacity="0.8"
            transform="translate(4.6,0)"
          />
          <circle cx={r.cx} cy={r.cy} r="8.5" fill="url(#lqc-bead)" />
          <ellipse
            cx={r.cx - 2.8}
            cy={r.cy - 3.4}
            rx="2.6"
            ry="1.6"
            fill="#FFFFFF"
            opacity="0.95"
            transform={"rotate(-28," + (r.cx - 2.8) + "," + (r.cy - 3.4) + ")"}
          />
        </g>
      ))}

      {/* ---------- BOTTOM: molten pool ---------- */}
      <g>
        <path d={poolPath} fill="url(#lqc-pool)" />
        <g clipPath="url(#lqc-poolclip)">
          <rect x="-6" y="356" width="312" height="2" fill={hexA("#FFFFFF", 0.32)} />
          <rect x="-6" y="368" width="312" height="1.2" fill={hexA("#FFFFFF", 0.16)} />
          <rect x="-6" y="386" width="312" height="3" fill={hexA(accent, 0.26)} />
          <ellipse cx="86" cy="349" rx="66" ry="3" fill={hexA("#FFFFFF", 0.42)} filter="url(#lqc-soft2)" />
          <ellipse cx="228" cy="376" rx="52" ry="2.4" fill={hexA(color2, 0.5)} filter="url(#lqc-soft2)" />
        </g>
        <path
          d={topEdge}
          fill="none"
          stroke={hexA(accent, 0.5)}
          strokeWidth="7"
          filter="url(#lqc-soft)"
          opacity="0.5"
        />
        <path
          d={topEdge}
          fill="none"
          stroke="url(#lqc-irid)"
          strokeWidth={1.1 * hair}
          strokeLinecap="round"
          opacity="0.75"
          transform="translate(0,3.4)"
        />
        <path
          d={topEdge}
          fill="none"
          stroke={hexA("#FFFFFF", 0.92)}
          strokeWidth={1.6 * hair}
          strokeLinecap="round"
        />
      </g>

      {/* ---------- chrome beads in the margins ---------- */}
      {beads.map((b) => (
        <g key={b.k}>
          <circle cx={b.x} cy={b.y} r={b.r + 2.4} fill={hexA(accent, 0.28)} filter="url(#lqc-soft)" />
          <circle cx={b.x} cy={b.y} r={b.r} fill="url(#lqc-bead)" />
          <circle
            cx={b.x}
            cy={b.y}
            r={b.r - 0.5}
            fill="none"
            stroke={hexA("#FFFFFF", 0.32)}
            strokeWidth={0.6 * hair}
          />
          <ellipse
            cx={b.x - b.r * 0.34}
            cy={b.y - b.r * 0.42}
            rx={b.r * 0.3}
            ry={b.r * 0.2}
            fill="#FFFFFF"
            opacity="0.95"
            transform={"rotate(-28," + (b.x - b.r * 0.34) + "," + (b.y - b.r * 0.42) + ")"}
          />
        </g>
      ))}

      {/* ---------- specular stars ---------- */}
      {sparks.map((s) => (
        <g key={s.k}>
          <path d={star(s.x, s.y, s.s * 1.5)} fill={hexA(accent, 0.45)} filter="url(#lqc-soft2)" />
          <path d={star(s.x, s.y, s.s)} fill="#FFFFFF" opacity="0.95" />
        </g>
      ))}

      {/* ---------- finish ---------- */}
      <rect x="0" y="0" width="300" height="400" fill="url(#lqc-vig)" />
      <rect
        x="0"
        y="0"
        width="300"
        height="400"
        filter="url(#lqc-grain)"
        opacity="0.07"
        style={{ mixBlendMode: "overlay" }}
      />
    </svg>
  );
}

// ─── RisoOverprintSunrise — risograph
function RisoOverprintSunrise({ color, color2, accent, scale = 300 }) {
  const P = "riso-ovps";
  const paper = "#F6EFDF";
  const inkA = color;
  const inkB = color2;
  const inkC = accent;

  const dk = scale < 200 ? 1.35 : 1;
  const cell = 6.6 * dk;
  const grainFreq = scale < 200 ? 0.5 : 0.85;
  const roughAmt = scale < 200 ? 1.9 : 1.4;

  const screens = [
    { k: "a", c: inkA, rot: 45 },
    { k: "b", c: inkB, rot: 15 },
    { k: "c", c: inkC, rot: 75 }
  ];
  const steps = [
    { k: "1", f: 0.42 },
    { k: "2", f: 0.3 },
    { k: "3", f: 0.19 },
    { k: "4", f: 0.105 }
  ];
  const screenDefs = [];
  screens.forEach(function (s) {
    steps.forEach(function (t) {
      screenDefs.push({ id: P + "-ht-" + s.k + "-" + t.k, c: s.c, rot: s.rot, r: cell * t.f });
    });
  });
  const ht = function (s, t) { return "url(#" + P + "-ht-" + s + "-" + t + ")"; };

  const sunX = 150;
  const sunY = 58;
  const rays = [];
  for (let i = 0; i < 24; i++) {
    const a = (i / 24) * Math.PI * 2 - Math.PI / 2;
    const outer = [66, 71, 62][i % 3];
    const ca = Math.cos(a);
    const sa = Math.sin(a);
    const ey = sunY + sa * outer;
    if (ey > 104) continue;
    rays.push({ i: i, x1: sunX + ca * 56, y1: sunY + sa * 56, x2: sunX + ca * outer, y2: ey });
  }

  const regPts = [[20, 20], [280, 20], [20, 380], [280, 380]];
  const regMarks = function (ink, tag) {
    return regPts.map(function (p, i) {
      return (
        <g key={tag + "-reg-" + i} stroke={ink} strokeWidth="1.15" fill="none">
          <line x1={p[0] - 7.5} y1={p[1]} x2={p[0] + 7.5} y2={p[1]} />
          <line x1={p[0]} y1={p[1] - 7.5} x2={p[0]} y2={p[1] + 7.5} />
          <circle cx={p[0]} cy={p[1]} r="4.3" />
        </g>
      );
    });
  };

  const specks = [
    [15, 134, 2.6], [286, 170, 2.0], [24, 212, 3.1], [277, 258, 2.3],
    [13, 306, 2.2], [287, 96, 2.8], [41, 74, 1.8], [261, 60, 2.4],
    [30, 352, 2.0], [271, 344, 1.6]
  ];
  const plusses = [[16, 250], [284, 196], [16, 176], [284, 272]];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        {screenDefs.map(function (d) {
          return (
            <pattern
              key={d.id}
              id={d.id}
              width={cell}
              height={cell}
              patternUnits="userSpaceOnUse"
              patternTransform={"rotate(" + d.rot + ")"}
            >
              <circle cx={cell / 2} cy={cell / 2} r={d.r} fill={d.c} />
            </pattern>
          );
        })}

        <filter id={P + "-rough-a"} x="-4%" y="-4%" width="108%" height="108%">
          <feTurbulence type="fractalNoise" baseFrequency="0.62" numOctaves="2" seed="3" result={P + "-nA"} />
          <feDisplacementMap in="SourceGraphic" in2={P + "-nA"} scale={roughAmt} xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id={P + "-rough-b"} x="-4%" y="-4%" width="108%" height="108%">
          <feTurbulence type="fractalNoise" baseFrequency="0.55" numOctaves="2" seed="9" result={P + "-nB"} />
          <feDisplacementMap in="SourceGraphic" in2={P + "-nB"} scale={roughAmt + 0.15} xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <filter id={P + "-grain"} x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency={grainFreq} numOctaves="3" seed="17" result={P + "-gz"} />
          <feColorMatrix
            in={P + "-gz"}
            type="matrix"
            values="0 0 0 0 0.26  0 0 0 0 0.19  0 0 0 0 0.15  0.85 0.4 0 0 -0.22"
          />
        </filter>
      </defs>

      <g style={{ isolation: "isolate" }}>
        <rect x="0" y="0" width="300" height="400" fill={paper} />
        <rect x="0" y="0" width="300" height="400" fill={ht("b", "4")} opacity="0.3" style={{ mixBlendMode: "multiply" }} />
        <path
          d="M48,352 L48,150 A102,52 0 0 1 252,150 L252,352 Z"
          fill={hexA(inkA, 0.075)}
          style={{ mixBlendMode: "multiply" }}
        />

        <g style={{ mixBlendMode: "multiply" }} transform="translate(-1.5 1.2)" filter={"url(#" + P + "-rough-b)"}>
          <path d="M0,400 L0,152 A16,16 0 0 1 32,152 L32,400 Z" fill={ht("b", "2")} />
          <path d="M268,400 L268,152 A16,16 0 0 1 300,152 L300,400 Z" fill={ht("b", "2")} />
          <path d="M88,58 A62,62 0 0 1 212,58" fill="none" stroke={inkB} strokeWidth="2.6" />
          <path d="M74,58 A76,76 0 0 1 226,58" fill="none" stroke={inkB} strokeWidth="1.9" strokeDasharray="3.6 5.4" />
          {rays.map(function (r) {
            return (
              <line key={"ray-" + r.i} x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2} stroke={inkB} strokeWidth="2.2" />
            );
          })}
          <path d="M-2,328 C55,312 95,322 150,326 C205,330 250,316 302,328 L302,404 L-2,404 Z" fill={inkB} />
          <rect x="9" y="9" width="282" height="382" fill="none" stroke={inkB} strokeWidth="1.3" />
          {regMarks(inkB, "b")}
        </g>

        <g style={{ mixBlendMode: "multiply" }} transform="translate(1.7 -1.2)" filter={"url(#" + P + "-rough-a)"}>
          <circle cx={sunX} cy={sunY} r="50" fill={ht("a", "1")} />
          <circle cx={sunX} cy={sunY} r="38" fill={inkA} />
          <rect x="40" y="132" width="6" height="216" fill={inkA} />
          <rect x="254" y="132" width="6" height="216" fill={inkA} />
          <path d="M-2,344 C60,330 100,340 150,343 C200,346 245,332 302,344 L302,404 L-2,404 Z" fill={ht("a", "1")} />
          {specks.map(function (s, i) {
            return <circle key={"spk-" + i} cx={s[0]} cy={s[1]} r={s[2]} fill={inkA} />;
          })}
          {regMarks(inkA, "a")}
        </g>

        <g style={{ mixBlendMode: "multiply" }} transform="translate(0.4 2.4)">
          <path d="M0,0 L50,0 L0,50 Z" fill={ht("c", "2")} />
          <path d="M300,0 L250,0 L300,50 Z" fill={ht("c", "2")} />
          <path d="M-2,372 C80,362 220,378 302,366 L302,404 L-2,404 Z" fill={inkC} />
          {plusses.map(function (p, i) {
            return (
              <g key={"plus-" + i} stroke={inkC} strokeWidth="1.6">
                <line x1={p[0] - 4.5} y1={p[1]} x2={p[0] + 4.5} y2={p[1]} />
                <line x1={p[0]} y1={p[1] - 4.5} x2={p[0]} y2={p[1] + 4.5} />
              </g>
            );
          })}
        </g>

        <rect
          x="0"
          y="0"
          width="300"
          height="400"
          filter={"url(#" + P + "-grain)"}
          opacity="0.26"
          style={{ mixBlendMode: "multiply" }}
        />
      </g>
    </svg>
  );
}

// ─── MatisseDecoupage — matisse
function MatisseDecoupage({ color, color2, accent, scale = 300 }) {
  const paper = color || "#F4EAD8";
  const ink = "#1E1B17";
  const blue = accent || "#1D4E9B";
  const red = color2 || "#D34327";
  const gold = "#E9A81C";
  const sea = "#0E7360";
  const fine = scale >= 190;

  const rnd = (i) => {
    const s = Math.sin(i * 127.1 + 311.7) * 43758.5453;
    return s - Math.floor(s);
  };

  const ring = (P) => {
    const n = P.length;
    let d = "M" + P[0][0].toFixed(1) + "," + P[0][1].toFixed(1);
    for (let i = 0; i < n; i++) {
      const a = P[(i - 1 + n) % n];
      const b = P[i];
      const c = P[(i + 1) % n];
      const e = P[(i + 2) % n];
      d +=
        "C" + (b[0] + (c[0] - a[0]) / 6).toFixed(1) + "," + (b[1] + (c[1] - a[1]) / 6).toFixed(1) +
        " " + (c[0] - (e[0] - b[0]) / 6).toFixed(1) + "," + (c[1] - (e[1] - b[1]) / 6).toFixed(1) +
        " " + c[0].toFixed(1) + "," + c[1].toFixed(1);
    }
    return d + "Z";
  };

  const blob = (cx, cy, rx, ry, lobes, amp, rot, seed) => {
    const n = Math.max(28, Math.min(56, Math.round((rx + ry) * 0.55)));
    const P = [];
    for (let i = 0; i < n; i++) {
      const t = (i / n) * 6.2832;
      const w =
        1 +
        amp * Math.sin(lobes * t + rot) +
        amp * 0.3 * Math.sin((lobes * 2 + 1) * t - rot * 1.7) +
        0.03 * (rnd(seed * 31 + i * 3) - 0.5);
      P.push([cx + Math.cos(t) * rx * w, cy + Math.sin(t) * ry * w]);
    }
    return ring(P);
  };

  const frond = (cx, y0, y1, w, lobes, lean, seed) => {
    const N = 24;
    const A = [];
    const B = [];
    for (let i = 0; i <= N; i++) {
      const u = i / N;
      const y = y0 + (y1 - y0) * u;
      const env = Math.pow(Math.sin(Math.PI * u), 0.55);
      const ax = cx + lean * (u - 0.5) * 2;
      A.push([ax + w * env * (1 + 0.42 * Math.sin(lobes * 6.2832 * u + seed) + 0.06 * (rnd(seed + i) - 0.5)), y]);
      B.push([ax - w * env * (1 + 0.42 * Math.sin(lobes * 6.2832 * u + seed + 2.1) + 0.06 * (rnd(seed * 3 + i) - 0.5)), y]);
    }
    return ring(A.concat(B.reverse()));
  };

  const star = (cx, cy, ro, ri, tips, rot, seed) => {
    const step = 6.2832 / tips;
    const at = (a, r) => [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
    const tip = (i) => at(rot + i * step, ro * (1 + 0.1 * (rnd(seed * 17 + (i % tips)) - 0.5)));
    let d = "";
    for (let i = 0; i < tips; i++) {
      const a0 = rot + i * step;
      const p0 = tip(i);
      const p1 = tip(i + 1);
      const c1 = at(a0 + step * 0.3, ri * (1 + 0.12 * (rnd(seed * 13 + i) - 0.5)));
      const c2 = at(a0 + step * 0.7, ri * (1 + 0.12 * (rnd(seed * 13 + i + 1) - 0.5)));
      d +=
        (i === 0 ? "M" + p0[0].toFixed(1) + "," + p0[1].toFixed(1) : "") +
        "C" + c1[0].toFixed(1) + "," + c1[1].toFixed(1) +
        " " + c2[0].toFixed(1) + "," + c2[1].toFixed(1) +
        " " + p1[0].toFixed(1) + "," + p1[1].toFixed(1);
    }
    return d + "Z";
  };

  const leaf = (cx, cy, w, h, bend) =>
    "M" + cx + "," + (cy - h) +
    "C" + (cx + w) + "," + (cy - h * 0.42) + " " + (cx + w * 0.88) + "," + (cy + h * 0.44) + " " + (cx + bend) + "," + (cy + h) +
    "C" + (cx - w * 0.9) + "," + (cy + h * 0.42) + " " + (cx - w * 1.04) + "," + (cy - h * 0.4) + " " + cx + "," + (cy - h) + "Z";

  const fieldTop = blob(58, -14, 126, 84, 4, 0.16, 0.5, 3);
  const fieldBase = blob(150, 434, 192, 100, 4, 0.18, 2.9, 11);

  const marks = [
    { k: "kelp-l", d: frond(26, 106, 324, 16, 3, 4, 21), f: red },
    { k: "kelp-r", d: frond(276, 92, 314, 15, 3, 4, 31), f: blue },
    { k: "bud-l", d: leaf(39, 138, 6.5, 16, 2), t: "rotate(26 39 138)", f: gold },
    { k: "bud-r", d: leaf(262, 282, 6.5, 16, -2), t: "rotate(-24 262 282)", f: sea },
    { k: "sliver", d: leaf(212, 8, 15, 30, 4), t: "rotate(14 212 8)", f: gold },
    { k: "star-red", d: star(258, 44, 37, 12.5, 4, -0.35, 5), f: red },
    { k: "star-ink", d: star(150, 26, 13, 4.2, 4, 0.4, 9), f: ink },
    { k: "leaf-edge", d: leaf(288, 96, 13, 32, 5), t: "rotate(22 288 96)", f: sea },
    { k: "coral", d: blob(14, 378, 46, 40, 5, 0.17, 2.2, 17), f: red },
    { k: "star-gold", d: star(274, 338, 31, 10, 4, 0.3, 19), f: gold }
  ];

  const pins = [[13, 120], [13, 330], [287, 122], [287, 350], [150, 388], [150, 10]];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <clipPath id="mtcut-clip-top">
          <path d={fieldTop} />
        </clipPath>
        <clipPath id="mtcut-clip-base">
          <path d={fieldBase} />
        </clipPath>
        <filter id="mtcut-lift" x="-25%" y="-25%" width="150%" height="150%">
          <feDropShadow dx="0" dy="1.2" stdDeviation="1.1" floodColor={hexA(ink, 0.16)} />
        </filter>
        <filter id="mtcut-grain" x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="3" seed="9" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.62" intercept="0" />
          </feComponentTransfer>
        </filter>
        <linearGradient id="mtcut-wash" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0" stopColor={hexA("#FFFFFF", 0.5)} />
          <stop offset="0.55" stopColor={hexA("#FFFFFF", 0)} />
          <stop offset="1" stopColor={hexA(ink, 0.07)} />
        </linearGradient>
      </defs>

      <rect x="0" y="0" width="300" height="400" fill={paper} />
      <rect x="0" y="0" width="300" height="400" fill="url(#mtcut-wash)" />

      <g filter="url(#mtcut-lift)">
        <path d={fieldTop} fill={blue} />
        <path d={fieldBase} fill={sea} />
      </g>

      <g clipPath="url(#mtcut-clip-top)">
        <path d={blob(-8, -42, 80, 64, 3, 0.14, 1.2, 51)} fill={hexA(ink, 0.11)} />
        <path d={leaf(46, 32, 13, 30, 3)} transform="rotate(-16 46 32)" fill={paper} opacity="0.94" />
      </g>

      <g clipPath="url(#mtcut-clip-base)">
        <path d={blob(258, 474, 122, 84, 3, 0.14, 0.7, 53)} fill={hexA(ink, 0.1)} />
        <path d={leaf(98, 370, 15, 30, -4)} transform="rotate(14 98 370)" fill={paper} opacity="0.94" />
        <path d={leaf(222, 384, 8, 17, 2)} transform="rotate(-30 222 384)" fill={paper} opacity="0.9" />
        <path d={leaf(132, 348, 9, 22, 3)} transform="rotate(-22 132 348)" fill={ink} opacity="0.92" />
      </g>

      <g filter="url(#mtcut-lift)">
        {marks.map((m) => (
          <path key={m.k} d={m.d} transform={m.t} fill={m.f} />
        ))}
      </g>

      {fine &&
        pins.map((p, i) => (
          <circle key={"pin-" + i} cx={p[0]} cy={p[1]} r="1.5" fill={hexA(ink, 0.2)} />
        ))}

      <rect
        x="0"
        y="0"
        width="300"
        height="400"
        filter="url(#mtcut-grain)"
        opacity={fine ? 0.085 : 0.05}
        style={{ mixBlendMode: "multiply" }}
      />
    </svg>
  );
}

// ─── FolkBlockPrint — blockprint
function FolkBlockPrint({ color, color2, accent, scale = 300 }) {
  const PAPER = "#EFE4CF";
  const ink = color;
  const sec = color2;
  const acc = accent;
  const lite = scale < 200;
  const f = (v) => Number(v).toFixed(2);
  const rnd = (i, s) => {
    const v = Math.sin(i * 127.1 + s * 311.7) * 43758.5453;
    return v - Math.floor(v);
  };
  const jit = (i, s, a) => (rnd(i, s) - 0.5) * 2 * a;

  const petalD = (len, wid, i) => {
    const a = jit(i, 1, 0.4);
    const b = jit(i, 2, 0.45);
    const t = len + jit(i, 3, 0.8);
    return `M0 -1.1C${f(wid + b)} ${f(-len * 0.3)} ${f(wid * 0.64 + a)} ${f(-len * 0.82)} 0 ${f(-t)}C${f(-wid * 0.64 + a)} ${f(-len * 0.82)} ${f(-wid - b)} ${f(-len * 0.3)} 0 -1.1Z`;
  };
  const leafD = (len, wid, i) => {
    const a = jit(i, 4, 0.35);
    return `M0 0C${f(len * 0.28)} ${f(-wid - a)} ${f(len * 0.7)} ${f(-wid * 0.86)} ${f(len)} 0C${f(len * 0.7)} ${f(wid * 0.86 + a)} ${f(len * 0.28)} ${f(wid)} 0 0Z`;
  };

  const bloom = (key, sd, cx, cy, r, n) => (
    <g key={key} transform={`translate(${f(cx)} ${f(cy)}) rotate(${f(jit(sd, 5, 9))})`}>
      {Array.from({ length: n }, (_, i) => (
        <path
          key={i}
          transform={`rotate(${f((360 / n) * i + jit(sd * 7 + i, 6, 3.5))})`}
          d={petalD(r, r * 0.38, sd * 13 + i)}
          fill={ink}
        />
      ))}
      <circle r={f(r * 0.34)} fill={PAPER} />
    </g>
  );

  const rosette = (key, sd, cx, cy, r) => (
    <g key={key} transform={`translate(${f(cx)} ${f(cy)}) rotate(${f(jit(sd, 9, 5))})`}>
      {Array.from({ length: 8 }, (_, i) => (
        <path
          key={"p" + i}
          transform={`rotate(${f(i * 45 + jit(sd * 11 + i, 2, 2.6))})`}
          d={petalD(r * 0.94, r * 0.34, sd * 17 + i)}
          fill={ink}
        />
      ))}
      {Array.from({ length: 4 }, (_, i) => {
        const a = ((i * 90 + 45) * Math.PI) / 180;
        return (
          <circle
            key={"d" + i}
            cx={f(Math.cos(a) * r * 1.3)}
            cy={f(Math.sin(a) * r * 1.3)}
            r="1.7"
            fill={ink}
          />
        );
      })}
      <circle r={f(r * 0.36)} fill={PAPER} />
    </g>
  );

  const sunburst = (key, sd, cx, cy, r) => (
    <g key={key} transform={`translate(${f(cx)} ${f(cy)})`}>
      {Array.from({ length: 14 }, (_, i) => (
        <path
          key={"r" + i}
          transform={`rotate(${f(i * (360 / 14) + jit(sd + i, 4, 2.4))})`}
          d={`M-2 ${f(-r * 1.02)}L0 ${f(-(r * 1.78 + jit(sd + i, 6, 1.5)))}L2 ${f(-r * 1.02)}Z`}
          fill={ink}
        />
      ))}
      <circle r={f(r)} fill={ink} />
      {Array.from({ length: 8 }, (_, i) => {
        const a = ((i * 45 + 22) * Math.PI) / 180;
        return (
          <circle
            key={"s" + i}
            cx={f(Math.cos(a) * r * 0.72)}
            cy={f(Math.sin(a) * r * 0.72)}
            r="1.35"
            fill={PAPER}
          />
        );
      })}
      <circle r={f(r * 0.44)} fill={PAPER} />
    </g>
  );

  const bird = (key, sd, cx, cy, s, flip) => (
    <g
      key={key}
      transform={`translate(${f(cx)} ${f(cy)}) scale(${flip ? -s : s} ${s}) rotate(${f(jit(sd, 3, 2.2))})`}
    >
      <path d="M-10.8 3.1C-12.3 -3 -6.5 -8.5 0.4 -8.1C6.4 -7.8 10.4 -4.4 10.6 0.6C10.8 5.6 6 9.3 0 9.1C-4.3 9 -9.5 7.3 -10.8 3.1Z" fill={ink} />
      <path d="M-10.4 1.3L-21.6 -5.4L-18.4 0.4L-22.6 2.5L-17.3 3.1L-18.1 7.8L-11.2 5.4Z" fill={ink} />
      <circle cx="9.5" cy="-6.4" r="4.4" fill={ink} />
      <path d="M13.1 -8.3L20.4 -6.6L13.1 -4.1Z" fill={ink} />
      <path d="M7.7 -10.6L6.6 -15.6L10.1 -12.6L12.5 -16.4L12.2 -10.9Z" fill={ink} />
      <path d="M-3 9L-4 13.6M2.7 9.1L3.6 13.6" stroke={ink} strokeWidth="1.5" strokeLinecap="round" fill="none" />
      <path d="M-6.8 -1.3C-2.7 -5.2 4.1 -4.1 5.8 0.6C3.5 4.7 -3.5 4.9 -6.8 -1.3Z" fill={PAPER} />
      <path d="M-5 -0.6C-1.7 -3.1 2.6 -2.5 4.1 0.4" stroke={ink} strokeWidth="1.1" strokeLinecap="round" fill="none" />
      <path d="M-3.7 2.1C-0.8 -0.2 2 0 3.5 2.3" stroke={ink} strokeWidth="1.1" strokeLinecap="round" fill="none" />
      <circle cx="10.6" cy="-7.1" r="1.3" fill={PAPER} />
    </g>
  );

  const diamond = (key, sd, cx, cy, r) => (
    <g key={key} transform={`translate(${f(cx)} ${f(cy)}) rotate(${f(jit(sd, 7, 3))})`}>
      <path d={`M0 ${f(-r)}L${f(r * 0.6)} 0L0 ${f(r)}L${f(-r * 0.6)} 0Z`} fill={ink} />
      <path d={`M0 ${f(-r * 0.46)}L${f(r * 0.27)} 0L0 ${f(r * 0.46)}L${f(-r * 0.27)} 0Z`} fill={PAPER} />
      <circle cx={f(r * 0.6 + 2.9)} r="1.5" fill={ink} />
      <circle cx={f(-r * 0.6 - 2.9)} r="1.5" fill={ink} />
    </g>
  );

  const sprig = (key, sd, cx, cy) => (
    <g key={key} transform={`translate(${f(cx)} ${f(cy)}) rotate(${f(jit(sd, 8, 2))})`}>
      <path d="M0 -8.6L4.3 -1.4L-4.3 -1.4Z" fill={ink} />
      <rect x="-4.7" y="0.5" width="9.4" height="2.4" fill={ink} />
      <circle cy="6.6" r="2.3" fill={ink} />
    </g>
  );

  const edgeRow = (tag, y) =>
    Array.from({ length: 7 }, (_, i) => {
      const cx = 66 + i * 28;
      return i % 2 === 0
        ? diamond(`${tag}d${i}`, i + y, cx, y, 8.4)
        : sprig(`${tag}s${i}`, i + y, cx, y);
    });

  const Y0 = 52;
  const Y1 = 348;
  const NV = 10;
  const STEP = (Y1 - Y0) / NV;
  const AMP = 6.6;

  const vineStem = (cx, dir) => {
    let d = `M${cx} ${Y0}`;
    for (let i = 0; i < NV; i++) {
      const s = (i % 2 === 0 ? 1 : -1) * dir;
      d += `Q${f(cx + s * AMP)} ${f(Y0 + STEP * (i + 0.5))} ${cx} ${f(Y0 + STEP * (i + 1))}`;
    }
    return d;
  };

  const vine = (tag, cx, dir) => (
    <g key={tag}>
      <path d={vineStem(cx, dir)} fill="none" stroke={ink} strokeWidth="2.3" strokeLinecap="round" />
      {Array.from({ length: NV }, (_, i) => {
        const s = (i % 2 === 0 ? 1 : -1) * dir;
        const my = Y0 + STEP * (i + 0.5);
        const mx = cx + s * AMP * 0.5;
        return (
          <path
            key={"l" + i}
            transform={`translate(${f(mx)} ${f(my)}) rotate(${f((s > 0 ? -20 : 160) + jit(i + cx, 8, 9))})`}
            d={leafD(12.6, 4.1, i + dir)}
            fill={ink}
          />
        );
      })}
      {Array.from({ length: 5 }, (_, i) =>
        bloom(`${tag}b${i}`, i * 3 + cx, cx, Y0 + STEP * (i * 2 + 1), 5.3, 6)
      )}
      {Array.from({ length: 4 }, (_, i) => (
        <circle key={"n" + i} cx={cx} cy={f(Y0 + STEP * (i * 2 + 2))} r="2" fill={ink} />
      ))}
    </g>
  );

  const chN = lite ? 80 : 190;
  let chatter = "";
  for (let i = 0; i < chN; i++) {
    const x = 5 + rnd(i, 11) * 290;
    const y = 5 + rnd(i, 12) * 390;
    if (x > 47 && x < 253 && y > 99 && y < 313) continue;
    const L = 0.6 + rnd(i, 13) * 2.5;
    const a = rnd(i, 14) * Math.PI;
    chatter += `M${x.toFixed(1)} ${y.toFixed(1)}l${(Math.cos(a) * L).toFixed(1)} ${(Math.sin(a) * L).toFixed(1)}`;
  }

  const fbN = lite ? 70 : 150;
  let fibre = "";
  for (let i = 0; i < fbN; i++) {
    const x = rnd(i, 31) * 300;
    const y = rnd(i, 32) * 400;
    const L = 0.5 + rnd(i, 33) * 1.7;
    fibre += `M${x.toFixed(1)} ${y.toFixed(1)}h${L.toFixed(1)}`;
  }

  const heroBloomX = [76, 113, 150, 187, 224];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <filter id="fbp-press" x="-4%" y="-4%" width="108%" height="108%" colorInterpolationFilters="sRGB">
          <feTurbulence type="fractalNoise" baseFrequency="0.42" numOctaves="2" seed="17" result="fbp-n" />
          <feDisplacementMap in="SourceGraphic" in2="fbp-n" scale="1.25" xChannelSelector="R" yChannelSelector="G" result="fbp-chip" />
          <feTurbulence type="fractalNoise" baseFrequency="0.028" numOctaves="3" seed="6" result="fbp-blot" />
          <feComponentTransfer in="fbp-blot" result="fbp-mask">
            <feFuncA type="table" tableValues="0 0.18 0.78 1 1" />
          </feComponentTransfer>
          <feComposite in="fbp-chip" in2="fbp-mask" operator="in" result="fbp-thin" />
          <feComponentTransfer in="fbp-chip" result="fbp-base">
            <feFuncA type="linear" slope="0.72" />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode in="fbp-base" />
            <feMergeNode in="fbp-thin" />
          </feMerge>
        </filter>
        <radialGradient id="fbp-lift" cx="50%" cy="52%" r="54%">
          <stop offset="0" stopColor="#FFF8EC" stopOpacity="0.6" />
          <stop offset="1" stopColor="#FFF8EC" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="fbp-vig" cx="50%" cy="48%" r="74%">
          <stop offset="0.52" stopColor={ink} stopOpacity="0" />
          <stop offset="1" stopColor={ink} stopOpacity="0.11" />
        </radialGradient>
      </defs>

      <rect width="300" height="400" fill={PAPER} />
      <rect width="300" height="400" fill={hexA(sec, 0.05)} />
      <ellipse cx="150" cy="208" rx="118" ry="140" fill="url(#fbp-lift)" />

      <g fill={hexA(sec, 0.1)}>
        <rect x="12" y="12" width="276" height="40" />
        <rect x="12" y="348" width="276" height="40" />
        <rect x="12" y="52" width="40" height="296" />
        <rect x="248" y="52" width="40" height="296" />
      </g>

      <rect x="4.5" y="4.5" width="291" height="391" fill="none" stroke={hexA(ink, 0.1)} strokeWidth="2.6" />

      <g filter="url(#fbp-press)">
        <g>
          <rect x="11.5" y="11.5" width="277" height="377" fill="none" stroke={ink} strokeWidth="2.5" />
          <rect x="7.5" y="7.5" width="285" height="385" fill="none" stroke={hexA(ink, 0.4)} strokeWidth="0.9" />
          <rect x="52" y="52" width="196" height="296" fill="none" stroke={ink} strokeWidth="1.5" />

          {rosette("cr0", 1, 32, 32, 13.4)}
          {rosette("cr1", 2, 268, 32, 13.4)}
          {rosette("cr2", 3, 32, 368, 13.4)}
          {rosette("cr3", 4, 268, 368, 13.4)}

          <g>{edgeRow("t", 32)}</g>
          <g>{edgeRow("b", 368)}</g>

          {vine("vl", 32, 1)}
          {vine("vr", 268, -1)}

          {sunburst("sun", 5, 150, 78, 10.2)}
          {bird("bl", 6, 107, 79, 0.82, false)}
          {bird("br", 7, 193, 79, 0.82, true)}
          {bloom("hb0", 21, 72, 78, 8.6, 6)}
          {bloom("hb1", 22, 228, 78, 8.6, 6)}
          <circle cx="72" cy="92.5" r="2.1" fill={ink} />
          <circle cx="228" cy="92.5" r="2.1" fill={ink} />
          <circle cx="85.5" cy="78" r="2" fill={ink} />
          <circle cx="214.5" cy="78" r="2" fill={ink} />

          <path d="M52 104.5H248" stroke={ink} strokeWidth="1.9" strokeLinecap="round" fill="none" />
          <path d="M52 309.5H248" stroke={ink} strokeWidth="1.9" strokeLinecap="round" fill="none" />

          <path d="M62 345.6H238" stroke={ink} strokeWidth="1.4" strokeLinecap="round" fill="none" />
          {heroBloomX.map((x, i) => (
            <g key={"st" + i}>
              <path d={`M${x} 345.4V${f(332.5 + jit(i, 15, 0.7))}`} stroke={ink} strokeWidth="1.9" strokeLinecap="round" fill="none" />
              <path transform={`translate(${x - 1} 339.5) rotate(${f(-158 + jit(i, 16, 8))})`} d={leafD(8.4, 3, i + 40)} fill={ink} />
              <path transform={`translate(${x + 1} 340.6) rotate(${f(-22 + jit(i, 17, 8))})`} d={leafD(8.4, 3, i + 50)} fill={ink} />
            </g>
          ))}
          {heroBloomX.map((x, i) =>
            bloom(`hbb${i}`, 30 + i, x, 326, i === 2 ? 8.6 : 7.2, i === 2 ? 8 : 6)
          )}
          {[94.5, 131.5, 168.5, 205.5].map((x, i) => (
            <g key={"bd" + i}>
              <path d={`M${x} 336L${x + 2.6} 340.5L${x} 345L${x - 2.6} 340.5Z`} fill={ink} />
              <circle cx={x} cy="329.5" r="1.6" fill={ink} />
            </g>
          ))}

          <path d={chatter} fill="none" stroke={ink} strokeWidth="0.85" strokeLinecap="round" opacity="0.32" />
        </g>

        <g transform="translate(1.4 -1.15)" opacity="0.94">
          <circle cx="150" cy="78" r="4.1" fill={acc} />
          <circle cx="32" cy="32" r="3.6" fill={acc} />
          <circle cx="268" cy="32" r="3.6" fill={acc} />
          <circle cx="32" cy="368" r="3.6" fill={acc} />
          <circle cx="268" cy="368" r="3.6" fill={acc} />
          <circle cx="72" cy="78" r="2.6" fill={acc} />
          <circle cx="228" cy="78" r="2.6" fill={acc} />
          <circle cx="106" cy="79.5" r="2.2" fill={acc} />
          <circle cx="194" cy="79.5" r="2.2" fill={acc} />
          {heroBloomX.map((x, i) => (
            <circle key={"ac" + i} cx={x} cy="326" r={i === 2 ? 2.9 : 2.4} fill={acc} />
          ))}
          {[66, 122, 178, 234].map((x, i) => (
            <g key={"ad" + i}>
              <circle cx={x} cy="32" r="1.8" fill={acc} />
              <circle cx={x} cy="368" r="1.8" fill={acc} />
            </g>
          ))}
          {Array.from({ length: 5 }, (_, i) => (
            <g key={"av" + i}>
              <circle cx="32" cy={f(Y0 + STEP * (i * 2 + 1))} r="1.7" fill={acc} />
              <circle cx="268" cy={f(Y0 + STEP * (i * 2 + 1))} r="1.7" fill={acc} />
            </g>
          ))}
          <path d="M52 108.6H248" stroke={acc} strokeWidth="1" strokeLinecap="round" fill="none" />
          <path d="M52 305.4H248" stroke={acc} strokeWidth="1" strokeLinecap="round" fill="none" />
          <path d="M62 20H238" stroke={hexA(acc, 0.55)} strokeWidth="0.9" strokeLinecap="round" fill="none" />
          <path d="M62 380H238" stroke={hexA(acc, 0.55)} strokeWidth="0.9" strokeLinecap="round" fill="none" />
        </g>
      </g>

      <path d={fibre} fill="none" stroke={hexA(ink, 0.11)} strokeWidth="0.6" strokeLinecap="round" />
      <rect width="300" height="400" fill="url(#fbp-vig)" />
    </svg>
  );
}

// ─── VintagePostmarkAirmail — postage
function VintagePostmarkAirmail({ color, color2, accent, scale = 300 }) {
  const paper = color;
  const ink = color2;
  const red = accent;
  const F = "Georgia, 'Times New Roman', Times, serif";
  const hair = scale < 200 ? 0.7 : 0.5;

  const rnd = (n) => {
    const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
    return s - Math.floor(s);
  };

  const LW = 8.4;
  const LP = 10;
  const SL = 3.4;
  const bandH = [];
  for (let i = 0; i < 27; i++) bandH.push(i);
  const bandV = [];
  for (let i = 0; i < 37; i++) bandV.push(i);

  const SX = 168;
  const SY = 26;
  const SW = 92;
  const SH = 86;
  const NX = 13;
  const NY = 12;
  const holes = [];
  for (let i = 0; i < NX; i++) {
    const cx = SX + ((i + 0.5) * SW) / NX;
    holes.push({ k: "t" + i, cx: cx, cy: SY });
    holes.push({ k: "b" + i, cx: cx, cy: SY + SH });
  }
  for (let j = 0; j < NY; j++) {
    const cy = SY + ((j + 0.5) * SH) / NY;
    holes.push({ k: "l" + j, cx: SX, cy: cy });
    holes.push({ k: "r" + j, cx: SX + SW, cy: cy });
  }

  const sky = [];
  let hy = 50;
  for (let i = 0; i < 14 && hy < 73; i++) {
    sky.push({ k: i, y: hy, o: Math.max(0.07, 0.3 - i * 0.017) });
    hy += 1.45 + i * 0.19;
  }

  const plane = [
    "M 0,0 C 3,-2.1 10,-2.7 15.6,-1.3 L 20.4,0 L 15.6,1.5 C 10,2.7 3,2.3 0,0 Z",
    "M 6.2,-0.6 L 1.6,-6.4 L 4.6,-6.6 L 10.2,-0.9 Z",
    "M 6.2,0.7 L 2.2,5.9 L 5.2,6.1 L 10.4,1 Z",
    "M 15.4,-1 L 18.6,-5.2 L 20.6,-5 L 19.8,-0.7 Z"
  ];

  const fg = [79, 81.4, 83.8, 86.2, 88.6];

  const FAR = "M 181,76 L 190,63 L 197,69 L 207,56 L 217,68 L 226,61 L 236,72 L 243,67 L 247,73 L 247,76 Z";
  const farHatch = [];
  for (let i = 0; i < 22; i++) farHatch.push({ k: i, x: 181 + i * 3.1 });

  const bars = [];
  for (let i = 0; i < 46; i++) {
    const t = (i * 7 + 3) % 5;
    bars.push({ k: i, x: 24 + i * 5.5, w: 1.1 + (t % 3) * 0.6, h: t > 2 ? 24 : 13 });
  }

  const chain = [];
  for (let i = 0; i < 13; i++) chain.push({ k: i, y: 132 + i * 13 });

  const flecks = [];
  for (let i = 0; i < 130; i++) {
    const fx = 9 + rnd(i) * 282;
    const fy = 9 + rnd(i + 77) * 382;
    if (fx > 48 && fx < 252 && fy > 106 && fy < 308) continue;
    flecks.push({
      k: i,
      x: fx,
      y: fy,
      r: 0.32 + rnd(i + 141) * 0.72,
      o: 0.05 + rnd(i + 219) * 0.09,
      c: i % 3 === 0 ? red : ink
    });
  }

  const killers = [50, 60, 70, 80];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="vpa-age" cx="50%" cy="44%" r="72%">
          <stop offset="0%" stopColor={hexA(red, 0)} />
          <stop offset="58%" stopColor={hexA(red, 0.05)} />
          <stop offset="100%" stopColor={hexA(red, 0.17)} />
        </radialGradient>
        <linearGradient id="vpa-sheen" x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0%" stopColor={hexA("#ffffff", 0.5)} />
          <stop offset="100%" stopColor={hexA("#ffffff", 0.12)} />
        </linearGradient>
        <mask id="vpa-perf" maskUnits="userSpaceOnUse">
          <rect x={SX} y={SY} width={SW} height={SH} fill="#ffffff" />
          {holes.map((h) => (
            <circle key={h.k} cx={h.cx} cy={h.cy} r="2.2" fill="#000000" />
          ))}
        </mask>
        <clipPath id="vpa-vig">
          <rect x="181" y="48" width="66" height="42" />
        </clipPath>
        <clipPath id="vpa-far">
          <path d="M 181,76 L 190,63 L 197,69 L 207,56 L 217,68 L 226,61 L 236,72 L 243,67 L 247,73 L 247,76 Z" />
        </clipPath>
        <path id="vpa-arc-top" d="M 117.02,53.81 A 27.8,27.8 0 0 1 166.98,53.81" fill="none" />
        <path id="vpa-arc-bot" d="M 114.86,82.96 A 32,32 0 0 0 169.14,82.96" fill="none" />
      </defs>

      {/* ---- paper stock ---- */}
      <rect x="0" y="0" width="300" height="400" fill={paper} />
      <g>
        {flecks.map((f) => (
          <circle key={f.k} cx={f.x} cy={f.y} r={f.r} fill={hexA(f.c, f.o)} />
        ))}
      </g>
      <rect x="0" y="0" width="300" height="400" fill="url(#vpa-age)" />

      {/* ---- faint airmail lozenge border ---- */}
      <g>
        {bandH.map((i) => (
          <path
            key={"bt" + i}
            d={"M " + (16 + i * LP) + ",15 L " + (16 + i * LP + SL) + ",7 L " + (16 + i * LP + SL + LW) + ",7 L " + (16 + i * LP + LW) + ",15 Z"}
            fill={hexA(i % 2 === 0 ? red : ink, i % 2 === 0 ? 0.4 : 0.34)}
          />
        ))}
        {bandH.map((i) => (
          <path
            key={"bb" + i}
            d={"M " + (16 + i * LP) + ",393 L " + (16 + i * LP + SL) + ",385 L " + (16 + i * LP + SL + LW) + ",385 L " + (16 + i * LP + LW) + ",393 Z"}
            fill={hexA(i % 2 === 0 ? ink : red, i % 2 === 0 ? 0.34 : 0.4)}
          />
        ))}
        {bandV.map((i) => (
          <path
            key={"bl" + i}
            d={"M 7," + (16 + i * LP) + " L 15," + (16 + i * LP + SL) + " L 15," + (16 + i * LP + SL + LW) + " L 7," + (16 + i * LP + LW) + " Z"}
            fill={hexA(i % 2 === 0 ? ink : red, i % 2 === 0 ? 0.34 : 0.4)}
          />
        ))}
        {bandV.map((i) => (
          <path
            key={"br" + i}
            d={"M 285," + (16 + i * LP) + " L 293," + (16 + i * LP + SL) + " L 293," + (16 + i * LP + SL + LW) + " L 285," + (16 + i * LP + LW) + " Z"}
            fill={hexA(i % 2 === 0 ? red : ink, i % 2 === 0 ? 0.4 : 0.34)}
          />
        ))}
      </g>

      {/* ---- engraved keyline ---- */}
      <rect x="17.5" y="17.5" width="265" height="365" fill="none" stroke={hexA(ink, 0.24)} strokeWidth={hair} />
      <rect x="20.5" y="20.5" width="259" height="359" fill="none" stroke={hexA(ink, 0.11)} strokeWidth={hair * 0.8} />

      {/* ---- side margins ---- */}
      <g transform="translate(31,214) rotate(-90)">
        <text
          x="0"
          y="0"
          textAnchor="middle"
          fontFamily={F}
          fontSize="5.6"
          letterSpacing="2.4"
          fill={hexA(ink, 0.36)}
        >
          VIA AIR MAIL · PAR AVION
        </text>
      </g>
      <line x1="24" y1="150" x2="24" y2="282" stroke={hexA(ink, 0.16)} strokeWidth={hair} />
      <g>
        {chain.map((c, i) => (
          <path
            key={c.k}
            d={"M 268," + (c.y - 3.4) + " L 271.4," + c.y + " L 268," + (c.y + 3.4) + " L 264.6," + c.y + " Z"}
            fill={hexA(i % 2 === 0 ? ink : red, 0.22)}
          />
        ))}
        <line x1="276" y1="132" x2="276" y2="288" stroke={hexA(ink, 0.14)} strokeWidth={hair} />
      </g>

      {/* ---- bottom: par avion label, cancel fragment, barcode ---- */}
      <line x1="24" y1="304" x2="276" y2="304" stroke={hexA(ink, 0.17)} strokeWidth={hair} />

      <g transform="rotate(11 250 330)" opacity="0.2">
        <circle cx="250" cy="330" r="21" fill="none" stroke={ink} strokeWidth="1.5" strokeDasharray="19 3 26 2.4" />
        <circle cx="250" cy="330" r="14.4" fill="none" stroke={ink} strokeWidth="1" strokeDasharray="14 2.6 22 2" />
        <text x="250" y="332" textAnchor="middle" fontFamily={F} fontSize="6" letterSpacing="0.6" fill={ink}>
          VII
        </text>
      </g>

      <g transform="translate(24,312) rotate(-2)">
        <rect x="0" y="0" width="106" height="30" fill={hexA(ink, 0.09)} stroke={hexA(ink, 0.42)} strokeWidth="0.8" />
        <rect x="2.6" y="2.6" width="100.8" height="24.8" fill="none" stroke={hexA(red, 0.55)} strokeWidth="1.5" strokeDasharray="3.2 3.2" />
        <rect x="2.6" y="2.6" width="100.8" height="24.8" fill="none" stroke={hexA(ink, 0.5)} strokeWidth="1.5" strokeDasharray="3.2 3.2" strokeDashoffset="3.2" />
        <text x="53" y="16.4" textAnchor="middle" fontFamily={F} fontSize="9" letterSpacing="1.7" fill={hexA(ink, 0.85)}>
          PAR AVION
        </text>
        <text x="53" y="24.4" textAnchor="middle" fontFamily={F} fontSize="4.6" letterSpacing="1.3" fill={hexA(red, 0.75)}>
          BY AIR MAIL
        </text>
      </g>

      <g>
        {bars.map((b) => (
          <rect key={b.k} x={b.x} y={378 - b.h} width={b.w} height={b.h} fill={hexA(ink, 0.62)} />
        ))}
        <line x1="24" y1="378" x2="277" y2="378" stroke={hexA(ink, 0.28)} strokeWidth={hair} />
      </g>

      {/* ---- the stamp ---- */}
      <g transform="translate(0,-2) rotate(-2.4 214 69)">
        <g transform="translate(1.4,2)" mask="url(#vpa-perf)">
          <rect x={SX} y={SY} width={SW} height={SH} fill={hexA(ink, 0.17)} />
        </g>
        <g mask="url(#vpa-perf)">
          <rect x={SX} y={SY} width={SW} height={SH} fill={paper} />
          <rect x={SX} y={SY} width={SW} height={SH} fill="url(#vpa-sheen)" />
          <rect x={SX} y={SY} width={SW} height={SH} fill={hexA(red, 0.05)} />
        </g>

        {/* engraved frame */}
        <rect x="175" y="33" width="78" height="72" fill="none" stroke={hexA(ink, 0.55)} strokeWidth="0.9" />
        <rect x="177.4" y="35.4" width="73.2" height="67.2" fill="none" stroke={hexA(ink, 0.26)} strokeWidth={hair * 0.8} />

        <text x="214" y="44" textAnchor="middle" fontFamily={F} fontSize="5.6" letterSpacing="1.5" fill={hexA(ink, 0.78)}>
          PAR AVION
        </text>

        {/* engraved vignette */}
        <g clipPath="url(#vpa-vig)">
          <rect x="181" y="48" width="66" height="42" fill={hexA(paper, 0.9)} />
          {sky.map((s) => (
            <line key={s.k} x1="181" y1={s.y} x2="247" y2={s.y} stroke={hexA(ink, s.o)} strokeWidth="0.45" />
          ))}
          <circle cx="233" cy="57" r="5.6" fill={paper} />
          <circle cx="233" cy="57" r="5.6" fill="none" stroke={hexA(ink, 0.45)} strokeWidth="0.5" />
          <path d={FAR} fill={hexA(ink, 0.19)} />
          <g clipPath="url(#vpa-far)">
            {farHatch.map((f, i) => (
              <line
                key={f.k}
                x1={f.x}
                y1="52"
                x2={f.x - 5}
                y2="78"
                stroke={hexA(paper, i % 2 === 0 ? 0.5 : 0.26)}
                strokeWidth="0.5"
              />
            ))}
            <path d={FAR} fill="none" stroke={hexA(ink, 0.42)} strokeWidth="0.55" />
          </g>
          <path
            d="M 181,76 L 189,71 L 200,77 L 211,69 L 222,76 L 233,70 L 243,78 L 247,75 L 247,90 L 181,90 Z"
            fill={hexA(ink, 0.34)}
          />
          {fg.map((y, i) => (
            <line
              key={"fg" + i}
              x1="181"
              y1={y}
              x2="247"
              y2={y}
              stroke={hexA(paper, 0.42 - i * 0.04)}
              strokeWidth="0.55"
            />
          ))}
          <path
            d="M 186,84 Q 191,71 196,62"
            fill="none"
            stroke={hexA(ink, 0.4)}
            strokeWidth="0.5"
            strokeDasharray="1.2 2.2"
          />
          <g transform="translate(192,55) rotate(-16) scale(0.92)">
            {plane.map((d, i) => (
              <path key={"ph" + i} d={d} fill="none" stroke={paper} strokeWidth="2.2" strokeLinejoin="round" />
            ))}
            {plane.map((d, i) => (
              <path key={"pf" + i} d={d} fill={hexA(ink, 0.82)} />
            ))}
          </g>
          <rect x="181" y="48" width="66" height="42" fill="none" stroke={hexA(ink, 0.5)} strokeWidth="0.8" />
        </g>

        <text x="181" y="100" fontFamily={F} fontSize="5.2" letterSpacing="1.1" fill={hexA(ink, 0.7)}>
          POSTES
        </text>
        <text x="247" y="100" textAnchor="end" fontFamily={F} fontSize="6.4" letterSpacing="0.4" fill={hexA(red, 0.85)}>
          24
        </text>
      </g>

      {/* ---- postmark, off-register over the stamp ---- */}
      <g transform="rotate(-7 142 66)" opacity="0.72">
        <circle cx="142" cy="66" r="36" fill="none" stroke={ink} strokeWidth="1.7" strokeDasharray="33 2.6 21 2 47 3" />
        <circle cx="142" cy="66" r="33.4" fill="none" stroke={hexA(ink, 0.55)} strokeWidth="0.55" />
        <circle cx="142" cy="66" r="25.2" fill="none" stroke={ink} strokeWidth="1.25" strokeDasharray="26 2.4 39 2" />
        <text fontFamily={F} fontSize="4.9" letterSpacing="2.4" fill={ink}>
          <textPath href="#vpa-arc-top" startOffset="50%" textAnchor="middle">
            AIR MAIL
          </textPath>
        </text>
        <text fontFamily={F} fontSize="4.9" letterSpacing="1.6" fill={ink}>
          <textPath href="#vpa-arc-bot" startOffset="50%" textAnchor="middle">
            PAR AVION
          </textPath>
        </text>
        <line x1="123" y1="57.5" x2="161" y2="57.5" stroke={hexA(ink, 0.7)} strokeWidth="0.6" />
        <text x="142" y="66.5" textAnchor="middle" fontFamily={F} fontSize="8.4" letterSpacing="1.2" fill={ink}>
          22 JUL
        </text>
        <line x1="123" y1="70.5" x2="161" y2="70.5" stroke={hexA(ink, 0.7)} strokeWidth="0.6" />
        <text x="142" y="78.5" textAnchor="middle" fontFamily={F} fontSize="6" letterSpacing="2.6" fill={hexA(ink, 0.9)}>
          2026
        </text>
        {killers.map((y, i) => (
          <path
            key={"kb" + i}
            d={"M 176," + y + " q 12,-2.4 24,0 t 24,0 t 24,0 t 24,0"}
            fill="none"
            stroke={hexA(ink, 0.62)}
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        ))}
      </g>
    </svg>
  );
}

// ─── GinghamPicnic — gingham
function GinghamPicnic({ color, color2, accent, scale = 300 }) {
  const berry = color || '#c23b3b';
  const cream = color2 || '#f5eedb';
  const leaf = accent || '#5c7d3a';

  // Check size follows scale but stays readable at 140..420 (cell 7..21)
  const cell = Math.max(5, Math.min(24, scale / 20));
  const tile = cell * 2;

  // Scalloped hems framing the calm cream center
  const n = 12;
  const sw = 300 / n;
  const depth = sw * 0.9;
  const yTop = 97;   // top gingham band dips to ~108 (above text @115)
  const yBot = 319;  // bottom band rises to ~308 (below text @300)

  const scallopEdge = (baseline, dir) => {
    let d = '';
    for (let i = 0; i < n; i++) {
      const xEnd = (300 - (i + 1) * sw).toFixed(2);
      const xMid = (300 - i * sw - sw / 2).toFixed(2);
      d += ` Q ${xMid} ${(baseline + dir * depth).toFixed(2)} ${xEnd} ${baseline.toFixed(2)}`;
    }
    return d;
  };

  const topBand = `M 0 0 L 300 0 L 300 ${yTop}${scallopEdge(yTop, 1)} Z`;
  const botBand = `M 0 400 L 300 400 L 300 ${yBot}${scallopEdge(yBot, -1)} L 0 400 Z`;
  const topTrim = `M 300 ${yTop}${scallopEdge(yTop, 1)}`;
  const botTrim = `M 300 ${yBot}${scallopEdge(yBot, -1)}`;

  const cusps = [];
  for (let i = 1; i < n; i++) cusps.push(300 - i * sw);

  const corners = [
    { k: 'tl', x: 6, y: 8, sx: 1, sy: 1 },
    { k: 'tr', x: 294, y: 8, sx: -1, sy: 1 },
    { k: 'bl', x: 6, y: 392, sx: 1, sy: -1 },
    { k: 'br', x: 294, y: 392, sx: -1, sy: -1 },
  ];

  const seeds = [[28, 27], [32, 29], [30, 32], [27, 31], [33, 33], [30, 35]];
  const petals = [0, 1, 2, 3, 4];

  const sprig = () => (
    <>
      <ellipse cx="22" cy="23" rx="23" ry="21" fill="url(#gpk-halo)" />
      <path d="M 13 13 C 19 18 23 23 30 29" fill="none" stroke={hexA(leaf, 0.9)} strokeWidth="1.4" strokeLinecap="round" />
      <g transform="translate(19 19) rotate(-32)">
        <ellipse rx="5.2" ry="2.6" fill={leaf} />
        <line x1="-4.6" y1="0" x2="4.6" y2="0" stroke={hexA('#ffffff', 0.4)} strokeWidth="0.5" />
      </g>
      <g transform="translate(29 23) rotate(28)">
        <ellipse rx="4.6" ry="2.3" fill={hexA(leaf, 0.92)} />
        <line x1="-4" y1="0" x2="4" y2="0" stroke={hexA('#ffffff', 0.4)} strokeWidth="0.5" />
      </g>
      <path d="M 30 22 C 22 22 22 31 30 37 C 38 31 38 22 30 22 Z" fill={berry} stroke={hexA(berry, 0.55)} strokeWidth="0.8" />
      {seeds.map((s, i) => (
        <ellipse key={`seed-${i}`} cx={s[0]} cy={s[1]} rx="0.7" ry="1.05" fill="#f6e7a6" transform={`rotate(18 ${s[0]} ${s[1]})`} />
      ))}
      <path d="M 30 23 L 25 19 L 28 23 L 30 18 L 32 23 L 35 19 L 30 23 Z" fill={leaf} />
      <path d="M 30 20 L 30 16" stroke={leaf} strokeWidth="1" strokeLinecap="round" />
      {petals.map((p) => {
        const a = (p / 5) * Math.PI * 2 - Math.PI / 2;
        const px = (12 + Math.cos(a) * 3.4).toFixed(2);
        const py = (12 + Math.sin(a) * 3.4).toFixed(2);
        return <circle key={`pet-${p}`} cx={px} cy={py} r="2.3" fill="#fffaf0" stroke={hexA(berry, 0.3)} strokeWidth="0.4" />;
      })}
      <circle cx="12" cy="12" r="1.9" fill="#f3c94e" />
    </>
  );

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice">
      <defs>
        <pattern id="gpk-gingham" patternUnits="userSpaceOnUse" width={tile} height={tile}>
          <rect x="0" y="0" width={cell} height={tile} fill={hexA(berry, 0.5)} />
          <rect x="0" y="0" width={tile} height={cell} fill={hexA(berry, 0.5)} />
        </pattern>
        <radialGradient id="gpk-glow" cx="50%" cy="46%" r="62%">
          <stop offset="0%" stopColor={hexA('#ffffff', 0.18)} />
          <stop offset="72%" stopColor={hexA('#ffffff', 0)} />
        </radialGradient>
        <radialGradient id="gpk-halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={hexA(cream, 0.9)} />
          <stop offset="55%" stopColor={hexA(cream, 0.55)} />
          <stop offset="100%" stopColor={hexA(cream, 0)} />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="300" height="400" fill={cream} />
      <rect x="0" y="0" width="300" height="400" fill="url(#gpk-glow)" />

      <path d={topBand} fill="url(#gpk-gingham)" />
      <path d={botBand} fill="url(#gpk-gingham)" />

      <path d={topTrim} fill="none" stroke={cream} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />
      <path d={botTrim} fill="none" stroke={cream} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />

      {cusps.map((cx, i) => (
        <g key={`cusp-${i}`}>
          <circle cx={cx} cy={yTop} r="1.4" fill={cream} stroke={hexA(berry, 0.45)} strokeWidth="0.5" />
          <circle cx={cx} cy={yBot} r="1.4" fill={cream} stroke={hexA(berry, 0.45)} strokeWidth="0.5" />
        </g>
      ))}

      {corners.map((c) => (
        <g key={c.k} transform={`translate(${c.x} ${c.y}) scale(${c.sx} ${c.sy})`}>
          {sprig()}
        </g>
      ))}
    </svg>
  );
}

// ─── AutumnHarvestFoliage — autumn-harvest
function AutumnHarvestFoliage({ color, color2, accent, scale = 300 }) {
  const rust = color || '#AE4726';
  const olive = color2 || '#6E7538';
  const gold = accent || '#C4892F';
  const plum = '#5E2C46';
  const brown = '#5B3A22';
  const cream = '#F6ECD6';

  const shade = (hex, amt) => {
    const n = hex.replace('#', '');
    const r = parseInt(n.slice(0, 2), 16);
    const g = parseInt(n.slice(2, 4), 16);
    const b = parseInt(n.slice(4, 6), 16);
    const t = amt < 0 ? 0 : 255;
    const p = Math.abs(amt);
    const mix = (c) => Math.round((t - c) * p + c);
    const hx = (c) => mix(c).toString(16).padStart(2, '0');
    return '#' + hx(r) + hx(g) + hx(b);
  };

  const tone = { rust, olive, gold, plum, brown };
  const gid = (t) => 'ahf-g-' + t;

  const MAPLE = "M0 -1 Q1 -2 3 -3 Q8 -3 11 -5 Q7 -7 5 -9 Q9 -11 12 -15 Q7 -17 4 -18 Q4 -21 5 -22 Q3 -25 0 -28 Q-3 -25 -5 -22 Q-4 -21 -4 -18 Q-7 -17 -12 -15 Q-9 -11 -5 -9 Q-7 -7 -11 -5 Q-8 -3 -3 -3 Q-1 -2 0 -1 Z";
  const MAPLEV = "M0 -2 L0 -26 M0 -9 L10 -6 M0 -9 L-10 -6 M0 -14 L11 -15 M0 -14 L-11 -15";
  const OAK = "M0 0 Q1 -2 1 -4 Q4 -5 4 -7 Q7 -8 5 -10 Q8 -11 8 -13 Q10 -15 7 -17 Q9 -20 6 -21 Q4 -24 3 -25 Q1 -26 0 -28 Q-1 -26 -3 -25 Q-4 -24 -6 -21 Q-9 -20 -7 -17 Q-10 -15 -8 -13 Q-8 -11 -5 -10 Q-7 -8 -4 -7 Q-4 -5 -1 -4 Q-1 -2 0 0 Z";
  const OAKV = "M0 -2 L0 -26 M0 -8 L5 -8 M0 -8 L-5 -8 M0 -14 L7 -14 M0 -14 L-7 -14 M0 -19 L6 -20 M0 -19 L-6 -20";
  const GRAIN = "M0 0 Q1.3 -1.6 1 -4 Q0.7 -6 0 -6.6 Q-0.7 -6 -1 -4 Q-1.3 -1.6 0 0 Z";
  const NUT = "M0 0 Q-3 -1 -3 -5 Q-3 -8 0 -8.4 Q3 -8 3 -5 Q3 -1 0 0 Z";
  const CAP = "M-4 -6.6 Q-4.6 -11 0 -11.4 Q4.6 -11 4 -6.6 Q0 -5 -4 -6.6 Z";
  const CAPTEX = "M-2.6 -7.6 L2.8 -8.9 M-2.8 -8.7 L2.6 -9.9 M-1.4 -6.6 L-1 -11 M1.4 -6.6 L1 -11";
  const STALK = "M0 -11.4 L0 -13.2";

  const L = (x, y, r, s, k, t) => ({ x, y, r, s, k, g: gid(t), base: tone[t] });

  const leaves = [
    L(40, 372, -38, 2.6, 'oak', 'olive'), L(68, 378, 25, 2.4, 'maple', 'rust'), L(30, 350, -20, 1.9, 'oak', 'gold'), L(80, 352, 48, 1.6, 'maple', 'plum'),
    L(262, 372, 38, 2.6, 'maple', 'rust'), L(234, 378, -25, 2.4, 'oak', 'olive'), L(272, 350, 22, 1.9, 'maple', 'gold'), L(222, 352, -46, 1.6, 'oak', 'plum'),
    L(140, 392, -18, 2.0, 'maple', 'olive'), L(162, 392, 18, 2.0, 'oak', 'rust'), L(151, 397, 0, 1.7, 'maple', 'gold'), L(112, 390, -42, 1.7, 'oak', 'plum'), L(190, 390, 42, 1.7, 'maple', 'olive'),
    L(8, 392, -72, 2.0, 'maple', 'rust'), L(292, 392, 72, 2.0, 'oak', 'olive'),
    L(28, 30, 150, 1.5, 'oak', 'olive'), L(52, 22, 116, 1.4, 'maple', 'rust'), L(14, 56, 188, 1.2, 'oak', 'gold'), L(62, 48, 138, 1.1, 'maple', 'plum'),
    L(272, 30, -150, 1.5, 'maple', 'rust'), L(248, 22, -116, 1.4, 'oak', 'olive'), L(286, 56, -188, 1.2, 'maple', 'gold'), L(238, 48, -138, 1.1, 'oak', 'plum'),
    L(15, 150, 205, 1.15, 'oak', 'gold'), L(20, 248, 182, 1.05, 'maple', 'olive'), L(285, 152, -205, 1.15, 'maple', 'rust'), L(280, 246, -182, 1.05, 'oak', 'olive'),
  ];

  const wheats = [
    { x: 48, y: 388, r: -18, s: 2.2 }, { x: 252, y: 388, r: 18, s: 2.2 }, { x: 150, y: 399, r: 0, s: 1.6 },
    { x: 40, y: 16, r: 126, s: 1.0 }, { x: 260, y: 16, r: -126, s: 1.0 },
  ];

  const grasses = [
    { x: 42, y: 392, r: -24, s: 2.4 }, { x: 258, y: 392, r: 24, s: 2.4 }, { x: 150, y: 398, r: 0, s: 1.7 },
  ];

  const acorns = [
    { x: 63, y: 392, r: 10, s: 1.6 }, { x: 237, y: 392, r: -10, s: 1.6 }, { x: 139, y: 396, r: -8, s: 1.5 },
    { x: 163, y: 396, r: 8, s: 1.5 }, { x: 34, y: 44, r: 196, s: 1.1 }, { x: 266, y: 44, r: 164, s: 1.1 },
  ];

  const berries = [
    { x: 74, y: 384, s: 1.4 }, { x: 226, y: 384, s: 1.4 }, { x: 124, y: 392, s: 1.2 }, { x: 178, y: 392, s: 1.2 },
    { x: 48, y: 34, s: 1.0 }, { x: 252, y: 34, s: 1.0 }, { x: 16, y: 205, s: 0.9 }, { x: 284, y: 205, s: 0.9 },
  ];

  const stems = [
    "M150 404 Q150 382 140 366", "M150 404 Q150 382 162 366", "M150 404 Q122 390 112 380", "M150 404 Q180 390 190 380",
    "M52 404 Q48 386 40 372", "M52 404 Q60 386 68 378", "M248 404 Q252 386 260 372", "M248 404 Q240 386 232 378",
  ];

  const grassColor = hexA(shade(gold, 0.06), 0.5);
  const stemColor = hexA(shade(olive, -0.12), 0.85);
  const petioleColor = hexA(shade(olive, -0.22), 0.7);

  const renderWheat = (o, i) => {
    const sw = (v) => v / o.s;
    const gs = [];
    for (let k = 0; k < 7; k++) {
      const py = -13 - k * 2.7;
      const ang = 30 - k * 1.6;
      gs.push({ py, rot: ang, id: 'r' + k });
      gs.push({ py, rot: -ang, id: 'l' + k });
    }
    return (
      <g key={`ahf-wh-${i}`} transform={`translate(${o.x} ${o.y}) rotate(${o.r}) scale(${o.s})`}>
        <path d="M0 0 Q1.5 -10 0.5 -20 Q-0.5 -28 0 -33" fill="none" stroke={hexA(shade(gold, -0.15), 0.9)} strokeWidth={sw(0.9)} strokeLinecap="round" />
        <path d="M0 -31 L0 -44 M0 -32 L4 -43 M0 -32 L-4 -43 M0 -32 L7.5 -41 M0 -32 L-7.5 -41" fill="none" stroke={hexA(shade(gold, 0.05), 0.75)} strokeWidth={sw(0.6)} strokeLinecap="round" />
        {gs.map((gg) => (
          <path key={`ahf-wh-${i}-${gg.id}`} d={GRAIN} transform={`translate(0 ${gg.py}) rotate(${gg.rot})`} fill="url(#ahf-g-gold)" stroke={hexA(shade(gold, -0.25), 0.5)} strokeWidth={sw(0.35)} strokeLinejoin="round" />
        ))}
        <path d={GRAIN} transform="translate(0 -33)" fill="url(#ahf-g-gold)" stroke={hexA(shade(gold, -0.25), 0.5)} strokeWidth={sw(0.35)} strokeLinejoin="round" />
      </g>
    );
  };

  const renderGrass = (o, i) => {
    const sw = (v) => v / o.s;
    const specs = [{ a: -30, l: 26 }, { a: -18, l: 29 }, { a: -7, l: 31 }, { a: 5, l: 30 }, { a: 16, l: 27 }, { a: 28, l: 23 }];
    return (
      <g key={`ahf-gr-${i}`} transform={`translate(${o.x} ${o.y}) rotate(${o.r}) scale(${o.s})`}>
        {specs.map((sp, j) => {
          const rad = sp.a * Math.PI / 180;
          const tx = Math.sin(rad) * sp.l;
          const ty = -Math.cos(rad) * sp.l;
          const cx = tx * 0.42;
          const cy = ty * 0.5;
          return <path key={`ahf-gr-${i}-${j}`} d={`M0 0 Q${cx.toFixed(1)} ${cy.toFixed(1)} ${tx.toFixed(1)} ${ty.toFixed(1)}`} fill="none" stroke={grassColor} strokeWidth={sw(0.7)} strokeLinecap="round" />;
        })}
      </g>
    );
  };

  const renderAcorn = (o, i) => {
    const sw = (v) => v / o.s;
    return (
      <g key={`ahf-ac-${i}`} transform={`translate(${o.x} ${o.y}) rotate(${o.r}) scale(${o.s})`}>
        <path d={STALK} fill="none" stroke={hexA(shade(brown, -0.1), 0.9)} strokeWidth={sw(0.8)} strokeLinecap="round" />
        <path d={NUT} fill="url(#ahf-g-brown)" stroke={hexA(shade(brown, -0.3), 0.4)} strokeWidth={sw(0.4)} strokeLinejoin="round" />
        <path d={CAP} fill={shade(brown, -0.14)} stroke={hexA(shade(brown, -0.35), 0.4)} strokeWidth={sw(0.4)} strokeLinejoin="round" />
        <path d={CAPTEX} fill="none" stroke={hexA(shade(brown, 0.22), 0.5)} strokeWidth={sw(0.35)} strokeLinecap="round" />
      </g>
    );
  };

  const renderBerry = (o, i) => {
    const pts = [
      { x: 0, y: 0, r: 2.5, c: plum }, { x: 3.3, y: -1, r: 2.1, c: rust }, { x: 1.6, y: -3.1, r: 2.2, c: plum },
      { x: -2.6, y: -1.4, r: 2.0, c: rust }, { x: -1, y: 1.9, r: 1.9, c: plum }, { x: 2.3, y: 1.7, r: 1.7, c: rust },
    ];
    return (
      <g key={`ahf-be-${i}`} transform={`translate(${o.x} ${o.y}) scale(${o.s})`}>
        <path d="M0 -2 L1.4 -6 M1.8 -2.5 L2.6 -6.2 M-1.2 -1.4 L-1.8 -5.4" fill="none" stroke={hexA(shade(olive, -0.15), 0.8)} strokeWidth={0.5 / o.s} strokeLinecap="round" />
        {pts.map((p, j) => (
          <g key={`ahf-be-${i}-${j}`}>
            <circle cx={p.x} cy={p.y} r={p.r} fill={p.c} stroke={hexA(shade(p.c, -0.3), 0.45)} strokeWidth={0.4 / o.s} />
            <circle cx={p.x - 0.7} cy={p.y - 0.8} r={p.r * 0.32} fill={hexA('#ffffff', 0.4)} />
          </g>
        ))}
      </g>
    );
  };

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice">
      <defs>
        {['rust', 'olive', 'gold', 'plum', 'brown'].map((t) => (
          <linearGradient key={`ahf-g-${t}`} id={`ahf-g-${t}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={shade(tone[t], 0.17)} />
            <stop offset="55%" stopColor={tone[t]} />
            <stop offset="100%" stopColor={shade(tone[t], -0.26)} />
          </linearGradient>
        ))}
        <linearGradient id="ahf-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={shade(cream, 0.02)} />
          <stop offset="100%" stopColor={shade(cream, -0.05)} />
        </linearGradient>
        <radialGradient id="ahf-vig" cx="50%" cy="44%" r="72%">
          <stop offset="0%" stopColor={hexA(brown, 0)} />
          <stop offset="62%" stopColor={hexA(brown, 0)} />
          <stop offset="100%" stopColor={hexA(shade(brown, 0.2), 0.12)} />
        </radialGradient>
        <radialGradient id="ahf-wash-b" cx="50%" cy="100%" r="75%">
          <stop offset="0%" stopColor={hexA(olive, 0.13)} />
          <stop offset="55%" stopColor={hexA(rust, 0.05)} />
          <stop offset="100%" stopColor={hexA(cream, 0)} />
        </radialGradient>
        <radialGradient id="ahf-wash-t" cx="50%" cy="0%" r="85%">
          <stop offset="0%" stopColor={hexA(gold, 0.1)} />
          <stop offset="100%" stopColor={hexA(cream, 0)} />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="300" height="400" fill={cream} />
      <rect x="0" y="0" width="300" height="400" fill="url(#ahf-sky)" />
      <rect x="0" y="300" width="300" height="100" fill="url(#ahf-wash-b)" />
      <rect x="0" y="0" width="300" height="115" fill="url(#ahf-wash-t)" />
      <rect x="0" y="0" width="300" height="400" fill="url(#ahf-vig)" />

      <g stroke={stemColor} fill="none" strokeLinecap="round">
        {stems.map((d, i) => (<path key={`ahf-st-${i}`} d={d} strokeWidth={1.4} />))}
      </g>

      {grasses.map((o, i) => renderGrass(o, i))}
      {wheats.map((o, i) => renderWheat(o, i))}

      {leaves.map((o, i) => {
        const vc = shade(o.base, -0.3);
        return (
          <g key={`ahf-lf-${i}`} transform={`translate(${o.x} ${o.y}) rotate(${o.r}) scale(${o.s})`}>
            <path d="M0 0 L0 5" fill="none" stroke={petioleColor} strokeWidth={1.1 / o.s} strokeLinecap="round" />
            <path d={o.k === 'maple' ? MAPLE : OAK} fill={`url(#${o.g})`} stroke={hexA(vc, 0.3)} strokeWidth={0.8 / o.s} strokeLinejoin="round" />
            <path d={o.k === 'maple' ? MAPLEV : OAKV} fill="none" stroke={hexA(vc, 0.4)} strokeWidth={0.55 / o.s} strokeLinecap="round" />
          </g>
        );
      })}

      {acorns.map((o, i) => renderAcorn(o, i))}
      {berries.map((o, i) => renderBerry(o, i))}
    </svg>
  );
}

export const SCENES = {
  'gingham': GinghamPicnic,
  'autumn-harvest': AutumnHarvestFoliage,
  'neon-sign': NeonTubeWall,
  'y2k-patch': Y2KChenillePatch,
  'pool-tiles': PoolTileCaustics,
  'foil-balloons': GoldFoilNumberBalloons,
  'racquet-club': RacquetClubArch,
  'civic': CivicMasthead,
  'kid-doodle': KidDoodleBalloons,
  'western': DesertLastRodeo,
  'chrome': LiquidChromeY2K,
  'risograph': RisoOverprintSunrise,
  'matisse': MatisseDecoupage,
  'blockprint': FolkBlockPrint,
  'postage': VintagePostmarkAirmail,
  'disco-neon': DiscoNeonGlow,
  'eucalyptus-wreath': WatercolorEucalyptusWreath,
  'monogram-crest': MonogramCrest,
  'gold-filigree': GildedCartoucheFrame,
  'string-lights': StringLightsChalkboard,
  'country-club': CountryClubParTee,
  'nautical-boat': NauticalBowOnWater,
  'city-skyline': CitySkylineLineArt,
  'magnolia-branch': WatercolorMagnoliaBranch,
  'confetti-balloons': ConfettiBalloonCelebration,
  'tropical-palm': TropicalPalmMonstera,
};

export const SCENE_KEYS = Object.keys(SCENES);

/** Render a named scene, or nothing if the key is unknown. */
export default function Scene({ name, color, color2, accent, scale = 300 }) {
  const Cmp = name && SCENES[name];
  if (!Cmp) return null;
  return <Cmp color={color} color2={color2} accent={accent} scale={scale} />;
}
